// Module frontend surface.
//
// The shell finds this file on its own (modules/*/frontend/index.js) and merges
// what it exports. Adding this module needs no edit to the shell (§4.2).
//
// La lista de obras, la ficha de cada una con sus cinco apartados, y desde la
// 0.26.0 cuatro pantallas que miran todas las obras a la vez (contratación,
// subcontratas, certificaciones y precios de obra), colgadas del menú como
// subapartados de «Obras». No hay pantalla de «importar»: una importación es de
// una obra y vive en un diálogo dentro de ella, porque una pantalla a la que se
// llega sin obra elegida empieza preguntando «¿qué obra?», y esa es la lista de
// la que se viene.

export const routes = [
  {
    path: '/construction',
    name: 'construction',
    component: () => import('./pages/WorksPage.vue'),
    meta: { permission: 'construction:read', title: 'construction.title' },
  },
  // ── Las pantallas que cruzan todas las obras (0.26.0) ──────────────────────
  // Las cuatro del menú, debajo de «Obras». Rutas fijas: ganan a `/:id` por
  // rango, así que «contracting» nunca se lee como el id de una obra. Las
  // cuatro detrás de `construction:margins`, que es lo que piden sus rutas:
  // son dinero de principio a fin.
  {
    path: '/construction/contracting',
    name: 'construction-contracting',
    component: () => import('./pages/ContractingPage.vue'),
    meta: { permission: 'construction:margins', title: 'construction.across.contracting.title' },
  },
  {
    path: '/construction/subcontracts',
    name: 'construction-subcontracts',
    component: () => import('./pages/SubcontractsPage.vue'),
    meta: { permission: 'construction:margins', title: 'construction.across.subcontracts.title' },
  },
  {
    path: '/construction/certifications',
    name: 'construction-certifications',
    component: () => import('./pages/CertificationsPage.vue'),
    meta: { permission: 'construction:margins', title: 'construction.across.certifications.title' },
  },
  // Los precios de obra pasaron a Compras como «Precios ofertados» (purchasing
  // 0.12.0). La dirección vieja sigue llevando a algún sitio: un enlace
  // guardado no tiene por qué saber que la pantalla cambió de dueño.
  { path: '/construction/prices', redirect: to => ({ path: '/tender-prices', query: to.query }) },
  {
    path: '/construction/:id',
    name: 'construction-work',
    component: () => import('./pages/WorkPage.vue'),
    // `ownsQuery`: la ficha escribe su apartado y su pestaña en la dirección
    // (`?section=…&tab=…`). Sin esto el armazón remontaría la ficha entera en
    // cada cambio de pestaña, porque la clave de la vista es la dirección
    // completa.
    meta: { permission: 'construction:read', title: 'construction.detailTitle', ownsQuery: true },
  },
  {
    // La ficha de una subcontrata: el contrato, sus extras y las cuatro
    // puertas. Detrás de `construction:margins` porque es dinero de la casa.
    path: '/construction/:id/subcontracts/:subcontractId',
    name: 'construction-subcontract',
    component: () => import('./pages/SubcontractPage.vue'),
    meta: { permission: 'construction:margins', title: 'construction.subcontracts.title' },
  },
  {
    // Lo que se le paga a una subcontrata un mes. Pantalla propia y no un modal
    // dentro del contrato: se rellena partida a partida y se guarda a medias.
    path: '/construction/:id/subcontracts/:subcontractId/certifications/:certificationId',
    name: 'construction-sub-certification',
    component: () => import('./pages/SubCertificationPage.vue'),
    meta: { permission: 'construction:margins', title: 'construction.subcerts.title' },
  },
  {
    // La certificación al cliente de un mes: se revisa línea a línea, se bajan
    // las que la dirección facultativa no acepta y se emite. Detrás de
    // `construction:margins`, que es lo que piden sus rutas.
    path: '/construction/:id/certifications/:certificationId',
    name: 'construction-certification',
    component: () => import('./pages/CertificationPage.vue'),
    meta: { permission: 'construction:margins', title: 'construction.clientCert.title' },
  },
  // El comparativo de ofertas es de Compras desde purchasing 0.12.0: la
  // dirección vieja, la de los enlaces y los PDF que ya circulan, lleva al nuevo.
  { path: '/construction/:id/tenders/:tenderId', redirect: to => `/tenders/${to.params.tenderId}` },
  {
    path: '/construction/:id/items/:itemId',
    name: 'construction-item',
    component: () => import('./pages/ItemPage.vue'),
    meta: { permission: 'construction:read', title: 'construction.item.title' },
  },
]

/**
 * Una entrada con sus subapartados (0.26.0).
 *
 * «Obras» abre la lista de siempre. Debajo, lo que se mira de TODAS las obras a
 * la vez y hasta ahora solo se podía ver obra por obra: la contratación, las
 * subcontratas y las certificaciones al cliente. Ningún nombre de aquí dice
 * «Compras» ni «Presupuestos»: los pedidos, los proveedores, los comparativos y
 * los precios ofertados son de compras (que los cuelga en su menú solo cuando
 * Obras está instalado), y la oferta de obra menor es de presupuestos. Las tres
 * son dinero y van detrás de `construction:margins`; a quien no lo tiene el
 * armazón le deja solo «Obras».
 */
export const navigation = [
  {
    to: '/construction',
    labelKey: 'construction.title',
    permission: 'construction:read',
    icon: 'crane',
    group: 'business',
    children: [
      { to: '/construction', labelKey: 'construction.nav.works', permission: 'construction:read' },
      { to: '/construction/contracting', labelKey: 'construction.nav.contracting', permission: 'construction:margins' },
      { to: '/construction/subcontracts', labelKey: 'construction.nav.subcontracts', permission: 'construction:margins' },
      { to: '/construction/certifications', labelKey: 'construction.nav.certifications', permission: 'construction:margins' },
    ],
  },
]

export const messages = {
  es: {
    // El nombre de cada permiso en la pantalla de roles. Sin esto salía el
    // código crudo («construction:margins») a quien decide quién ve el dinero.
    permissions: {
      construction: {
        read: 'Ver las obras, su presupuesto y su avance',
        write: 'Dar de alta obras, medir y asignar costes a las partidas',
        import: 'Cargar y editar el presupuesto (BC3 o a mano)',
        margins: 'Ver y gestionar el dinero: objetivo, coste, margen, paquetes y subcontratas',
        award: 'Adjudicar paquetes desde su comparativo de ofertas: firma la subcontrata o el compromiso',
      },
    },
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { construction: 'Obras' },
    // Las aperturas del asistente: la clave es el id estable de
    // backend/assistant.js; el texto inglés de allí es solo el fallback.
    assistant: {
      openers: {
        construction: {
          'how-are-the-works': '¿Cómo van las obras?',
          'what-is-deviating': '¿Qué se está desviando en las obras?',
          'certifications-pending': '¿Qué certificaciones quedan pendientes?',
        },
      },
    },
    // La auditoría busca la acción tal cual se grabó: `construction.cost_voided`.
    audit: { actions: { construction: { cost_voided: 'Apunte de coste anulado' } } },
    construction: {
      // El comparativo de ofertas de un paquete (0.25.0).
      tenders: {
        title: 'Comparativo de ofertas',
        back: 'A los paquetes',
        pageTitle: 'Comparativo · {code} {name}',
        pageSubtitle: 'Comparativo n.º {number} · {count} línea | Comparativo n.º {number} · {count} líneas',
        print: 'Imprimir PDF',
        template: 'Plantilla para las empresas',
        addOffer: 'Añadir oferta',
        editOffer: 'Editar oferta',
        status: {
          open: 'Licitando',
          awarded: 'Adjudicado',
          cancelled: 'Cerrado sin adjudicar',
        },
        dueOn: 'Ofertas hasta el {date}',
        frozen: 'Congelado el {date}: así se vio al adjudicar',
        target: 'Objetivo',
        offersCount: 'Ofertas',
        bestComplete: 'Mejor oferta completa',
        vsTarget: 'Contra objetivo',
        linesChanged: 'El paquete ha cambiado desde que se abrió el comparativo: sus líneas ya no son las que se pidieron.',
        refreshLines: 'Actualizar líneas',
        linesRefreshed: 'Líneas actualizadas. Los precios de cada empresa se conservan por partida.',
        ruleLabel: 'Las líneas no ofertadas se completan con',
        rule: {
          highest: 'El precio más alto ofertado',
          target: 'El objetivo',
        },
        ruleHelp: {
          highest: 'Homogeneizado: cada línea que una empresa no oferta se completa con el precio más alto que han ofertado las demás (o con el objetivo si nadie la ha ofertado). Es la regla prudente: esa línea la acabarás contratando, y no te va a salir barata.',
          target: 'Homogeneizado: cada línea que una empresa no oferta se completa al precio objetivo de la línea.',
        },
        awardedTo: 'Adjudicado a {supplier}',
        awardedBy: 'Por {who} el {date}',
        wasRecommended: 'era la oferta recomendada',
        wasNotRecommended: 'la recomendada era {supplier}',
        reason: 'Motivo',
        seeSubcontract: 'Ver la subcontrata que se dio de alta',
        orderNote: 'Compromiso apuntado en el paquete. Si compras está instalado, el pedido queda en borrador con estas líneas y estos precios: lo manda quien compra.',
        cancelAward: 'Anular la adjudicación',
        cancelAwardHelp: 'Para cuando la empresa se echa atrás antes de empezar. El comparativo se reabre con sus ofertas, la subcontrata se anula (si no se le ha certificado nada) y el compromiso se retira. La adjudicación anulada queda en el historial.',
        cancelAwardWithdraw: 'La empresa se retira: su oferta deja de competir',
        awardCancelled: 'Adjudicación anulada. El comparativo vuelve a estar abierto.',
        recommended: 'Recomendada: {supplier}',
        recommendedShort: 'Recomendada',
        basis: {
          cheapest_complete: 'Es la oferta completa y vigente más barata, contando las líneas homogeneizadas.',
          cheapest_complete_expired: 'Es la completa más barata, pero todas las completas están caducadas: pide a la empresa que mantenga el precio antes de adjudicar.',
          no_complete: 'Ninguna oferta está completa: es la más barata homogeneizada. Pide precio de lo que falta antes de adjudicar.',
        },
        belowTarget: 'Queda {amount} ({pct}) por debajo del objetivo.',
        aboveTarget: 'Queda {amount} ({pct}) por encima del objetivo.',
        runnerUp: 'La siguiente es {supplier}, {amount} más cara.',
        cheaperIncomplete: '{supplier} oferta {offered}, pero deja {count} línea sin precio: homogeneizada sale {homogenized}. | {supplier} oferta {offered}, pero deja {count} líneas sin precio: homogeneizada sale {homogenized}.',
        cheaperExpired: '{supplier} sería más barata ({amount}) pero su oferta ha caducado: pídele que la renueve.',
        awardRecommended: 'Adjudicar a la recomendada',
        emptyTitle: 'Sin ofertas todavía',
        emptyHelp: 'Descarga la plantilla, mándala a tres o cuatro empresas y mete aquí sus precios: tecleados o leyendo su Excel.',
        offerStatus: {
          received: 'Recibida',
          awarded: 'Adjudicada',
          not_awarded: 'No adjudicada',
          withdrawn: 'Retirada',
        },
        expired: 'Caducada',
        missingCount: '{count} línea sin precio | {count} líneas sin precio',
        offered: 'Ofertado',
        homogenized: 'Homogeneizado',
        bestLines: 'Líneas con el mejor precio',
        reference: 'N.º de oferta',
        validUntil: 'Válida hasta',
        executionDays: 'Plazo (días)',
        days: '{count} día | {count} días',
        paymentTerms: 'Forma de pago',
        paymentTermsPlaceholder: 'Ej.: 60 días fecha factura',
        retentionPct: 'Retención (%)',
        discountPct: 'Descuento global (%)',
        discountHelp: 'Sobre lo que oferta, no sobre las líneas que se completan.',
        discountShort: 'dto. {pct} %',
        totalDeclared: 'Total que escribe la empresa',
        totalDeclaredHelp: 'El de su oferta. Si no cuadra con sus precios, se avisa.',
        declaredMismatch: 'Su oferta dice {declared} y sus precios suman {computed}: revisa qué se ha tecleado mal.',
        strongCount: '{count} precio se desvía mucho de la media: revisa unidad y medición. | {count} precios se desvían mucho de la media: revisa unidades y mediciones.',
        exclusions: 'No incluye',
        exclusionsHelp: 'Lo que la oferta deja fuera. Una por línea, con Intro.',
        exclusionsPlaceholder: 'Ej.: reposición de pavimento',
        notes: 'Notas',
        documents: 'Sin papeles | 1 papel | {count} papeles',
        documentsTitle: 'Papeles de la oferta de {supplier}',
        withdrawOffer: 'Retirar',
        restoreOffer: 'Volver a tenerla en cuenta',
        removeOffer: 'Quitar',
        withdrawHelp: 'La empresa se echa atrás. La oferta se queda en el comparativo, pero deja de competir y de marcar mejores precios.',
        restoreHelp: 'La oferta vuelve a competir con las demás.',
        removeOfferTitle: '¿Quitar la oferta?',
        removeOfferHelp: 'Se borra la oferta de {supplier} con sus precios y sus papeles. Si la empresa se ha echado atrás, mejor «Retirar»: así consta.',
        award: 'Adjudicar',
        awardTitle: 'Adjudicar el paquete',
        awardLead: 'Adjudicar a {supplier} por {amount}.',
        awardSubcontractHelp: 'Se da de alta la subcontrata enlazada a este paquete, con el importe de la oferta, un {retention} % de retención y {days} días de plazo. Su alcance se lee del paquete.',
        awardOrderHelp: 'Se apunta en el paquete un compromiso de {amount}, y compras deja el pedido en borrador con estas líneas. Cada pedido que se confirme a esta empresa en este paquete descuenta del compromiso.',
        awardIncomplete: 'Esta oferta deja {count} línea sin precio, que vale {amount} al objetivo: habrá que contratarla aparte. | Esta oferta deja {count} líneas sin precio, que valen {amount} al objetivo: habrá que contratarlas aparte.',
        signedLabel: 'Firma',
        signedHelp: 'Dejarla ya firmada (compromete su importe desde hoy)',
        reasonRequired: 'Motivo (obligatorio: la recomendada es {supplier})',
        reasonOptional: 'Motivo (opcional)',
        awarded: 'Paquete adjudicado. El comparativo queda congelado.',
        tableTitle: 'Tabla comparativa',
        viewLabel: 'Ver',
        view: {
          table: 'Tabla',
          lines: 'Por línea',
          offers: 'Por oferta',
        },
        line: 'Línea',
        unitPrice: 'Precio',
        amount: 'Importe',
        pastPrices: 'Precios de otras obras',
        notOffered: 'No ofertada',
        filledWith: 'se completa con {amount}',
        lastShort: 'últ. {price}',
        lastLong: 'La última vez ofertó {price} ({work}, {date})',
        notOfferedLines: 'Líneas no ofertadas',
        targetShort: 'objetivo {price} · {amount}',
        best: 'Mejor',
        pickOffer: 'Oferta',
        historyTitle: 'Historial del comparativo',
        cancelTender: 'Cerrar sin adjudicar',
        cancelTenderHelp: 'El paquete vuelve a «Abierto». Los precios se quedan en el histórico: fueron ofertas de verdad.',
        offerDate: 'Fecha de la oferta',
        pricesTitle: 'Precios por línea',
        pricesHelp: 'En blanco es «no la oferta», y así se ve en la tabla. Un 0 es un precio: la línea va incluida en otra.',
        importSheet: 'Leer su Excel',
        reading: 'Leyendo…',
        sheetRead: '{file} · hoja «{sheet}»: {priced} de {lines} líneas con precio. Revisa y guarda.',
        sheetMissing: '{count} línea no aparece en su hoja: queda sin ofertar. | {count} líneas no aparecen en su hoja: quedan sin ofertar.',
        sheetQuantity: '{code}: ha ofertado {offered} {unit} donde se pedían {asked}. Su total no es comparable hasta que se aclare.',
        sheetUnmatched: 'Fila {row} sin casar con ninguna línea: {text}',
        draftTotal: 'Ofertado: {amount} · sin líneas sin precio | Ofertado: {amount} · {count} línea sin precio | Ofertado: {amount} · {count} líneas sin precio',
        offerSaved: 'Oferta guardada',
        history: {
          opened: 'Comparativo abierto con {count} líneas',
          edited: 'Cambiada la regla o la fecha',
          lines_refreshed: 'Líneas actualizadas desde el paquete ({count})',
          cancelled: 'Cerrado sin adjudicar: {comment}',
          offer_added: 'Oferta de {supplier}',
          offer_revised: 'Revisada la oferta de {supplier}',
          offer_withdrawn: '{supplier} retira su oferta {comment}',
          offer_restored: 'La oferta de {supplier} vuelve a competir',
          offer_removed: 'Quitada la oferta de {supplier}',
          awarded: 'Adjudicado a {supplier} por {amount} {comment}',
          award_cancelled: 'Anulada la adjudicación a {supplier}: {comment}',
        },
        templateLabels: {
          code: 'Código',
          description: 'Concepto',
          unit: 'Ud',
          quantity: 'Cantidad',
          price: 'Precio unitario',
          amount: 'Importe',
          total: 'TOTAL',
        },
        pdf: {
          title: 'Comparativo de ofertas',
          work: 'Obra',
          package: 'Paquete',
          date: 'Fecha',
          code: 'Código',
          description: 'Concepto',
          unit: 'Ud',
          quantity: 'Cantidad',
          target: 'Objetivo',
          unitPrice: 'Precio',
          amount: 'Importe',
          notOffered: 'no ofertada',
          offered: 'Ofertado',
          homogenized: 'Homogeneizado',
          vsTarget: 'Contra objetivo',
          missing: 'Líneas no ofertadas',
          ruleHighest: 'Las líneas no ofertadas se completan con el precio más alto ofertado por las demás empresas (o con el objetivo si nadie las ofertó).',
          ruleTarget: 'Las líneas no ofertadas se completan al precio objetivo.',
          recommended: 'Recomendada',
          awardedTo: 'Adjudicado a',
          reason: 'Motivo',
          best: 'Mejor precio',
          frozen: 'Registro congelado de la adjudicación',
          validUntil: 'válida hasta',
          expired: 'caducada',
          withdrawn: 'retirada',
          exclusions: 'No incluye',
          discount: 'Descuento',
        },
        rowOpen: 'licitando, sin ofertas | licitando, 1 oferta | licitando, {count} ofertas',
        rowAwarded: 'adjudicado a {supplier}',
        panelAwarded: 'Adjudicado a {supplier} por {amount}. El comparativo queda como registro.',
        panelOpen: 'Licitando, todavía sin ofertas. | Licitando, con 1 oferta. | Licitando, con {count} ofertas.',
        panelNone: 'Pide precio a varias empresas sobre lo que reclama el paquete y compáralas contra su objetivo.',
        openComparison: 'Abrir el comparativo',
        start: 'Sacar a precio',
        fromTender: 'Adjudicada desde un comparativo de ofertas: ver las demás ofertas y el motivo',
      },
      // La base de precios de la casa, en la ficha de la partida (0.25.0).
      priceHistory: {
        title: 'Precios ofertados antes para esta unidad',
        empty: 'Todavía no hay ofertas guardadas para esta unidad de obra. Cada precio que entre en un comparativo aparecerá aquí.',
        min: 'Mínimo',
        median: 'Mediana',
        max: 'Máximo',
        lastAwarded: 'Último adjudicado',
        againstTarget: 'Precio objetivo de esta partida: {target}. Mediana de lo ofertado: {median}.',
        awarded: 'Adjudicado',
        howMatched: 'Se buscan por código de base de precios o por concepto parecido, con la misma unidad y los mismos números (diámetro, espesor, resistencia).',
      },
      item: {
        historicalNote: 'Esta partida es de una versión que ya no es la vigente: se consulta, no se cambia.',
        cancelAction: 'Anular partida',
        packageCommitted: '{amount} le llegan del contrato de su paquete {packages}. Ya están dentro. | {amount} le llegan del contrato de sus paquetes {packages}. Ya están dentro.',
        restoreAction: 'Reabrir partida',
        cancelTitle: '¿Anular esta partida?',
        cancelHelp: 'Su medición pasa a cero y deja de contar en la obra. No se borra: se queda con su historial y con el coste que ya tenga, y se puede reabrir.',
        cancelledToast: 'Partida anulada',
        restoreTitle: '¿Reabrir la partida?',
        restoreHelp: 'Vuelve con las mediciones que tenía antes de anularla.',
        restoredToast: 'Partida reabierta',
        reasonRequired: 'Motivo (obligatorio)',
        reasonOptional: 'Motivo (opcional)',
        reasonPlaceholder: 'Por qué, en una frase',
        lumpSum: 'Partida alzada',
        lumpSumHint: 'Se mide entera o nada: sin ejecutar, o ejecutada entera.',
        lumpNone: 'Sin ejecutar',
        lumpWhole: 'Ejecutada entera',
        derivedHelp: 'Partida a justificar: no se mide. Su avance sale del coste que se le imputa contra su objetivo.',
        pendingAuto: 'Pendiente calculado por el sistema.',
        pendingManual: 'Pendiente fijado a mano: {reason}',
        pendingAdjust: 'Ajustar pendiente',
        targetAdjust: 'Ajustar objetivo',
        targetTitle: 'Objetivo de la partida',
        targetHelp: 'Lo que se espera que cueste. Todas las cifras de control se juzgan contra este importe, así que el cambio queda en el historial con su motivo.',
        targetPriceLabel: 'Precio objetivo por {unit}',
        targetQuantityLabel: 'Medición objetivo ({unit})',
        targetPreview: 'Objetivo resultante: {amount}',
        targetReason: 'Motivo del cambio',
        targetReasonPlaceholder: 'Ej.: precio cerrado con el proveedor, revisión del estudio',
        targetSaved: 'Objetivo actualizado.',
        pendingTitle: 'Lo que falta por gastar',
        pendingHelp: 'Si sabes que lo que falta no es lo que sale del cálculo, escríbelo aquí. La previsión al cierre se rehace con esta cifra.',
        pendingEmpty: 'Vacío vuelve al cálculo automático. Cero significa que ya no falta nada.',
        pendingSaved: 'Pendiente guardado',
        cycle: {
          statusTitle: 'Cambiar la aprobación',
          stageTitle: 'Cambiar la ejecución',
          moveTo: 'Pasar a «{what}»',
          approveHelp: 'Al aprobarlo entra en la venta de la obra y ya se puede certificar. No tiene vuelta atrás.',
          saved: 'Modificado actualizado',
          status: {
            proposed: 'Volver a proponer',
            pricing: 'Pasar a valoración',
            sent: 'Enviar a la DF',
            approved: 'Aprobar',
            rejected: 'Rechazar',
          },
        },
        title: 'Partida',
        back: 'Al árbol',
        cancelled: 'Anulada',
        bornLosing: 'Nace perdiendo',
        sale: 'Venta',
        target: 'Objetivo',
        real: 'Real',
        deviation: 'Desvío',
        forecast: 'Previsión al cierre',
        committed: 'Comprometido',
        margin: 'Margen',
        excess: 'Se lleva construido {quantity} {unit} más de lo vendido ({pct}). Se señala, no se bloquea: quién lo paga lo decide una persona.',
        cancelledFor: 'Anulada: {reason}',
        newMeasure: 'Apuntar medición',
        newMeasureHint: 'A origen, no del mes: lo construido en total hasta hoy.',
        quantity: 'Cantidad a origen',
        comment: 'Comentario',
        record: 'Apuntar',
        recorded: 'Medición apuntada',
        date: 'Fecha',
        toDate: 'A origen',
        moved: 'Movió',
        who: 'Quién',
        length: 'Largo',
        width: 'Ancho',
        height: 'Alto',
        subtotal: 'Parcial',
        code: 'Código',
        concept: 'Concepto',
        performance: 'Rendimiento',
        price: 'Precio',
        breakdownCost: 'Lo que cuesta el descompuesto:',
        splitHelp: 'La partida conserva su objetivo íntegro: lo que reclaman los paquetes es como mucho ese objetivo, y nunca se lo resta. El reparto se decide desde el paquete, en su selector.',
        splitQuantity: 'En cantidad: {claimed} {unit} en paquetes y {free} libres, de {total}.',
        noPackages: {
          title: 'No está en ningún paquete',
          subtitle: 'Su objetivo no se ha sacado todavía al mercado, así que no hay disponible contra el que comparar un pedido. Se mete desde el selector de un paquete.',
        },
        costKind: 'Tipo',
        whatHappened: 'Qué pasó',
        detail: 'Detalle',
        bc3Lines: 'Las líneas del proyecto',
        tabs: {
          measurements: 'Mediciones',
          breakdown: 'Descompuesto',
          costs: 'Coste',
          history: 'Historial',
          packages: 'Repartida en',
          prices: 'Precios anteriores',
        },
        noMeasures: {
          title: 'Sin medir todavía',
          subtitle: 'Apunta arriba lo que se lleva construido, a origen.',
        },
        noBreakdown: {
          title: 'Sin descompuesto',
          subtitle: 'El fichero no traía los recursos de esta partida. Su objetivo sale del coeficiente de baja.',
        },
        noCosts: {
          title: 'Sin coste todavía',
          subtitle: 'Nada colgado de esta partida. Míralo en la bandeja de sin asignar.',
        },
        noHistory: {
          title: 'Sin cambios',
          subtitle: 'Nadie ha tocado esta partida desde que entró.',
        },
      },
      status2: {
        approved: 'Aprobada',
        proposed: 'Propuesta',
        pricing: 'En valoración',
        sent: 'Enviada a la DF',
        rejected: 'Rechazada',
      },
      stage: {
        not_started: 'Sin empezar', in_progress: 'En ejecución',
        done: 'Ejecutado', invoiced: 'Facturado', paid: 'Cobrado',
      },
      subcerts: {
        title: 'Certificación de subcontrata', back: 'Al contrato',
        openDraft: 'Abrir la que está en marcha',
        listTitle: 'Certificaciones', number: 'Certificación {number}',
        prepare: 'Preparar la siguiente',
        periodEnd: 'Hasta',
        period: 'Del periodo',
        periodHelp: 'Lo certificado este mes: la diferencia con lo que llevaba certificado.',
        accumulated: 'A origen', ceiling: 'Techo del contrato',
        ceilingHelp: 'Contratado más los extras aprobados. Pasarlo exige una adenda antes, no una explicación después.',
        charges: 'Cargos', penalties: 'Penalizaciones',
        kindLabel: 'Tipo', kind: { charge: 'Cargo', penalty: 'Penalización' },
        retention: 'Retención',
        retentionHelp: 'Sobre lo pagadero, no sobre lo certificado: si no, se retiene más de lo que dice el contrato.',
        payable: 'A pagar', payableHelp: 'La base de su factura de este mes.',
        consumed: '{pct} % del contrato',
        linesTitle: 'Partidas certificadas', addLine: 'Añadir partida',
        itemPlaceholder: 'Buscar la partida',
        before: 'Anterior', toDate: 'A origen', price: 'Precio', thisPeriod: 'Este mes',
        noLines: { title: 'Sin partidas', subtitle: 'Añade lo que se le certifica este mes. Las cantidades van a origen.' },
        none: { title: 'Sin certificaciones', subtitle: 'Todavía no se le ha certificado nada a esta subcontrata.' },
        deductionsTitle: 'Cargos y penalizaciones',
        noDeductions: { title: 'Sin descuentos', subtitle: 'No se le está descontando nada.' },
        addCharge: 'Añadir cargo', addPenalty: 'Añadir penalización',
        chargeHelp: 'Trabajo que hizo la casa y le tocaba a ella: la grúa que le puso el jefe de obra, la limpieza que no hizo. Se justifica con un albarán.',
        penaltyHelp: 'El contrato aplicándose: retraso, incumplimiento. Se justifica con una cláusula.',
        chargePlaceholder: 'Ej.: Grúa del día 12',
        penaltyPlaceholder: 'Ej.: Retraso de 10 días, cláusula 8.2',
        evidence: 'Justificante', evidencePlaceholder: 'Ej.: ALB-2026-77',
        appliedLabel: 'Estado', charged: 'Cobrado', pending: 'Pendiente',
        nothingHere: 'Nada que elegir.',
        refusalsTitle: 'Esto impide aprobarla',
        overContract: 'Se pasa del contrato: {accumulated} certificado contra un techo de {ceiling}. Hace falta una adenda.',
        overClient: 'En {item} se le certifican {toSub} {unit} y al cliente solo {toClient}. Se pagaría trabajo que todavía no se cobra.',
        scopeMax: 'máx. {max} {unit}',
        scopeNote: 'El máximo de cada partida sale de su paquete, {name}: lo que reclama, menos lo que ya lleven certificado otras subcontratas del mismo paquete.',
        overScope: 'En {item} se le certifican {asked} {unit} a origen y su paquete le da {scope}. Se pagaría lo que no es suyo.',
        warningsTitle: 'Míralo antes de aprobar',
        warn: {
          negative_period: 'El mes sale en negativo ({amount}): se está corrigiendo a la baja.',
          negative_payable: 'No hay nada que pagar: los descuentos se comen la certificación ({amount}).',
          line_went_back: 'En {item} se certifica menos que el mes pasado ({amount}).',
        },
        approve: 'Aprobar',
        approveConfirm: 'Se apunta el coste en el libro de la obra de este mes y baja el compromiso del contrato. A pagar: {amount}.',
        approved: 'Certificación aprobada',
        blocked: 'No se puede aprobar',
        certifiedLocked: 'Lo pone la suma de sus certificaciones.',
        status: {
          draft: 'Borrador', sent: 'Enviada', approved: 'Aprobada',
          rejected: 'Rechazada', cancelled: 'Anulada',
        },
        what: { created: 'Alta', status: 'Estado', lines: 'Partidas' },
      },
      templates: {
        title: 'Plantillas de indirectos', manage: 'Plantillas',
        yours: 'Tuyas ({count})', new: 'Nueva', newName: 'Plantilla nueva',
        builtIn: 'Del catálogo',
        builtInHelp: 'Esta es del catálogo y no se toca. Cópiala y edita la copia: así «edificación» sigue siendo la del catálogo y se sabe en qué se diferencia la tuya.',
        name: 'Nombre', forWhat: 'Para qué es',
        forWhatPlaceholder: 'Ej.: naves industriales de menos de 12 meses',
        concept: 'Concepto', amount: 'Importe', category: 'Categoría',
        addLine: 'Añadir concepto', noLines: 'Todavía no tiene conceptos.',
        lineCount: '{count} concepto | {count} conceptos',
        zeroHelp: 'El importe puede ir a cero, y no es un hueco sin rellenar: quiere decir que ese concepto existe en esta clase de obra y aquí no cuesta.',
        copy: 'Copiar', copySuffix: 'copia', copied: 'Plantilla copiada',
        apply: 'Usar en esta obra', saved: 'Plantilla guardada',
        saveFromWork: 'Guardar esta obra como plantilla',
        fromWorkSuffix: 'plantilla',
        savedFromWork: 'Guardada como «{name}»',
        dropTitle: 'Borrar la plantilla',
        dropConfirm: '«{name}» desaparece. Las obras que se abrieron con ella no cambian: ya tienen sus conceptos copiados.',
        none: { title: 'Sin plantillas', subtitle: 'Empieza por una del catálogo o hazte la tuya.' },
        pick: { title: 'Elige una', subtitle: 'A la izquierda están las tuyas y las del catálogo.' },
      },
      invoices: {
        title: 'Facturas registradas',
        register: 'Registrar una factura', registerFrom: 'Facturar esto',
        edit: 'La factura', saved: 'Guardado',
        uninvoiced: 'Sin factura',
        uninvoicedHelp: 'Coste que ya está y cuyo papel no ha llegado.',
        uninvoicedTitle: 'Lo que está sin facturar',
        received: 'Recibidas', paid: 'Pagadas',
        owed: 'Se le debe', owedHelp: 'Registrado y sin pagar: lo que hay que pagar.',
        supplier: 'Proveedor', number: 'Nº de factura',
        numberPlaceholder: 'Ej.: F-2026-0451',
        issueDate: 'Fecha', dueDate: 'Vence', state: 'Estado',
        dueHelp: 'Sin fecha no se avisa de nada: no está ni al día ni vencida.',
        amount: 'Base', amountHelp: 'Lo que se imputa a la obra. El IVA no es coste.',
        tax: 'IVA', notes: 'Notas',
        against: 'Contra qué coste va',
        nothingToPick: 'Este proveedor no tiene nada pendiente de facturar.',
        noSupplier: 'Sin proveedor',
        overdue: 'Vencida',
        markPaid: 'Marcar pagada', markUnpaid: 'Marcar no pagada',
        dropTitle: '¿Quitar la factura?',
        dropConfirm: 'La {number} desaparece y su coste vuelve a estar sin facturar.',
        status: { received: 'Recibida', paid: 'Pagada' },
        alerts: {
          overdue: '{count} factura vencida sin pagar | {count} facturas vencidas sin pagar',
          due_soon: '{count} factura vence esta semana | {count} facturas vencen esta semana',
          uninvoiced: 'Coste recibido del que todavía no ha llegado la factura',
        },
        none: { title: 'Sin facturas', subtitle: 'Aquí van las del material y las de las subcontratas.' },
        allInvoiced: { title: 'Todo facturado', subtitle: 'No hay coste recibido esperando su papel.' },
      },
      subcontracts: {
        title: 'Subcontrata', back: 'A la obra',
        new: 'Nueva subcontrata', create: 'Dar de alta',
        newHelp: 'Solo lo del contrato. Las partidas que cubre se atan después, en su ficha, que es donde se ve contra qué objetivo se compara.',
        companyPlaceholder: 'Buscar el proveedor',
        code: 'Contrato', codePlaceholder: 'Ej.: SUB-2026-03',
        scope: 'Alcance', scopePlaceholder: 'Qué trabajo cubre',
        start: 'Inicio', end: 'Fin previsto',
        retentionPct: 'Retención',
        paid: 'Pagado', saved: 'Guardado',
        targetHelp: 'El objetivo interno de las partidas que cubre. Sin partidas atadas no hay contra qué comparar.',
        extrasTitle: 'Extras del contrato',
        late: 'Fuera de plazo',
        noPackage: 'Sin paquete: no descuenta de ningún disponible',
        sources: {
          executedScope: 'lo que la obra lleva medido en su parte de cada partida, al precio de su contrato.',
          executedCertified: 'sin paquete no se sabe qué parte de la medición es suya: se enseña lo certificado.',
          certified: 'la suma de sus certificaciones aprobadas: lo de cada mes más sus extras.',
          invoiced: 'las facturas recibidas contra esas certificaciones, por su neto (los cargos y penalizaciones no se facturan).',
          paid: 'lo de esas facturas marcadas como pagadas, sin la retención que aplicó cada certificación, que se queda aparte.',
        },
        withExtras: 'incluye {amount} de extras',
        tabs: { summary: 'Resumen', money: 'Extras y descuentos', contract: 'El contrato' },
        documents: 'Papeles del contrato',
        addDocument: 'Subir un documento',
        uploading: 'Subiendo…', uploaded: 'Documento subido',
        noDocuments: 'Sin papeles. Sube aquí el contrato firmado, las adendas y los certificados.',
        dropDocument: 'Quitar el documento',
        dropDocumentConfirm: '«{name}» deja de estar en el contrato.',
        addExtra: 'Añadir extra',
        approveExtra: 'Aprobar',
        extraHelp: 'Con descripción obligatoria: un aumento de precio sin decir de qué no se recuerda al final de la obra. Nace sin aprobar, porque proponer no es acordar.',
        extraWhat: 'Concepto', extraPlaceholder: 'Ej.: Refuerzos no previstos en el pórtico 4',
        approved: 'Aprobado', notApproved: 'Sin aprobar', approvedAlready: '¿Ya acordado?',
        noExtras: { title: 'Sin extras', subtitle: 'El contrato va por lo que se firmó.' },
        covers: 'Cubre {count} partida | Cubre {count} partidas',
        scopeAlertsTitle: 'Certificado por encima de su alcance',
        scopeOver: '{item}: lleva {certified} {unit} certificados a origen y su paquete le da {scope}.',
        scopeFromPackage: 'Cubre lo que reclama su paquete, {name}: las partidas, sus cantidades y el tope de lo que se le puede certificar. Se lee del paquete, así que si el reparto cambia, esto también.',
        scopePackageEmpty: 'Su paquete, {name}, todavía no agrupa ninguna partida: cubre las suyas y no tiene tope de alcance.',
        scopeQuantity: 'Su parte',
        certifiedToDate: 'Certificado / máx.',
        certifiedOf: '{certified} de {max}',
        packageHelp: 'Le da su alcance: las partidas y cantidades que reclama el paquete, y el tope de lo que se le puede certificar.',
        noPackageOption: 'Sin paquete',
        packageLabel: 'Paquete que adjudica',
        noPackageOption: 'Sin paquete (se enlaza después)',
        packagePickHelp: 'Su alcance se leerá del paquete: las partidas y cantidades que reclama, y el tope de lo que se le puede certificar.',
        packagePicked: 'Heredará {count} partida con un objetivo de {amount}. | Heredará {count} partidas con un objetivo de {amount}.',
        noItems: 'Sin partidas atadas: no hay contra qué comparar el contrato.',
        openItem: 'Abrir la partida',
        to_signed: 'Firmar', to_in_progress: 'Empezó', to_finished: 'Terminó', to_closed: 'Liquidar',
        // Todos los apuntes que escribe ManageSubcontracts: sin los seis
        // últimos, la penalización del historial salía como clave cruda.
        what: {
          created: 'Alta', status: 'Estado', extra: 'Extra', package: 'Paquete',
          extra_approved: 'Extra aprobado', extra_unapproved: 'Extra sin aprobar',
          charge: 'Cargo', penalty: 'Penalización',
          charge_removed: 'Cargo retirado', penalty_removed: 'Penalización retirada',
        },
        company: 'Empresa', contracted: 'Contratado', extras: 'Extras',
        variance: 'Contra objetivo',
        varianceHelp: 'La previsión del contrato contra el objetivo interno de las partidas que cubre. Negativo es ganar.',
        outstanding: 'Se le debe',
        outstandingHelp: 'Lo pagadero de sus certificaciones (sin cargos, penalizaciones ni retención) menos lo pagado: lo que le debemos hoy.',
        retained: 'Retenido',
        retainedHelp: 'Garantía que le retenemos y le devolvemos al acabar. Se pacta en el contrato.',
        pendingExtras: '{amount} sin aprobar',
        status: {
          draft: 'Borrador', signed: 'Firmada', in_progress: 'Trabajando',
          finished: 'Terminada', closed: 'Liquidada', cancelled: 'Anulada',
        },
        empty: { title: 'Sin subcontratas', subtitle: 'Todo lo de esta obra lo hace la casa.' },
      },
      certifications: {
        number: 'Nº', period: 'Periodo',
        produced: 'Ejecutado', certified: 'Certificado', invoiced: 'Facturado', paid: 'Cobrado',
        toCertify: 'Pendiente de certificar',
        toCertifyHelp: 'Obra ejecutada que todavía no se le ha pasado al cliente. Es dinero hecho y sin reclamar.',
        toInvoice: 'Pendiente de facturar', toCollect: 'Pendiente de cobrar',
        retained: 'Retenido en garantía',
        retainedHelp: 'Dinero de la empresa que el cliente tiene y devuelve al final de la obra. Se olvida en todas.',
        current: 'De este mes', accumulated: 'A origen', payable: 'A cobrar',
        prepare: 'Preparar la del mes',
        previewTitle: 'La certificación de este mes',
        previewHelp: 'Todavía no existe: esto es lo que saldría con lo que la obra lleva ejecutado a origen. Al crearla nace en borrador, para que alguien la revise antes de emitirla.',
        prepareHelp: 'Se rellena con lo que la obra lleva ejecutado, a origen. Nace en borrador para que alguien la revise antes de emitirla.',
        prepared: 'Certificación preparada',
        to_issued: 'Emitir', to_invoiced: 'Facturar', to_paid: 'Cobrada',
        status: { draft: 'Borrador', issued: 'Emitida', invoiced: 'Facturada', paid: 'Cobrada' },
        empty: { title: 'Sin certificaciones', subtitle: 'Prepara la primera con lo que la obra lleva ejecutado.' },
      },
      // La ficha de una certificación al cliente (CertificationPage.vue).
      clientCert: {
        title: 'Certificación', number: 'Certificación nº {number}', back: 'Obra',
        until: 'hasta el {date}', unsaved: 'Cambios sin guardar',
        accumulated: 'A origen', previous: 'Certificado anterior', current: 'Este mes',
        retention: 'Retención ({pct} %)', payable: 'Líquido a cobrar',
        negativeHelp: 'Este mes sale en negativo: se certificó de más en una anterior y ahora se devuelve. Es la corrección a origen, no un error.',
        previousOf: 'Se resta contra la nº {number}, la última emitida antes de esta.',
        first: 'Es la primera: no hay nada anterior que restar.',
        reviewNote: 'Se ha rellenado con lo ejecutado a origen. Baja las líneas que la dirección facultativa no vaya a aceptar y guarda; no se puede pasar de lo ejecutado. Al emitirla ya no se toca: lo que haya que corregir irá en la siguiente.',
        readonly: {
          issued: 'Emitida: ya no se toca. Lo que haya que corregir se corrige en la siguiente, a origen.',
          historical: 'Hecha con una versión del presupuesto que ya no es la vigente: se lee, pero no se toca.',
          permission: 'Solo lectura: no tienes permiso para cambiar certificaciones.',
        },
        linesTitle: 'Líneas', linesCount: '{count} partida | {count} partidas',
        reset: 'Volver a lo ejecutado',
        resetHelp: 'Pone cada línea en lo que la obra lleva ejecutado a origen. No se guarda hasta que pulses «Guardar líneas».',
        overMax: '{count} línea pasa de lo ejecutado: bájala para poder guardar | {count} líneas pasan de lo ejecutado: bájalas para poder guardar',
        lowered: '{count} línea bajada respecto a lo ejecutado | {count} líneas bajadas respecto a lo ejecutado',
        empty: { title: 'Sin líneas', subtitle: 'No hay nada ejecutado que certificar en esta.' },
        col: {
          item: 'Partida', unit: 'Ud', contracted: 'Proyecto', executed: 'Ejecutado a origen',
          previous: 'Cert. anterior', toDate: 'A origen de esta', current: 'Este mes', toDateAmount: 'Importe a origen',
        },
        blocked: {
          cancelled: 'Anulada: se queda con lo que ya llevaba certificado.',
          unapproved_variation: 'Contradictorio sin aprobar: se queda con lo que ya llevaba certificado.',
        },
        max: 'máx. {max} {unit}',
        save: 'Guardar líneas', saved: 'Líneas guardadas',
        to_issued: 'Emitir', to_invoiced: 'Marcar facturada', to_paid: 'Marcar cobrada',
        invoiceTitle: 'Marcar como facturada',
        invoiceMessage: 'La nº {number} pasa a facturada por {amount}, lo certificado este mes.',
        invoiceRef: 'Número de factura',
        invoiceRefHelp: 'El de la factura que ha salido. Es lo que casa la certificación con el papel.',
        confirm: {
          issued: {
            title: 'Emitir la certificación nº {number}',
            message: 'Se emite por {amount} este mes. A partir de aquí no se puede tocar: lo que haya que corregir irá en la siguiente, a origen.',
          },
          paid: {
            title: 'Marcar como cobrada la nº {number}',
            message: 'Confirma que han entrado {amount} en el banco por esta certificación.',
          },
        },
        done: { issued: 'Certificación emitida', invoiced: 'Marcada como facturada', paid: 'Marcada como cobrada' },
        issuedAt: 'Emitida el {date}', invoicedAt: 'Facturada el {date} · {ref}', paidAt: 'Cobrada el {date}',
        download: 'Descargar PDF',
        downloadHelp: 'La certificación impresa, para la dirección facultativa y la propiedad. Un borrador sale con la marca «BORRADOR».',
        // Lo medido después de preparar el borrador, que no tiene línea.
        measured: {
          note: '{count} partida se ha medido después de preparar el borrador y no tiene línea. | {count} partidas se han medido después de preparar el borrador y no tienen línea.',
          add: 'Añadir lo medido desde entonces',
          title: 'Lo medido desde que se preparó',
          help: 'Partidas con ejecución que no estaban medidas cuando se preparó el borrador. Entran con lo ejecutado a origen y después se pueden bajar como cualquier otra línea. Al añadirlas se guardan las líneas, con los cambios que tengas.',
          confirm: 'Añadir {count} partida | Añadir {count} partidas',
          added: '{count} partida añadida | {count} partidas añadidas',
        },
        // Lo que el papel dice y la ficha no enseña. El resto sale de las
        // mismas claves que la pantalla (frontend/certificationPdf.js).
        pdf: {
          period: 'Periodo', date: 'Fecha', work: 'Obra', code: 'Código',
          price: 'Precio', currentAmount: 'Importe del mes', chapterTotal: 'Total capítulo',
          noVat: 'Importes sin IVA.', notes: 'Observaciones',
          signContractor: 'La empresa constructora', signDirection: 'La dirección facultativa',
          signAgreed: 'Conforme', signName: 'Nombre y firma', signDate: 'Fecha',
        },
      },
      variations: {
        new: 'Nuevo modificado',
        newHelp: 'Nace propuesto y no suma a la venta hasta que se aprueba. La aprobación y la ejecución se llevan desde su ficha.',
        create: 'Proponer',
        created: 'Modificado propuesto',
        chapter: 'Capítulo',
        noChapter: 'Sin capítulo',
        kind: 'Tipo de partida',
        customerRef: 'Referencia del cliente',
        customerRefHint: 'La orden o el número con que la propiedad o la DF lo conoce.',
        reason: 'Motivo',
        reasonPlaceholder: 'Por qué aparece este trabajo',
        what: 'Modificado', approval: 'Aprobación', stage: 'Trabajo',
        approved: 'Aprobado y en venta',
        approvedHelp: 'Extras que el cliente ha aceptado. Ya suman a la venta de la obra.',
        open: 'Pedido y sin respuesta',
        openHelp: 'Propuesto o enviado a la dirección facultativa. Todavía no suma a la venta: nadie ha dicho que lo vaya a pagar.',
        atRisk: 'Ejecutado sin aprobar',
        atRiskHelp: 'Trabajo hecho que ya ha costado dinero y que nadie ha aprobado. Es lo que se está regalando.',
        atRiskNote: '{count} extra ejecutado sin aprobar, con {cost} de coste ya dentro de la previsión. Se reclama ahora o no se cobra. | {count} extras ejecutados sin aprobar, con {cost} de coste ya dentro de la previsión. Se reclaman ahora o no se cobran.',
        empty: { title: 'Sin modificados', subtitle: 'Todo lo que se está construyendo estaba en el proyecto.' },
      },
      costKind: {
        incurred: 'Gastado',
        committed: 'Comprometido',
      },
      lineType: {
        measurement: 'Medición',
        subtotal_partial: 'Subtotal',
        subtotal_running: 'Acumulado',
        expression: 'Fórmula',
      },
      historyWhat: {
        created: 'Alta',
        progress: 'Medición',
        target: 'Objetivo',
        status: 'Estado',
        cancelled: 'Anulada',
        restored: 'Reabierta',
        edited: 'Editada',
        stage: 'Etapa',
        copied: 'Copiada a la revisión',
        assigned: 'A un paquete',
        unassigned: 'Fuera de un paquete',
        packages: 'Reparto en paquetes',
        pending: 'Pendiente fijado a mano',
      },
      historyField: {
        code: 'código',
        description: 'concepto',
        label: 'resumen',
        longText: 'texto largo',
        unit: 'unidad',
        kind: 'tipo',
        saleQuantity: 'medición de venta',
        salePrice: 'precio de venta',
        targetQuantity: 'medición objetivo',
        targetPrice: 'precio objetivo',
        pendingOverride: 'pendiente',
      },
      historyExtra: {
        automatic: 'automático',
        packagesCount: '{count} paquete | {count} paquetes',
        copiedDetail: 'Nueva revisión del presupuesto',
      },

      title: 'Obras',

      related: { forCustomer: 'Obras' },

      help: {
        title: 'Obras',
        subtitle: 'Del presupuesto a la previsión de cierre: objetivo, paquetes de contratación, subcontratas y certificaciones',
      },

      scope: { only: 'Solo las obras de {name}', showAll: 'Ver todas', oneCustomer: 'este cliente' },
      subtitle: 'El presupuesto de cada obra por capítulos y partidas, y lo que lleva gastado',
      detailTitle: 'Obra',

      search: { subtitle: 'Obra' },
      dashboard: {
        works: 'Obras en marcha',
        worksHelp: 'Cuántas obras hay abiertas hoy, sin contar las cerradas ni las anuladas.',
        production: 'Producción a origen',
        productionHelp: 'Lo construido hasta hoy en las obras en marcha, valorado a precio de venta. Esté certificado o no.',
        backlog: 'Cartera por ejecutar',
        backlogHelp: 'Lo vendido que todavía está por construir en las obras en marcha.',
        toCertify: 'Pendiente de certificar',
        toCertifyHelp: 'Lo producido que aún no ha entrado en ninguna certificación al cliente.',
        receivable: 'Pendiente de cobro',
        receivableHelp: 'Certificaciones emitidas o facturadas y todavía sin cobrar, con la retención ya descontada.',
        margin: 'Margen previsto',
        marginHelp: 'El margen que se espera al cierre de las obras en marcha, sobre la venta y ponderado por importe.',
        deviation: 'Desvío sobre objetivo',
        deviationHelp: 'Cuánto se lleva gastado por encima del objetivo repartido, sumando todas las obras en marcha.',
        unassigned: 'Coste sin asignar',
        unassignedHelp: 'Dinero imputado a una obra y todavía sin partida. Mientras no esté a cero, el margen está incompleto.',
        supplierInvoices: 'Facturas de proveedor por pagar',
        supplierInvoicesHelp: 'Lo recibido y no pagado de los proveedores de obra, con IVA.',
        worksList: 'Obras, una a una',
        worksListHelp: 'Las obras en marcha ordenadas por importe, con su avance y su margen.',
        deviated: 'Obras desviadas',
        deviatedHelp: 'Las obras que van por encima del objetivo, de mayor a menor desvío.',
      },

      actions: {
        new: 'Nueva obra',
        open: 'Abrir la obra',
        import: 'Importar BC3',
        more: 'Acciones',
      },

      status: {
        draft: 'Borrador',
        live: 'En ejecución',
        closed: 'Cerrada',
        cancelled: 'Anulada',
      },

      fields: {
        code: "Código de obra",
        manager: "Jefe de obra",
        address: "Dirección",
        city: "Municipio",
        postalCode: "Código postal",
        labourRate: "Precio de la hora",
        labourRateHelp: "Lo que cuesta una hora de cuadrilla en esta obra. Con esto, las horas de los partes llegan valoradas; sin esto llegan contadas y sin importe.",
        name: 'Nombre de la obra',
        customerName: 'Cliente',
        fileNumber: 'Expediente',
        authority: 'Organismo',
        tenderAmount: 'Importe de licitación',
        awardAmount: 'Importe de adjudicación',
        lowCoefficient: 'Baja',
        lastData: 'Último dato',
      },

      header: { collapse: 'Contraer', expand: 'Ver las cifras' },
      figures: {
        sale: 'Venta',
        saleHelp: 'Lo que se le factura al cliente por esta obra, al precio del contrato.',
        target: 'Objetivo',
        targetHelp: 'Lo que la empresa se propuso gastar en ejecutarla. Es contra esto contra lo que se juzga el coste.',
        spent: 'Gastado',
        real: 'Real',
        realHelp: 'Coste que YA se ha producido: albaranes recibidos, horas puestas, maquinaria usada.',
        committed: 'Comprometido',
        committedHelp: 'Pedidos, contratos y obligaciones ya aprobadas que todavía no son coste, pero que ya no se pueden evitar.',
        pending: 'Pendiente',
        pendingHelp: 'Lo que se estima que falta por gastar para terminar, descontado lo que ya está comprometido.',
        forecast: 'Previsión',
        forecastHelp: 'En cuánto acaba la obra: real + comprometido + pendiente. Ningún euro se cuenta dos veces.',
        forecastMargin: 'Margen previsto',
        forecastMarginHelp: 'Lo que quedará al final: venta menos previsión. No es el margen del contrato, es el que se va a cobrar.',
        margin: 'Margen',
        notInvoiced: 'Sin factura',
        notInvoicedHelp: 'Parte de lo real que ya se ha ejecutado y cuya factura de proveedor todavía no ha llegado. Ya está contado dentro de Real.',
        unassigned: 'Sin asignar',
        unassignedHelp: 'Dinero gastado en esta obra que todavía no cuelga de ninguna partida. Ya cuenta como coste; lo que falta es decir de qué.',
        progress: 'Avance físico',
        consumed: 'Coste consumido',
        costAhead: 'El coste va por delante de la ejecución: se está gastando más rápido de lo que se construye.',
      },

      alerts: {
        title: 'Qué mirar hoy',
        more: 'Ver {count} más',
        work_over_target: 'la obra va a acabar por encima del objetivo',
        work_negative_margin: 'la obra va a costar más de lo que se vendió',
        work_cost_ahead_of_progress: 'el coste corre por delante de la ejecución',
        unassigned_significant: 'hay mucho coste sin colgar de ninguna partida',
        unassigned_stale: 'hay costes esperando desde hace semanas sin asignar',
        item_over_target: 'va a acabar por encima de su objetivo',
        item_negative_margin: 'se va a vender por debajo de lo que va a costar',
        cost_ahead_of_progress: 'gasta más rápido de lo que construye',
        item_near_target: 'casi ha consumido su objetivo y queda trabajo',
        committed_without_progress: 'todo pedido y sin empezar',
        item_born_losing: 'su descompuesto cuesta más que su precio objetivo',
        variation_executed_unapproved: 'ejecutado y sin aprobar: se está regalando',
        variation_awaiting_approval: 'esperando respuesta del cliente',
      },

      closing: {
        against: 'Contra un objetivo de {target}:',
        marginInitial: 'Margen del contrato',
        marginInitialHelp: 'El que se firmó: venta menos objetivo.',
        marginCurrent: 'Margen previsto',
        marginChange: 'Lo que se ha ido',
        marginChangeHelp: 'La diferencia entre el margen que se firmó y el que se prevé cobrar.',
        manual: '{count} de {items} partidas llevan el pendiente puesto a mano.',
        ranking: 'Dónde estamos perdiendo dinero',
        unexplained: '{amount} sin explicar',
        noCauses: 'nadie ha dicho por qué',
      },

      causes: {
        title: 'Por qué se desvía',
        edit: 'Clasificar',
        help: 'Reparte la desviación por causas. Lo que no encaje en ninguna se queda sin explicar, y se enseña como tal: cuadrarlo metiendo la diferencia en «Otros» sería inventarse una explicación.',
        what: 'Causa', amount: 'Importe', note: 'Detalle',
        notePlaceholder: 'Obligatorio en «Otros»',
        add: 'Añadir causa',
        sum: 'Explicado {explained} de {variance}. Sin explicar: {left}.',
        saved: 'Causas guardadas',
        unexplained: 'Sin explicar',
        none: 'Nadie ha dicho todavía por qué se desvía.',
        chargeable: '{amount} se le puede pasar al cliente.',
        supplier_price: 'Precio de proveedor',
        more_quantity: 'Mayor medición',
        extra_hours: 'Horas adicionales',
        budget_error: 'Error de presupuesto',
        scope_change: 'Cambio de alcance',
        customer_variation: 'Modificado del cliente',
        delay: 'Retraso',
        rework: 'Retrabajo',
        material_waste: 'Desperdicio de material',
        subcontract_price: 'Subcontrata más cara',
        extra_plant: 'Maquinaria adicional',
        other: 'Otros',
      },

      sort: { label: 'Ordenar por', variance: 'Mayor desviación', margin: 'Menor margen', unassigned: 'Más sin asignar', sale: 'Mayor importe', name: 'Nombre' },
      stats: { works: 'Obras' },

      list: { work: 'Obra' },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },

      filters: {
        searchPlaceholder: 'Nombre, expediente, cliente…',
        status: 'Estado',
        allStatuses: 'Todas',
      },

      empty: {
        title: 'Todavía no hay obras',
        subtitle: 'Una obra es un proyecto con presupuesto por partidas. Abre la primera y métele su BC3.',
      },

      new: {
        codePlaceholder: "OBR-2026-014",
        customerPlaceholder: "Busca el cliente",
        managerPlaceholder: "Busca a la persona",
        title: 'Abrir una obra',
        help: "La obra es la ficha principal: su código, su cliente y su jefe de obra. El presupuesto por partidas se le mete después, con el BC3.",
      },

      tabs: {
        tree: 'Partidas',
        packages: 'Paquetes',
        variations: 'Modificados y contradictorios',
        versions: 'Revisiones',
        subcontracts: 'Subcontratas',
        certifications: 'Certificaciones',
        invoices: 'Facturas recibidas',
        closing: 'Previsión de cierre',
        unassigned: 'Costes sin imputar',
        indirect: 'Costes indirectos',
        report: 'La importación',
      },

      // El menú lateral, con sus subapartados (0.26.0). Ninguno se llama
      // «Compras» ni «Presupuestos»: esos son otros módulos.
      nav: {
        works: 'Obras',
        contracting: 'Contratación',
        subcontracts: 'Subcontratas',
        certifications: 'Certificaciones',
        prices: 'Precios de obra',
      },

      // Los cinco apartados de la ficha de una obra (0.26.0).
      sections: {
        label: 'Apartados de la obra',
        budget: 'Presupuesto de obra',
        contracting: 'Contratación',
        production: 'Producción y cobro',
        control: 'Control',
        daily: 'Día a día',
      },

      // La pestaña «Revisiones» del presupuesto de obra (0.26.0).
      revisions: {
        name: 'Versión',
        created: 'Creada',
        current: 'Vigente',
        historical: 'Histórica',
        viewing: 'Es la que estás viendo en el árbol',
        view: 'Ver en el árbol',
        compare: 'Comparar con la vigente',
        noReport: 'Esta versión no vino de un BC3: se escribió a mano o es copia de otra.',
      },

      // Las pantallas del menú que cruzan todas las obras (0.26.0).
      across: {
        work: 'Obra',
        allWorks: 'Todas las obras',
        allBuyers: 'Todos',
        attention: 'Lo que hay que atender',
        warnings: 'Avisos',
        contracting: {
          title: 'Contratación',
          subtitle: 'Los paquetes y comparativos de todas las obras, contra su objetivo',
          count: 'Paquetes',
          emptyTitle: 'Ningún paquete con estos filtros',
          emptyHelp: 'Los paquetes se crean en cada obra, en «Contratación». Aquí salen los de todas.',
          alerts: {
            offers_expired: '{name} ({work}): {count} oferta caducada; la más barata, {supplier} | {name} ({work}): {count} ofertas caducadas; la más barata, {supplier}',
            offers_expiring: '{name} ({work}): {count} oferta caduca esta semana; la más barata, {supplier} | {name} ({work}): {count} ofertas caducan esta semana; la más barata, {supplier}',
            awarded_over_target: '{name} ({work}) se adjudicó a {supplier} un {pct} % por encima del objetivo',
            tendering_no_offers: '{name} ({work}) está en contratación y no tiene ninguna oferta',
          },
        },
        subcontracts: {
          title: 'Subcontratas',
          subtitle: 'Todos los contratos de todas las obras: lo contratado, lo certificado y lo que se les debe',
          count: 'Contratos',
          search: 'Empresa, código o alcance…',
          pending: 'Certificaciones por aprobar',
          pendingHelp: 'Certificaciones de subcontrata en borrador o enviadas que todavía nadie ha aprobado.',
          pendingShort: 'Por aprobar',
          pendingCount: '{count} por aprobar | {count} por aprobar',
          warnings: {
            late: 'Fuera de plazo',
            over_scope: 'Certificado de más',
          },
        },
        certifications: {
          title: 'Certificaciones',
          subtitle: 'Las certificaciones al cliente de todas las obras: qué revisar, qué facturar y qué cobrar',
          count: 'Certificaciones',
          missingTitle: 'Obras sin la certificación de este mes',
          missingRow: '{work} · {name}: producción sin certificar',
          bucket: 'Qué toca',
          allBuckets: 'Todas',
          bucketCount: '{count} certificación | {count} certificaciones',
          nextLabel: 'Siguiente paso',
          buckets: {
            review: 'Borradores por revisar',
            to_invoice: 'Emitidas por facturar',
            to_collect: 'Facturadas por cobrar',
            done: 'Cobradas',
          },
          next: {
            review: 'Revisar y emitir',
            to_invoice: 'Facturar',
            to_collect: 'Cobrar',
            done: 'Cobrada',
          },
        },
        prices: {
          title: 'Precios de obra',
          subtitle: 'Lo que las empresas han ofertado y lo que se ha contratado, en todas las obras',
          count: 'Precios',
          search: 'Concepto o código…',
          unitPlaceholder: 'm3, m2, kg…',
          supplier: 'Empresa',
          which: 'Qué precios',
          allPrices: 'Ofertados y contratados',
          onlyAwarded: 'Solo contratados',
          mixedUnits: 'Hay precios de varias unidades. Escribe una unidad para ver el precio medio, el mínimo y el último.',
          mean: 'Precio medio',
          min: 'Mínimo',
          max: 'Máximo',
          last: 'Último',
          lastAwarded: 'Último contratado',
          fromWhere: '{supplier} · {work} · {date}',
          awarded: 'Contratado',
          origin: 'Obra y comparativo',
          date: 'Fecha',
          emptyTitle: 'Ningún precio con estos filtros',
          emptyHelp: 'Cada oferta que se mete en un comparativo deja aquí sus precios, línea a línea.',
          onlySupplier: 'Solo los precios de {name}',
          oneSupplier: 'este proveedor',
        },
      },

      // La pestaña «En obras» que este módulo cuelga en la ficha de un
      // proveedor, que es de compras (0.26.0).
      supplierPanel: {
        title: 'En obras',
        awards: 'Adjudicaciones',
        offers: 'Ofertas en comparativos',
        subcontracts: 'Subcontratas',
        prices: 'Histórico de precios',
        pricesRow: 'Sus precios en los comparativos de obra',
      },

      // Anular un coste tecleado a mano (0.26.0).
      voidCost: {
        action: 'Anular',
        title: 'Anular este coste',
        help: '«{description}», {amount}. Desaparece de la obra y de su partida; el motivo queda en el registro de actividad.',
        reason: 'Motivo',
        reasonPlaceholder: 'Ej.: tecleado dos veces',
        confirm: 'Anular el coste',
        done: 'Coste anulado.',
      },

      packages: {
        title: 'Paquetes de contratación',
        newPackage: 'Nuevo paquete',
        newHelp: 'Un paquete es la unidad contra la que se COMPRA: agrupa partidas para pedir precio a un proveedor. Se crea vacío y las partidas se le asignan después.',
        createPackage: 'Crear paquete',
        newName: 'Paquete nuevo',
        buyer: 'Responsable de compra',
        buyerPlaceholder: 'Busca a alguien de la casa',
        empty: {
          title: 'Sin paquetes de contratación',
          subtitle: 'Un paquete es lo que se contrata de una vez: «estructura» son el hormigón, el acero y el encofrado de doce partidas. Sin paquetes no hay disponible, y sin disponible nadie sabe si un pedido cabe en lo presupuestado.',
        },
        type: {
          supply: 'Suministro',
          subcontract: 'Subcontrata',
          hire: 'Alquiler',
          service: 'Servicio',
          own_means: 'Medios propios',
        },
        columns: {
          package: 'Paquete',
          type: 'Tipo',
          target: 'Objetivo',
          available: 'Disponible',
        },
        availableHelp: 'Objetivo menos lo comprometido y lo gastado. Es lo que queda para comprar.',
        itemCount: '{count} partida | {count} partidas',
        itemsTitle: 'Qué agrupa',
        noItems: 'Todavía no agrupa ninguna partida, así que no tiene objetivo contra el que comprar.',
        addItems: 'Añadir partidas',
        ctc: 'Lo que falta por gastar',
        ctcValue: 'Fijado a mano',
        ctcHint: 'Si lo dejas vacío, el motor propone {amount}.',
        ctcReason: 'Por qué',
        ctcReasonPlaceholder: 'Ej.: la partida está acabada, no va a gastar más',
        ctcManual: 'La previsión de este paquete usa tu cifra, no la calculada.',
        calculatedHelp: 'Suma de las {count} partidas que agrupa: {amount}.',
        manualAdjust: 'Ajuste manual',
        manualAdjustHelp: 'Lo que el jefe de obra sabe y el árbol no dice. Con motivo, siempre.',
        reason: 'Motivo del ajuste',
        removeTitle: '¿Borrar este paquete?',
        removeHelp: 'Solo se borra el que no tiene ninguna compra. En cuanto tiene pedidos o gasto se CIERRA, que además es lo correcto: dejó de comprarse, no dejó de haberse comprado.',
        alerts: {
          over_committed: 'En {name} se ha comprometido {amount} más de lo que había.',
          forecast_over: '{name} prevé cerrar {amount} por encima del objetivo.',
          suspicious_ctc: '{name} no tiene nada comprometido ni nada por gastar, y la obra no está hecha.',
          no_target: 'Se está gastando en {name} sin objetivo detrás.',
          own_means_committed: '{name} es de medios propios y tiene {amount} comprometidos: o el pedido va al paquete equivocado, o el paquete no era de medios propios.',
          sub_over_scope: '{supplier} lleva certificados {certified} {unit} de {item} y su paquete, {name}, le da {scope}. Se paga lo que no es suyo.',
          quantity_over_measurement: 'En {item} los paquetes reclaman {claimed} {unit} y la medición es {measurement}. Hay que decidir quién se queda sin los que faltan.',
          offers_expired: '{name} está licitando con {count} oferta caducada: la más barata, {supplier} | {name} está licitando con {count} ofertas caducadas: la más barata, {supplier}',
          awarded_over_target: '{name} se adjudicó a {supplier} un {pct} % por encima del objetivo.',
          awarded_incomplete: '{name} se adjudicó a {supplier} dejando {count} línea sin contratar. | {name} se adjudicó a {supplier} dejando {count} líneas sin contratar.',
        },
        status: 'Estado',
        statusOf: {
          open: 'Abierto',
          tendering: 'En contratación',
          awarded: 'Contratado',
          closed: 'Cerrado',
        },
        typeHelp: {
          supply: 'Se comprará con pedido. Genera comprometido al aprobarlo.',
          hire: 'Se comprará con pedido. Genera comprometido al aprobarlo.',
          service: 'Se comprará con pedido. Genera comprometido al aprobarlo.',
          subcontract: 'Se contratará con techo, retención y certificaciones.',
          own_means: 'No se compra. Se controla por rendimiento desde los partes.',
        },
        // El historial corto del panel del paquete (0.26.0).
        history: {
          title: 'Lo que le ha pasado',
          created: 'Se dio de alta',
          edited: 'Se editó',
          committed: 'Pasó solo a «{to}» al llegar {reference} por {amount}',
          status: 'Pasó de «{from}» a «{to}»',
          other: 'Cambio en el paquete',
          assigned: 'Cambió lo que agrupa',
          tender: 'Movimiento en su comparativo de ofertas',
        },
      },
      coverage: {
        target: 'Objetivo de coste de la obra',
        covered: 'Cubierto por paquetes',
        pct: '{pct} % del objetivo',
        unpackaged: 'Sin empaquetar',
        orphans: 'Ver la partida suelta · {amount} | Ver las {count} partidas sueltas · {amount}',
        approve: 'Aprobar el objetivo',
        approveTitle: '¿Aprobar el objetivo de esta obra?',
        approveHelp: 'A partir de aquí las desviaciones se miden contra esta raya, y el reparto entre paquetes queda quieto. Se puede levantar después, con motivo.',
        approved: 'Objetivo aprobado.',
        approvedBy: 'Aprobado por {who} el {when}',
        cannotApprove: 'No se puede aprobar mientras quede objetivo fuera de todo paquete: ese dinero no tiene disponible, así que ningún pedido contra él se puede comparar con nada.',
        blocked: 'El objetivo está aprobado: el reparto no se toca. Levanta la aprobación para cambiarlo.',
        revoke: 'Levantar la aprobación',
        revokeTitle: 'Levantar la aprobación del objetivo',
        revokeHelp: 'El reparto vuelve a poder tocarse. Queda escrito quién lo levantó y por qué.',
        revokeReason: 'Por qué se levanta',
      },
      picker: {
        title: 'Qué agrupa {name}',
        search: 'Buscar por código o concepto',
        onlyFree: 'Solo con disponible',
        natureHint: 'Se añaden las líneas de esa naturaleza, nunca la partida entera.',
        claimed: 'Asignado',
        free: 'Disponible',
        assigned: 'Asignada',
        noBreakdown: 'sin descompuesto',
        takenBy: 'Se la llevan: {packages}',
        allChapters: 'Todos los capítulos',
        allNatures: 'Todas las naturalezas',
        amountLabel: 'Importe (máximo {max})',
        amountReason: 'Por qué este importe',
        amountReasonPlaceholder: 'Ej.: solo el material; la bomba la pone la subcontrata',
        amountConfirm: 'Añadir con este importe',
        freeQuantity: 'Libre: {free} {unit} de {total}',
        byQuantity: 'Por cantidad',
        byAmount: 'Por importe',
        quantityLabel: 'Cantidad en {unit} (máximo {max})',
        quantityHint: '{pct} % de la medición · {amount}',
        quantityReason: 'Por qué esta cantidad',
        quantityReasonPlaceholder: 'Ej.: tramo norte, del P.K. 0+000 al 0+400',
        quantityConfirm: 'Añadir con esta cantidad',
        quantityCol: 'Cantidad',
        tally: '{lines} de {items} · {amount}',
        tallyLines: '0 líneas | {count} línea | {count} líneas',
        tallyItems: '0 partidas | {count} partida | {count} partidas',
        pickAll: 'Seleccionar todo lo filtrado',
        clear: 'Quitar la selección',
        add: 'Añadir {count}',
        added: 'Añadidas {count} asignaciones.',
        stale: 'revisar',
        origin: 'De dónde sale',
        mode: {
          whole: 'Partida entera',
          lines: 'Línea',
          pct: 'Porcentaje',
          amount: 'Importe',
          quantity: 'Cantidad',
        },
        dropTitle: '¿Quitar {code} del paquete?',
        dropHelp: 'La partida conserva su objetivo íntegro; lo que se quita es lo que este paquete reclamaba de ella.',
        dropReason: 'Por qué se quita',
        dropReasonHelp: 'Este paquete ya ha comprado: quitarle una partida le baja el objetivo sin bajarle el gasto, así que su disponible se estrecha de golpe.',
        none: {
          title: 'No hay partidas que encajen',
          subtitle: 'Prueba a quitar el filtro de naturaleza o de capítulo. Si la obra no tiene presupuesto todavía, no hay nada que empaquetar.',
        },
      },
      indirect: {
        templates: {
          'IND-01': "Jefe de obra",
          'IND-02': "Encargado",
          'IND-03': "Administrativo de obra",
          'IND-10': "Casetas de obra",
          'IND-11': "Vallado provisional",
          'IND-12': "Acometidas provisionales",
          'IND-13': "Medios auxiliares comunes",
          'IND-20': "Electricidad",
          'IND-21': "Agua",
          'IND-22': "Internet y comunicaciones",
          'IND-30': "Limpieza",
          'IND-31': "Vigilancia",
          'IND-32': "Seguridad y salud general",
          'IND-33': "Gestión de residuos general",
          'IND-34': "Seguros específicos de obra",
          'IND-40': "Vehículos de obra",
          'IND-41': "Combustible",
          'IND-42': "Desplazamientos y dietas",
          'IND-90': "Alquileres generales",
          'IND-91': "Pequeños consumibles",
          'IND-92': "Otros costes generales de obra",
        },
        title: 'Costes indirectos de obra',
        subtitle: 'Lo que cuesta tener la obra abierta y no pertenece a ninguna partida.',
        empty: {
          title: 'Sin presupuesto de indirectos',
          subtitle: 'El jefe de obra, la caseta, la vigilancia y la luz cuestan dinero cada mes que la obra sigue abierta. Sin presupuestarlos, la obra dice que gana más de lo que gana.',
        },
        fromTemplate: 'Empezar desde una plantilla',
        templateKind: 'Tipo de obra',
        clear: 'Vaciar el bloque',
        restart: 'Empezar de nuevo',
        restartTitle: '¿Empezar de nuevo?',
        restartConfirm: 'Se quitan los {count} conceptos y se abre el gestor para elegir otra plantilla. Lo que ya tenga coste imputado se queda.',
        clearTitle: '¿Vaciar los costes indirectos?',
        clearConfirm: 'Se quitan los {count} conceptos de esta obra. Los que ya tengan coste imputado se quedan: eso no se borra, se cierra.',
        cleared: 'Vaciado: {count} conceptos fuera',
        clearedSome: 'Fuera {count}. Se quedan, porque ya tienen coste: {kept}',
        removeTitle: '¿Quitar el concepto?',
        removeConfirm: '«{name}» desaparece del presupuesto de indirectos. Si ya tiene coste imputado, no se podrá: ciérralo en vez de quitarlo.',
        fullscreenHint: 'Costes indirectos a pantalla completa · Escape para salir',
        newConcept: 'Añadir concepto',
        created: 'Presupuesto creado con {count} conceptos. Ponles importe.',
        kind: {
          building: 'Edificación',
          industrial: 'Nave industrial',
          refurbishment: 'Rehabilitación',
          civil: 'Obra civil',
          fit_out: 'Reforma',
        },
        category: {
          personnel: 'Personal de obra',
          facilities: 'Instalaciones provisionales',
          utilities: 'Suministros',
          services: 'Servicios generales',
          vehicles: 'Vehículos y desplazamientos',
          other: 'Otros',
        },
        behaviour: {
          label: 'Comportamiento',
          help: 'Decide cómo se estima lo que falta por gastar.',
          fixed: 'Pago único',
          monthly: 'Mensual',
          weekly: 'Semanal',
          daily: 'Diario',
          variable: 'Variable',
          manual: 'Manual',
        },
        rate: 'Coste por periodo',
        rateHelp: 'Lo que cuesta cada mes, semana o día. De aquí sale lo que falta por gastar.',
        durationSensitive: 'Depende del plazo',
        durationSensitiveHelp: 'Si un mes más de obra lo hace costar más. Un seguro decenal, no; el jefe de obra, sí.',
        closed: 'Cerrado',
        closedHelp: 'Ya no va a costar más. Un concepto cuya fecha pasó sigue costando si nadie lo cierra.',
        columns: {
          concept: 'Concepto',
          type: 'Tipo',
          target: 'Objetivo',
          real: 'Real',
          committed: 'Comprometido',
          pending: 'Pendiente previsto',
          forecast: 'Previsión cierre',
          deviation: 'Desviación',
          consumed: '% consumido',
        },
        totals: {
          target: 'Objetivo',
          real: 'Real',
          committed: 'Comprometido',
          pending: 'Pendiente previsto',
          forecast: 'Previsión cierre',
          deviation: 'Desviación',
        },
        duration: {
          title: 'El plazo',
          planned: 'Fin planificado',
          forecast: 'Fin previsto',
          extra: '{days} días de más',
          perMonth: '{amount} al mes dependen del plazo',
          impact: 'Sobrecoste por ampliación del plazo',
          shift: 'Mover el fin previsto',
          shiftTitle: '¿Mover el fin previsto de toda la obra?',
          shiftHelp: 'Se mueve en los {count} conceptos que dependen del plazo. Es lo que de verdad pasa: la obra se retrasa y con ella la caseta, la vigilancia y el jefe de obra.',
          shiftReason: 'Por qué se retrasa',
          moved: 'Movidos {count} conceptos.',
          onTime: 'La obra va en plazo.',
        },
        deviations: { title: 'Principales desviaciones', empty: 'Ningún concepto se está pasando del objetivo.' },
        evolution: { title: 'Evolución mensual', real: 'Real', target: 'Objetivo repartido', linear: 'El objetivo se reparte lineal a lo largo del plazo.' },
        movements: { title: 'De dónde sale', empty: 'Todavía no se le ha imputado nada.', origin: 'Origen', date: 'Fecha', amount: 'Importe' },
        identity: 'Identificación',
        family: 'Familia',
        budget: 'Presupuesto',
        period: 'Periodo',
        startDate: 'Inicio previsto',
        endDate: 'Fin previsto',
        actualStart: 'Inicio real',
        forecastEnd: 'Fin previsto actualizado',
        initialTarget: 'Presupuesto inicial',
        pendingOverride: 'Pendiente a mano',
        pendingOverrideHelp: 'Cuando lo que falta lo dice una persona y no el cálculo por plazo.',
        removeTitle: '¿Borrar este concepto?',
        removeHelp: 'Solo se borra el que no tiene ningún movimiento. En cuanto tiene coste imputado se CIERRA, que además es lo correcto: dejó de correr, no dejó de haber pasado.',
        alerts: {
          duration_overrun: 'El retraso de {days} días supone {amount} más en costes indirectos.',
          over_budget: '{name} supera el objetivo en {pct} %.',
          forecast_over: '{name} prevé cerrar {amount} por encima del objetivo.',
          past_due: '{name} sigue generando coste {days} días después de su fecha prevista.',
          no_budget: 'Se ha registrado coste en {name} sin presupuesto asociado.',
          unbooked_periods: 'Llevan {periods} periodos corridos de {name} y faltan {amount} por imputar.',
        },
        unassignedHint: 'Hay {count} movimiento sin imputar en esta obra. Podría ser indirecto. | Hay {count} movimientos sin imputar en esta obra. Alguno podría ser indirecto.',
      },
      versions: {
        label: 'Versión del presupuesto',
        remove: 'Quitar esta versión',
        removeTitle: '¿Quitar la versión?',
        removeConfirm: '«{name}» desaparece con sus capítulos y sus partidas, y esto no tiene vuelta. Solo se puede si no tiene ninguna medición, ningún coste ni ninguna certificación detrás.',
        removed: 'Versión quitada ({chapters} capítulos, {items} partidas)',
        historical: 'histórica',
        newRevision: 'Nueva revisión',
        newRevisionTitle: '¿Crear una revisión del presupuesto?',
        newRevisionHelp: 'Se copia el presupuesto entero y la obra pasa a la copia: mediciones, costes y certificaciones se mueven con ella, así que la certificación siguiente sigue siendo la resta con la anterior. La versión de ahora se queda para consultar.',
        created: 'Revisión creada con {chapters} capítulos y {items} partidas.',
        compare: 'Comparar',
        compareTitle: 'Qué cambió',
        added: 'Entran', removed: 'Salen', changed: 'Cambian',
        delta: 'Mueve la venta',
        deltaHelp: 'Lo que entra menos lo que sale, más lo que cambia de precio o de cantidad.',
        historicalNote: 'Estás viendo una versión antigua: solo lectura.',
        same: { title: 'Sin diferencias', subtitle: 'Las dos versiones dicen lo mismo.' },
        what: { added: 'Entra', removed: 'Sale', changed: 'Cambia' },
        field: { description: 'Concepto', unit: 'Unidad', saleQuantity: 'Cantidad', salePrice: 'Precio' },
      },

      editor: {
        edit: 'Editar capítulos y partidas',
        done: 'Dejar de editar',
        hint: 'Se guarda solo al salir de cada casilla.',
        addChapter: 'Añadir capítulo',
        addSubchapter: 'Añadir un capítulo dentro',
        addItem: 'Añadir una partida a este capítulo',
        chapter: 'capítulo',
        item: 'partida',
        unit: 'Ud',
        quantity: 'Cantidad',
        price: 'Precio',
        amount: 'Importe',
        newChapter: 'Capítulo nuevo',
        newItem: 'Partida nueva',
        openBudget: 'Escribir el presupuesto a mano',
        budgetOpened: 'Presupuesto abierto. Empieza por un capítulo.',
        removeTitle: '¿Borrar del presupuesto?',
        removeChapterHelp: 'Solo se puede borrar un capítulo vacío. Si tiene partidas dentro, sácalas o anúlalas primero.',
        removeItemHelp: 'Solo se borra una partida que no tenga nada apuntado. En cuanto tiene medición, coste o certificación, se anula con un motivo y se queda en el histórico.',
      },
      tree: {
        fullscreen: 'Ampliar',
        exitFullscreen: 'Salir',
        fullscreenHint: 'Control de partidas a pantalla completa · Escape para salir',
        concept: 'Concepto',
        quantity: 'Medición',
        target: 'Objetivo',
        spent: 'Gastado',
        progress: 'Ejecutado',
        consumed: 'Consumido',
        deviation: 'Desvío',
        empty: {
          title: 'La obra no tiene presupuesto todavía',
          subtitle: 'Importa el BC3 que te pasó la oficina técnica y el árbol aparece aquí.',
        },
      },

      itemKind: {
        normal: 'Normal',
        lump_sum: 'Alzada',
        lump_sum_justified: 'A justificar',
      },

      origin: {
        bc3: 'Del BC3',
        variation: 'Contradictorio',
        manual: 'A mano',
      },

      nature: {
        labour: 'Mano de obra',
        plant: 'Maquinaria',
        material: 'Material',
        subcontract: 'Subcontrata',
        other: 'Otros',
      },

      quiet: {
        days: 'Hace {days} días',
        today: 'Hoy',
        never: 'Ninguno todavía',
      },

      unassigned: {
        assignTo: 'Colgar de la partida',
        pickItem: 'Busca la partida',
        assign: 'Asignar {count}',
        assigned: '{count} asientos colgados de su partida',
        help: 'Todo el coste que llega del campo entra aquí: un parte no sabe de códigos de partida y no tiene por qué saberlo. Lo que hace que esto funcione es que alguien vacíe esta bandeja, no que se le pida la partida al que está en el andamio.',
        date: 'Fecha',
        description: 'Concepto',
        nature: 'Naturaleza',
        origin: 'De dónde viene',
        amount: 'Importe',
        empty: {
          title: 'No hay nada sin asignar',
          subtitle: 'Todo el coste de esta obra está colgado de su partida.',
        },
      },

      import: {
        title: 'Importar un presupuesto',
        help: 'Un fichero BC3 (FIEBDC-3), el que exporta Presto, Arquímedes o Menfis. Sustituye el árbol de la versión, no lo mezcla.',
        file: 'El fichero',
        versionName: 'Nombre de la versión',
        versionHelp: 'Si lo dejas vacío se usa el título que trae el fichero.',
        kind: 'Qué es',
        original: 'Proyecto original',
        amendment: 'Modificado',
        charset: 'Juego de caracteres',
        charsetHelp: 'Déjalo automático. Solo se toca si los acentos salen mal.',
        charsetAuto: 'El que diga el fichero',
        done: 'Entraron {items} partidas por {total}',
      },

      report: {
        chapters: 'Capítulos',
        items: 'Partidas',
        charset: 'Caracteres',
        program: 'Programa',
        anomaly: 'Qué pasó',
        count: 'Cuántas veces',
        clean: 'El fichero entró limpio: nada que contar.',
        empty: {
          title: 'No se ha importado nada todavía',
          subtitle: 'Cuando entre un BC3, aquí queda escrito qué traía y qué le faltaba.',
        },
      },

      anomaly: {
        CHARSET_NOT_DECLARED: 'El fichero no dice en qué caracteres está; se ha supuesto CP850',
        CHARSET_UNKNOWN: 'El fichero nombra un juego de caracteres que no conocemos',
        CHARSET_IS_UTF8: 'El fichero es UTF-8 aunque diga otra cosa',
        PREAMBLE_IGNORED: 'Había texto antes del primer registro',
        RECORD_HEADER_INVALID: 'Un registro sin cabecera válida',
        RECORD_TYPE_UNSUPPORTED: 'Registros que este lector aún no interpreta',
        FORMAT_VERSION_UNKNOWN: 'Versión del formato desconocida',
        DECIMALS_NOT_DECLARED: 'Sin registro de decimales; se usan los del formato',
        ROOT_NOT_FOUND: 'El fichero no tiene raíz de obra',
        MULTIPLE_ROOTS: 'El fichero tiene más de una raíz',
        CHAPTER_EMPTY: 'Capítulos sin nada dentro',
        DECOMPOSITION_CYCLE: 'Un descompuesto que se llama a sí mismo',
        DECOMPOSITION_TOO_DEEP: 'Un descompuesto demasiado anidado',
        TREE_TOO_LARGE: 'Un árbol demasiado grande para ser un presupuesto',
        FILE_TOO_LARGE: 'Un fichero demasiado grande para leerlo',
        CONCEPT_NOT_DECLARED: 'Conceptos usados y nunca definidos',
        CONCEPT_REDECLARED: 'Conceptos definidos dos veces',
        CONCEPT_WITHOUT_PRICE: 'Conceptos sin precio',
        CONCEPT_WITHOUT_MEASUREMENT: 'Partidas sin medición',
        CONCEPT_WITHOUT_DECOMPOSITION: 'Partidas sin descompuesto',
        PRICE_DISAGREES_WITH_DECOMPOSITION: 'El precio no cuadra con su descompuesto',
        MEASUREMENT_TOTAL_MISMATCH: 'La medición no cuadra con sus líneas',
        MEASUREMENT_EXPRESSION_INVALID: 'Una fórmula de medición que no lo es',
        MEASUREMENT_EXPRESSION_FAILED: 'Una fórmula de medición que no se pudo calcular',
        MEASUREMENT_WITHOUT_TARGET: 'Mediciones que no dicen de qué son',
      },
    },
  },

  ca: {
    // El nombre de cada permiso en la pantalla de roles. Sin esto salía el
    // código crudo («construction:margins») a quien decide quién ve el dinero.
    permissions: {
      construction: {
        read: 'Veure les obres, el pressupost i l’avanç',
        write: 'Donar d’alta obres, amidar i assignar costos a les partides',
        import: 'Carregar i editar el pressupost (BC3 o a mà)',
        margins: 'Veure i gestionar els diners: objectiu, cost, marge, paquets i subcontractes',
        award: 'Adjudicar paquets des del seu comparatiu d’ofertes: signa la subcontracta o el compromís',
      },
    },
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { construction: 'Obres' },
    // Las aperturas del asistente: la clave es el id estable de
    // backend/assistant.js; el texto inglés de allí es solo el fallback.
    assistant: {
      openers: {
        construction: {
          'how-are-the-works': 'Com van les obres?',
          'what-is-deviating': "Què s'està desviant a les obres?",
          'certifications-pending': 'Quines certificacions queden pendents?',
        },
      },
    },
    audit: { actions: { construction: { cost_voided: 'Apunt de cost anul·lat' } } },
    construction: {
      // El comparativo de ofertas de un paquete (0.25.0).
      tenders: {
        title: 'Comparatiu d’ofertes',
        back: 'Als paquets',
        pageTitle: 'Comparatiu · {code} {name}',
        pageSubtitle: 'Comparatiu núm. {number} · {count} línia | Comparatiu núm. {number} · {count} línies',
        print: 'Imprimir PDF',
        template: 'Plantilla per a les empreses',
        addOffer: 'Afegir oferta',
        editOffer: 'Editar oferta',
        status: {
          open: 'Licitant',
          awarded: 'Adjudicat',
          cancelled: 'Tancat sense adjudicar',
        },
        dueOn: 'Ofertes fins al {date}',
        frozen: 'Congelat el {date}: així es va veure en adjudicar',
        target: 'Objectiu',
        offersCount: 'Ofertes',
        bestComplete: 'Millor oferta completa',
        vsTarget: 'Contra objectiu',
        linesChanged: 'El paquet ha canviat des que es va obrir el comparatiu: les seves línies ja no són les que es van demanar.',
        refreshLines: 'Actualitzar línies',
        linesRefreshed: 'Línies actualitzades. Els preus de cada empresa es conserven per partida.',
        ruleLabel: 'Les línies no ofertades es completen amb',
        rule: {
          highest: 'El preu més alt ofertat',
          target: 'L’objectiu',
        },
        ruleHelp: {
          highest: 'Homogeneïtzat: cada línia que una empresa no oferta es completa amb el preu més alt que han ofertat les altres (o amb l’objectiu si ningú no l’ha ofertada). És la regla prudent: aquesta línia l’acabaràs contractant, i no et sortirà barata.',
          target: 'Homogeneïtzat: cada línia que una empresa no oferta es completa al preu objectiu de la línia.',
        },
        awardedTo: 'Adjudicat a {supplier}',
        awardedBy: 'Per {who} el {date}',
        wasRecommended: 'era l’oferta recomanada',
        wasNotRecommended: 'la recomanada era {supplier}',
        reason: 'Motiu',
        seeSubcontract: 'Veure la subcontracta que es va donar d’alta',
        orderNote: 'Compromís apuntat al paquet. Si compres està instal·lat, la comanda queda en esborrany amb aquestes línies i aquests preus: l’envia qui compra.',
        cancelAward: 'Anul·lar l’adjudicació',
        cancelAwardHelp: 'Per quan l’empresa es fa enrere abans de començar. El comparatiu es reobre amb les seves ofertes, la subcontracta s’anul·la (si no se li ha certificat res) i el compromís es retira. L’adjudicació anul·lada queda a l’historial.',
        cancelAwardWithdraw: 'L’empresa es retira: la seva oferta deixa de competir',
        awardCancelled: 'Adjudicació anul·lada. El comparatiu torna a estar obert.',
        recommended: 'Recomanada: {supplier}',
        recommendedShort: 'Recomanada',
        basis: {
          cheapest_complete: 'És l’oferta completa i vigent més barata, comptant les línies homogeneïtzades.',
          cheapest_complete_expired: 'És la completa més barata, però totes les completes han caducat: demana a l’empresa que mantingui el preu abans d’adjudicar.',
          no_complete: 'Cap oferta no és completa: és la més barata homogeneïtzada. Demana preu del que falta abans d’adjudicar.',
        },
        belowTarget: 'Queda {amount} ({pct}) per sota de l’objectiu.',
        aboveTarget: 'Queda {amount} ({pct}) per sobre de l’objectiu.',
        runnerUp: 'La següent és {supplier}, {amount} més cara.',
        cheaperIncomplete: '{supplier} oferta {offered}, però deixa {count} línia sense preu: homogeneïtzada surt {homogenized}. | {supplier} oferta {offered}, però deixa {count} línies sense preu: homogeneïtzada surt {homogenized}.',
        cheaperExpired: '{supplier} seria més barata ({amount}) però la seva oferta ha caducat: demana-li que la renovi.',
        awardRecommended: 'Adjudicar a la recomanada',
        emptyTitle: 'Encara sense ofertes',
        emptyHelp: 'Descarrega la plantilla, envia-la a tres o quatre empreses i posa aquí els seus preus: teclejats o llegint el seu Excel.',
        offerStatus: {
          received: 'Rebuda',
          awarded: 'Adjudicada',
          not_awarded: 'No adjudicada',
          withdrawn: 'Retirada',
        },
        expired: 'Caducada',
        missingCount: '{count} línia sense preu | {count} línies sense preu',
        offered: 'Ofertat',
        homogenized: 'Homogeneïtzat',
        bestLines: 'Línies amb el millor preu',
        reference: 'Núm. d’oferta',
        validUntil: 'Vàlida fins a',
        executionDays: 'Termini (dies)',
        days: '{count} dia | {count} dies',
        paymentTerms: 'Forma de pagament',
        paymentTermsPlaceholder: 'Ex.: 60 dies data factura',
        retentionPct: 'Retenció (%)',
        discountPct: 'Descompte global (%)',
        discountHelp: 'Sobre el que oferta, no sobre les línies que es completen.',
        discountShort: 'dte. {pct} %',
        totalDeclared: 'Total que escriu l’empresa',
        totalDeclaredHelp: 'El de la seva oferta. Si no quadra amb els seus preus, s’avisa.',
        declaredMismatch: 'La seva oferta diu {declared} i els seus preus sumen {computed}: revisa què s’ha teclejat malament.',
        strongCount: '{count} preu es desvia molt de la mitjana: revisa unitat i amidament. | {count} preus es desvien molt de la mitjana: revisa unitats i amidaments.',
        exclusions: 'No inclou',
        exclusionsHelp: 'El que l’oferta deixa fora. Una per línia, amb Intro.',
        exclusionsPlaceholder: 'Ex.: reposició de paviment',
        notes: 'Notes',
        documents: 'Sense papers | 1 paper | {count} papers',
        documentsTitle: 'Papers de l’oferta de {supplier}',
        withdrawOffer: 'Retirar',
        restoreOffer: 'Tornar-la a tenir en compte',
        removeOffer: 'Treure',
        withdrawHelp: 'L’empresa es fa enrere. L’oferta es queda al comparatiu, però deixa de competir i de marcar millors preus.',
        restoreHelp: 'L’oferta torna a competir amb les altres.',
        removeOfferTitle: 'Treure l’oferta?',
        removeOfferHelp: 'S’esborra l’oferta de {supplier} amb els seus preus i els seus papers. Si l’empresa s’ha fet enrere, millor «Retirar»: així consta.',
        award: 'Adjudicar',
        awardTitle: 'Adjudicar el paquet',
        awardLead: 'Adjudicar a {supplier} per {amount}.',
        awardSubcontractHelp: 'Es dona d’alta la subcontracta enllaçada a aquest paquet, amb l’import de l’oferta, un {retention} % de retenció i {days} dies de termini. El seu abast es llegeix del paquet.',
        awardOrderHelp: 'S’apunta al paquet un compromís de {amount}, i compres deixa la comanda en esborrany amb aquestes línies. Cada comanda que es confirmi a aquesta empresa en aquest paquet descompta del compromís.',
        awardIncomplete: 'Aquesta oferta deixa {count} línia sense preu, que val {amount} a l’objectiu: caldrà contractar-la a part. | Aquesta oferta deixa {count} línies sense preu, que valen {amount} a l’objectiu: caldrà contractar-les a part.',
        signedLabel: 'Signatura',
        signedHelp: 'Deixar-la ja signada (compromet el seu import des d’avui)',
        reasonRequired: 'Motiu (obligatori: la recomanada és {supplier})',
        reasonOptional: 'Motiu (opcional)',
        awarded: 'Paquet adjudicat. El comparatiu queda congelat.',
        tableTitle: 'Taula comparativa',
        viewLabel: 'Veure',
        view: {
          table: 'Taula',
          lines: 'Per línia',
          offers: 'Per oferta',
        },
        line: 'Línia',
        unitPrice: 'Preu',
        amount: 'Import',
        pastPrices: 'Preus d’altres obres',
        notOffered: 'No ofertada',
        filledWith: 'es completa amb {amount}',
        lastShort: 'últ. {price}',
        lastLong: 'L’última vegada va ofertar {price} ({work}, {date})',
        notOfferedLines: 'Línies no ofertades',
        targetShort: 'objectiu {price} · {amount}',
        best: 'Millor',
        pickOffer: 'Oferta',
        historyTitle: 'Historial del comparatiu',
        cancelTender: 'Tancar sense adjudicar',
        cancelTenderHelp: 'El paquet torna a «Obert». Els preus es queden a l’històric: van ser ofertes de veritat.',
        offerDate: 'Data de l’oferta',
        pricesTitle: 'Preus per línia',
        pricesHelp: 'En blanc és «no l’oferta», i així es veu a la taula. Un 0 és un preu: la línia va inclosa en una altra.',
        importSheet: 'Llegir el seu Excel',
        reading: 'Llegint…',
        sheetRead: '{file} · full «{sheet}»: {priced} de {lines} línies amb preu. Revisa i desa.',
        sheetMissing: '{count} línia no apareix al seu full: queda sense ofertar. | {count} línies no apareixen al seu full: queden sense ofertar.',
        sheetQuantity: '{code}: ha ofertat {offered} {unit} on se’n demanaven {asked}. El seu total no és comparable fins que s’aclareixi.',
        sheetUnmatched: 'Fila {row} sense casar amb cap línia: {text}',
        draftTotal: 'Ofertat: {amount} · cap línia sense preu | Ofertat: {amount} · {count} línia sense preu | Ofertat: {amount} · {count} línies sense preu',
        offerSaved: 'Oferta desada',
        history: {
          opened: 'Comparatiu obert amb {count} línies',
          edited: 'Canviada la regla o la data',
          lines_refreshed: 'Línies actualitzades des del paquet ({count})',
          cancelled: 'Tancat sense adjudicar: {comment}',
          offer_added: 'Oferta de {supplier}',
          offer_revised: 'Revisada l’oferta de {supplier}',
          offer_withdrawn: '{supplier} retira la seva oferta {comment}',
          offer_restored: 'L’oferta de {supplier} torna a competir',
          offer_removed: 'Treta l’oferta de {supplier}',
          awarded: 'Adjudicat a {supplier} per {amount} {comment}',
          award_cancelled: 'Anul·lada l’adjudicació a {supplier}: {comment}',
        },
        templateLabels: {
          code: 'Codi',
          description: 'Concepte',
          unit: 'Ut',
          quantity: 'Quantitat',
          price: 'Preu unitari',
          amount: 'Import',
          total: 'TOTAL',
        },
        pdf: {
          title: 'Comparatiu d’ofertes',
          work: 'Obra',
          package: 'Paquet',
          date: 'Data',
          code: 'Codi',
          description: 'Concepte',
          unit: 'Ut',
          quantity: 'Quantitat',
          target: 'Objectiu',
          unitPrice: 'Preu',
          amount: 'Import',
          notOffered: 'no ofertada',
          offered: 'Ofertat',
          homogenized: 'Homogeneïtzat',
          vsTarget: 'Contra objectiu',
          missing: 'Línies no ofertades',
          ruleHighest: 'Les línies no ofertades es completen amb el preu més alt ofertat per les altres empreses (o amb l’objectiu si ningú no les va ofertar).',
          ruleTarget: 'Les línies no ofertades es completen al preu objectiu.',
          recommended: 'Recomanada',
          awardedTo: 'Adjudicat a',
          reason: 'Motiu',
          best: 'Millor preu',
          frozen: 'Registre congelat de l’adjudicació',
          validUntil: 'vàlida fins a',
          expired: 'caducada',
          withdrawn: 'retirada',
          exclusions: 'No inclou',
          discount: 'Descompte',
        },
        rowOpen: 'licitant, sense ofertes | licitant, 1 oferta | licitant, {count} ofertes',
        rowAwarded: 'adjudicat a {supplier}',
        panelAwarded: 'Adjudicat a {supplier} per {amount}. El comparatiu queda com a registre.',
        panelOpen: 'Licitant, encara sense ofertes. | Licitant, amb 1 oferta. | Licitant, amb {count} ofertes.',
        panelNone: 'Demana preu a diverses empreses sobre el que reclama el paquet i compara-les amb el seu objectiu.',
        openComparison: 'Obrir el comparatiu',
        start: 'Treure a preu',
        fromTender: 'Adjudicada des d’un comparatiu d’ofertes: veure les altres ofertes i el motiu',
      },
      // La base de precios de la casa, en la ficha de la partida (0.25.0).
      priceHistory: {
        title: 'Preus ofertats abans per a aquesta unitat',
        empty: 'Encara no hi ha ofertes desades per a aquesta unitat d’obra. Cada preu que entri en un comparatiu apareixerà aquí.',
        min: 'Mínim',
        median: 'Mediana',
        max: 'Màxim',
        lastAwarded: 'Últim adjudicat',
        againstTarget: 'Preu objectiu d’aquesta partida: {target}. Mediana de l’ofertat: {median}.',
        awarded: 'Adjudicat',
        howMatched: 'Es busquen per codi de base de preus o per concepte semblant, amb la mateixa unitat i els mateixos números (diàmetre, gruix, resistència).',
      },
      item: {
        historicalNote: 'Aquesta partida és d’una versió que ja no és la vigent: es consulta, no es canvia.',
        cancelAction: 'Anul·lar partida',
        packageCommitted: "{amount} li arriben del contracte del seu paquet {packages}. Ja hi són dins. | {amount} li arriben del contracte dels seus paquets {packages}. Ja hi són dins.",
        restoreAction: 'Reobrir partida',
        cancelTitle: 'Vols anul·lar aquesta partida?',
        cancelHelp: 'El seu amidament passa a zero i deixa de comptar a l’obra. No s’esborra: es queda amb el seu historial i amb el cost que ja tingui, i es pot reobrir.',
        cancelledToast: 'Partida anul·lada',
        restoreTitle: 'Vols reobrir la partida?',
        restoreHelp: 'Torna amb els amidaments que tenia abans d’anul·lar-la.',
        restoredToast: 'Partida reoberta',
        reasonRequired: 'Motiu (obligatori)',
        reasonOptional: 'Motiu (opcional)',
        reasonPlaceholder: 'Per què, en una frase',
        lumpSum: 'Partida alçada',
        lumpSumHint: 'Es mesura sencera o res: sense executar, o executada sencera.',
        lumpNone: 'Sense executar',
        lumpWhole: 'Executada sencera',
        derivedHelp: 'Partida a justificar: no es mesura. El seu avanç surt del cost que se li imputa contra el seu objectiu.',
        pendingAuto: 'Pendent calculat pel sistema.',
        pendingManual: 'Pendent fixat a mà: {reason}',
        pendingAdjust: 'Ajustar pendent',
        targetAdjust: 'Ajustar objectiu',
        targetTitle: 'Objectiu de la partida',
        targetHelp: "El que s'espera que costi. Totes les xifres de control es jutgen contra aquest import, així que el canvi queda a l'historial amb el motiu.",
        targetPriceLabel: 'Preu objectiu per {unit}',
        targetQuantityLabel: 'Amidament objectiu ({unit})',
        targetPreview: 'Objectiu resultant: {amount}',
        targetReason: 'Motiu del canvi',
        targetReasonPlaceholder: "Ex.: preu tancat amb el proveïdor, revisió de l'estudi",
        targetSaved: 'Objectiu actualitzat.',
        pendingTitle: 'El que falta per gastar',
        pendingHelp: 'Si saps que el que falta no és el que surt del càlcul, escriu-ho aquí. La previsió al tancament es refà amb aquesta xifra.',
        pendingEmpty: 'Buit torna al càlcul automàtic. Zero vol dir que ja no falta res.',
        pendingSaved: 'Pendent desat',
        cycle: {
          statusTitle: 'Canviar l’aprovació',
          stageTitle: 'Canviar l’execució',
          moveTo: 'Passar a «{what}»',
          approveHelp: 'En aprovar-lo entra a la venda de l’obra i ja es pot certificar. No té volta enrere.',
          saved: 'Modificat actualitzat',
          status: {
            proposed: 'Tornar a proposar',
            pricing: 'Passar a valoració',
            sent: 'Enviar a la DF',
            approved: 'Aprovar',
            rejected: 'Rebutjar',
          },
        },
        title: 'Partida',
        back: "A l'arbre",
        cancelled: 'Anul·lada',
        bornLosing: 'Neix perdent',
        sale: 'Venda',
        target: 'Objectiu',
        real: 'Real',
        deviation: 'Desviació',
        forecast: 'Previsió al tancament',
        committed: 'Compromès',
        margin: 'Marge',
        excess: "S'ha construït {quantity} {unit} més del venut ({pct}). Se senyala, no es bloqueja.",
        cancelledFor: 'Anul·lada: {reason}',
        newMeasure: 'Apuntar amidament',
        newMeasureHint: 'A origen, no del mes: el construït en total fins avui.',
        quantity: 'Quantitat a origen',
        comment: 'Comentari',
        record: 'Apuntar',
        recorded: 'Amidament apuntat',
        date: 'Data',
        toDate: 'A origen',
        moved: 'Ha mogut',
        who: 'Qui',
        length: 'Llarg',
        width: 'Ample',
        height: 'Alt',
        subtotal: 'Parcial',
        code: 'Codi',
        concept: 'Concepte',
        performance: 'Rendiment',
        price: 'Preu',
        breakdownCost: 'El que costa el descompost:',
        splitHelp: 'La partida conserva el seu objectiu íntegre: el que reclamen els paquets és com a molt aquest objectiu, i mai l’hi resta. El repartiment es decideix des del paquet, al seu selector.',
        splitQuantity: 'En quantitat: {claimed} {unit} en paquets i {free} lliures, de {total}.',
        noPackages: {
          title: 'No és a cap paquet',
          subtitle: 'El seu objectiu encara no s’ha tret al mercat, així que no hi ha disponible contra el qual comparar una comanda. S’hi posa des del selector d’un paquet.',
        },
        costKind: 'Tipus',
        whatHappened: 'Què ha passat',
        detail: 'Detall',
        bc3Lines: 'Les línies del projecte',
        tabs: {
          measurements: 'Amidaments',
          breakdown: 'Descompost',
          costs: 'Cost',
          history: 'Historial',
          packages: 'Repartida en',
          prices: 'Preus anteriors',
        },
        noMeasures: {
          title: 'Sense amidar encara',
          subtitle: "Apunta a dalt el que s'ha construït, a origen.",
        },
        noBreakdown: {
          title: 'Sense descompost',
          subtitle: "El fitxer no portava els recursos d'aquesta partida.",
        },
        noCosts: {
          title: 'Sense cost encara',
          subtitle: 'Res penjat en aquesta partida.',
        },
        noHistory: {
          title: 'Sense canvis',
          subtitle: 'Ningú no ha tocat aquesta partida des que va entrar.',
        },
      },
      status2: {
        approved: 'Aprovada',
        proposed: 'Proposta',
        pricing: 'En valoració',
        sent: 'Enviada a la DF',
        rejected: 'Rebutjada',
      },
      stage: {
        not_started: 'Sense començar', in_progress: 'En execució',
        done: 'Executat', invoiced: 'Facturat', paid: 'Cobrat',
      },
      subcerts: {
        title: 'Certificació de subcontracta', back: 'Al contracte',
        openDraft: 'Obrir la que hi ha en marxa',
        listTitle: 'Certificacions', number: 'Certificació {number}',
        prepare: 'Preparar la següent',
        periodEnd: 'Fins a',
        period: 'Del període',
        periodHelp: 'El que s’ha certificat aquest mes: la diferència amb el que ja portava.',
        accumulated: 'A origen', ceiling: 'Sostre del contracte',
        ceilingHelp: 'Contractat més els extres aprovats. Passar-lo demana una addenda abans, no una explicació després.',
        charges: 'Càrrecs', penalties: 'Penalitzacions',
        kindLabel: 'Tipus', kind: { charge: 'Càrrec', penalty: 'Penalització' },
        retention: 'Retenció',
        retentionHelp: 'Sobre el que es paga, no sobre el que es certifica: si no, es reté més del que diu el contracte.',
        payable: 'A pagar', payableHelp: 'La base de la seva factura d’aquest mes.',
        consumed: '{pct} % del contracte',
        linesTitle: 'Partides certificades', addLine: 'Afegir partida',
        itemPlaceholder: 'Cercar la partida',
        before: 'Anterior', toDate: 'A origen', price: 'Preu', thisPeriod: 'Aquest mes',
        noLines: { title: 'Sense partides', subtitle: 'Afegeix el que se li certifica aquest mes. Les quantitats van a origen.' },
        none: { title: 'Sense certificacions', subtitle: 'Encara no se li ha certificat res a aquesta subcontracta.' },
        deductionsTitle: 'Càrrecs i penalitzacions',
        noDeductions: { title: 'Sense descomptes', subtitle: 'No se li està descomptant res.' },
        addCharge: 'Afegir càrrec', addPenalty: 'Afegir penalització',
        chargeHelp: 'Feina que va fer la casa i li tocava a ella: la grua que li va posar el cap d’obra, la neteja que no va fer. Es justifica amb un albarà.',
        penaltyHelp: 'El contracte aplicant-se: retard, incompliment. Es justifica amb una clàusula.',
        chargePlaceholder: 'Ex.: Grua del dia 12',
        penaltyPlaceholder: 'Ex.: Retard de 10 dies, clàusula 8.2',
        evidence: 'Justificant', evidencePlaceholder: 'Ex.: ALB-2026-77',
        appliedLabel: 'Estat', charged: 'Cobrat', pending: 'Pendent',
        nothingHere: 'Res per triar.',
        refusalsTitle: 'Això impedeix aprovar-la',
        overContract: 'Passa del contracte: {accumulated} certificat contra un sostre de {ceiling}. Cal una addenda.',
        overClient: 'A {item} se li certifiquen {toSub} {unit} i al client només {toClient}. Es pagaria feina que encara no es cobra.',
        scopeMax: 'màx. {max} {unit}',
        scopeNote: 'El màxim de cada partida surt del seu paquet, {name}: el que reclama, menys el que ja portin certificat altres subcontractes del mateix paquet.',
        overScope: 'A {item} se li certifiquen {asked} {unit} a origen i el seu paquet li dona {scope}. Es pagaria el que no és seu.',
        warningsTitle: 'Mira-ho abans d’aprovar',
        warn: {
          negative_period: 'El mes surt en negatiu ({amount}): s’està corregint a la baixa.',
          negative_payable: 'No hi ha res a pagar: els descomptes es mengen la certificació ({amount}).',
          line_went_back: 'A {item} es certifica menys que el mes passat ({amount}).',
        },
        approve: 'Aprovar',
        approveConfirm: 'S’apunta el cost al llibre de l’obra d’aquest mes i baixa el compromís del contracte. A pagar: {amount}.',
        approved: 'Certificació aprovada',
        blocked: 'No es pot aprovar',
        certifiedLocked: 'Ho posa la suma de les seves certificacions.',
        status: {
          draft: 'Esborrany', sent: 'Enviada', approved: 'Aprovada',
          rejected: 'Rebutjada', cancelled: 'Anul·lada',
        },
        what: { created: 'Alta', status: 'Estat', lines: 'Partides' },
      },
      templates: {
        title: 'Plantilles d’indirectes', manage: 'Plantilles',
        yours: 'Teves ({count})', new: 'Nova', newName: 'Plantilla nova',
        builtIn: 'Del catàleg',
        builtInHelp: 'Aquesta és del catàleg i no es toca. Copia-la i edita la còpia: així «edificació» continua sent la del catàleg i se sap en què es diferencia la teva.',
        name: 'Nom', forWhat: 'Per a què és',
        forWhatPlaceholder: 'Ex.: naus industrials de menys de 12 mesos',
        concept: 'Concepte', amount: 'Import', category: 'Categoria',
        addLine: 'Afegir concepte', noLines: 'Encara no té conceptes.',
        lineCount: '{count} concepte | {count} conceptes',
        zeroHelp: 'L’import pot anar a zero, i no és un buit sense omplir: vol dir que aquest concepte existeix en aquesta mena d’obra i aquí no costa.',
        copy: 'Copiar', copySuffix: 'còpia', copied: 'Plantilla copiada',
        apply: 'Usar en aquesta obra', saved: 'Plantilla desada',
        saveFromWork: 'Desar aquesta obra com a plantilla',
        fromWorkSuffix: 'plantilla',
        savedFromWork: 'Desada com a «{name}»',
        dropTitle: 'Esborrar la plantilla',
        dropConfirm: '«{name}» desapareix. Les obres obertes amb ella no canvien: ja tenen els seus conceptes copiats.',
        none: { title: 'Sense plantilles', subtitle: 'Comença per una del catàleg o fes-te la teva.' },
        pick: { title: 'Tria’n una', subtitle: 'A l’esquerra hi ha les teves i les del catàleg.' },
      },
      invoices: {
        title: 'Factures registrades',
        register: 'Registrar una factura', registerFrom: 'Facturar això',
        edit: 'La factura', saved: 'Desat',
        uninvoiced: 'Sense factura',
        uninvoicedHelp: 'Cost que ja hi és i el paper del qual no ha arribat.',
        uninvoicedTitle: 'El que està sense facturar',
        received: 'Rebudes', paid: 'Pagades',
        owed: 'Se li deu', owedHelp: 'Registrat i sense pagar: el que cal pagar.',
        supplier: 'Proveïdor', number: 'Núm. de factura',
        numberPlaceholder: 'Ex.: F-2026-0451',
        issueDate: 'Data', dueDate: 'Venç', state: 'Estat',
        dueHelp: 'Sense data no s’avisa de res: no és ni al dia ni vençuda.',
        amount: 'Base', amountHelp: 'El que s’imputa a l’obra. L’IVA no és cost.',
        tax: 'IVA', notes: 'Notes',
        against: 'Contra quin cost va',
        nothingToPick: 'Aquest proveïdor no té res pendent de facturar.',
        noSupplier: 'Sense proveïdor',
        overdue: 'Vençuda',
        markPaid: 'Marcar pagada', markUnpaid: 'Marcar no pagada',
        dropTitle: 'Treure la factura?',
        dropConfirm: 'La {number} desapareix i el seu cost torna a estar sense facturar.',
        status: { received: 'Rebuda', paid: 'Pagada' },
        alerts: {
          overdue: '{count} factura vençuda sense pagar | {count} factures vençudes sense pagar',
          due_soon: '{count} factura venç aquesta setmana | {count} factures vencen aquesta setmana',
          uninvoiced: 'Cost rebut del qual encara no ha arribat la factura',
        },
        none: { title: 'Sense factures', subtitle: 'Aquí van les del material i les de les subcontractes.' },
        allInvoiced: { title: 'Tot facturat', subtitle: 'No hi ha cost rebut esperant el seu paper.' },
      },
      subcontracts: {
        title: 'Subcontracta', back: "A l'obra",
        new: 'Nova subcontracta', create: 'Donar d\'alta',
        newHelp: "Només el contracte. Les partides que cobreix es lliguen després, a la seva fitxa.",
        companyPlaceholder: 'Cercar el proveïdor',
        code: 'Contracte', codePlaceholder: 'Ex.: SUB-2026-03',
        scope: 'Abast', scopePlaceholder: 'Quina feina cobreix',
        start: 'Inici', end: 'Fi prevista',
        retentionPct: 'Retenció',
        paid: 'Pagat', saved: 'Desat',
        targetHelp: "L'objectiu intern de les partides que cobreix.",
        extrasTitle: 'Extres del contracte',
        late: 'Fora de termini',
        noPackage: 'Sense paquet: no descompta de cap disponible',
        sources: {
          executedScope: 'el que l’obra porta amidat en la seva part de cada partida, al preu del seu contracte.',
          executedCertified: 'sense paquet no se sap quina part de l’amidament és seva: es mostra el certificat.',
          certified: 'la suma de les seves certificacions aprovades: el de cada mes més els seus extres.',
          invoiced: 'les factures rebudes contra aquestes certificacions, pel seu net (els càrrecs i les penalitzacions no es facturen).',
          paid: 'el d’aquestes factures marcades com a pagades, sense la retenció que va aplicar cada certificació, que queda a part.',
        },
        withExtras: 'inclou {amount} d’extres',
        tabs: { summary: 'Resum', money: 'Extres i descomptes', contract: 'El contracte' },
        documents: 'Papers del contracte',
        addDocument: 'Pujar un document',
        uploading: 'Pujant…', uploaded: 'Document pujat',
        noDocuments: 'Sense papers. Puja aquí el contracte signat, les addendes i els certificats.',
        dropDocument: 'Treure el document',
        dropDocumentConfirm: '«{name}» deixa d’estar al contracte.',
        addExtra: 'Afegir extra',
        approveExtra: 'Aprovar',
        extraHelp: "Amb descripció obligatòria. Neix sense aprovar: proposar no és acordar.",
        extraWhat: 'Concepte', extraPlaceholder: 'Ex.: Reforços no previstos',
        approved: 'Aprovat', notApproved: 'Sense aprovar', approvedAlready: 'Ja acordat?',
        noExtras: { title: 'Sense extres', subtitle: 'El contracte va pel que es va signar.' },
        covers: 'Cobreix {count} partida | Cobreix {count} partides',
        scopeAlertsTitle: 'Certificat per sobre del seu abast',
        scopeOver: '{item}: porta {certified} {unit} certificats a origen i el seu paquet li dona {scope}.',
        scopeFromPackage: 'Cobreix el que reclama el seu paquet, {name}: les partides, les quantitats i el topall del que se li pot certificar. Es llegeix del paquet, així que si el repartiment canvia, això també.',
        scopePackageEmpty: 'El seu paquet, {name}, encara no agrupa cap partida: cobreix les seves i no té topall d’abast.',
        scopeQuantity: 'La seva part',
        certifiedToDate: 'Certificat / màx.',
        certifiedOf: '{certified} de {max}',
        packageHelp: 'Li dona el seu abast: les partides i quantitats que reclama el paquet, i el topall del que se li pot certificar.',
        noPackageOption: 'Sense paquet',
        packageLabel: 'Paquet que adjudica',
        noPackageOption: 'Sense paquet (s’enllaça després)',
        packagePickHelp: 'El seu abast es llegirà del paquet: les partides i quantitats que reclama, i el límit del que se li pot certificar.',
        packagePicked: 'Heretarà {count} partida amb un objectiu de {amount}. | Heretarà {count} partides amb un objectiu de {amount}.',
        noItems: 'Sense partides lligades: no hi ha contra què comparar.',
        openItem: 'Obrir la partida',
        to_signed: 'Signar', to_in_progress: 'Va començar', to_finished: 'Va acabar', to_closed: 'Liquidar',
        what: {
          created: 'Alta', status: 'Estat', extra: 'Extra', package: 'Paquet',
          extra_approved: 'Extra aprovat', extra_unapproved: 'Extra sense aprovar',
          charge: 'Càrrec', penalty: 'Penalització',
          charge_removed: 'Càrrec retirat', penalty_removed: 'Penalització retirada',
        },
        company: 'Empresa', contracted: 'Contractat', extras: 'Extres',
        variance: 'Contra objectiu',
        varianceHelp: "La previsió del contracte contra l'objectiu intern de les partides que cobreix. Negatiu és guanyar.",
        outstanding: 'Se li deu',
        outstandingHelp: 'El que toca pagar per les seves certificacions (sense càrrecs, penalitzacions ni retenció) menys el pagat.',
        retained: 'Retingut',
        retainedHelp: 'Garantia que li retenim i li tornem en acabar.',
        pendingExtras: '{amount} sense aprovar',
        status: {
          draft: 'Esborrany', signed: 'Signada', in_progress: 'Treballant',
          finished: 'Acabada', closed: 'Liquidada', cancelled: 'Anul·lada',
        },
        empty: { title: 'Sense subcontractes', subtitle: "Tot el d'aquesta obra ho fa la casa." },
      },
      certifications: {
        number: 'Núm.', period: 'Període',
        produced: 'Executat', certified: 'Certificat', invoiced: 'Facturat', paid: 'Cobrat',
        toCertify: 'Pendent de certificar',
        toCertifyHelp: "Obra executada que encara no s'ha passat al client.",
        toInvoice: 'Pendent de facturar', toCollect: 'Pendent de cobrar',
        retained: 'Retingut en garantia',
        retainedHelp: "Diners de l'empresa que el client té i retorna al final de l'obra.",
        current: "D'aquest mes", accumulated: "A origen", payable: 'A cobrar',
        prepare: 'Preparar la del mes',
        previewTitle: "La certificació d'aquest mes",
        previewHelp: "Encara no existeix: això és el que sortiria amb el que l'obra porta executat a origen. En crear-la neix en esborrany, perquè algú la revisi abans d'emetre-la.",
        prepareHelp: "S'omple amb el que l'obra porta executat, a origen. Neix en esborrany.",
        prepared: 'Certificació preparada',
        to_issued: 'Emetre', to_invoiced: 'Facturar', to_paid: 'Cobrada',
        status: { draft: 'Esborrany', issued: 'Emesa', invoiced: 'Facturada', paid: 'Cobrada' },
        empty: { title: 'Sense certificacions', subtitle: "Prepara la primera amb el que l'obra porta executat." },
      },
      clientCert: {
        title: 'Certificació', number: 'Certificació núm. {number}', back: 'Obra',
        until: 'fins al {date}', unsaved: 'Canvis sense desar',
        accumulated: 'A origen', previous: 'Certificat anterior', current: 'Aquest mes',
        retention: 'Retenció ({pct} %)', payable: 'Líquid a cobrar',
        negativeHelp: "Aquest mes surt en negatiu: es va certificar de més en una anterior i ara es retorna. És la correcció a origen, no un error.",
        previousOf: "Es resta contra la núm. {number}, l'última emesa abans d'aquesta.",
        first: 'És la primera: no hi ha res anterior per restar.',
        reviewNote: "S'ha omplert amb l'executat a origen. Baixa les línies que la direcció facultativa no acceptarà i desa; no es pot passar de l'executat. Un cop emesa ja no es toca: el que calgui corregir anirà a la següent.",
        readonly: {
          issued: 'Emesa: ja no es toca. El que calgui corregir es corregeix a la següent, a origen.',
          historical: "Feta amb una versió del pressupost que ja no és la vigent: es llegeix, però no es toca.",
          permission: 'Només lectura: no tens permís per canviar certificacions.',
        },
        linesTitle: 'Línies', linesCount: '{count} partida | {count} partides',
        reset: "Tornar a l'executat",
        resetHelp: "Posa cada línia en el que l'obra porta executat a origen. No es desa fins que premis «Desar línies».",
        overMax: "{count} línia passa de l'executat: baixa-la per poder desar | {count} línies passen de l'executat: baixa-les per poder desar",
        lowered: "{count} línia baixada respecte a l'executat | {count} línies baixades respecte a l'executat",
        empty: { title: 'Sense línies', subtitle: 'No hi ha res executat per certificar en aquesta.' },
        col: {
          item: 'Partida', unit: 'Ut', contracted: 'Projecte', executed: 'Executat a origen',
          previous: 'Cert. anterior', toDate: "A origen d'aquesta", current: 'Aquest mes', toDateAmount: 'Import a origen',
        },
        blocked: {
          cancelled: 'Anul·lada: es queda amb el que ja portava certificat.',
          unapproved_variation: 'Contradictori sense aprovar: es queda amb el que ja portava certificat.',
        },
        max: 'màx. {max} {unit}',
        save: 'Desar línies', saved: 'Línies desades',
        to_issued: 'Emetre', to_invoiced: 'Marcar facturada', to_paid: 'Marcar cobrada',
        invoiceTitle: 'Marcar com a facturada',
        invoiceMessage: 'La núm. {number} passa a facturada per {amount}, el certificat aquest mes.',
        invoiceRef: 'Número de factura',
        invoiceRefHelp: "El de la factura que ha sortit. És el que lliga la certificació amb el paper.",
        confirm: {
          issued: {
            title: 'Emetre la certificació núm. {number}',
            message: "S'emet per {amount} aquest mes. A partir d'aquí no es pot tocar: el que calgui corregir anirà a la següent, a origen.",
          },
          paid: {
            title: 'Marcar com a cobrada la núm. {number}',
            message: "Confirma que han entrat {amount} al banc per aquesta certificació.",
          },
        },
        done: { issued: 'Certificació emesa', invoiced: 'Marcada com a facturada', paid: 'Marcada com a cobrada' },
        issuedAt: 'Emesa el {date}', invoicedAt: 'Facturada el {date} · {ref}', paidAt: 'Cobrada el {date}',
        download: 'Descarregar PDF',
        downloadHelp: 'La certificació impresa, per a la direcció facultativa i la propietat. Un esborrany surt amb la marca «ESBORRANY».',
        measured: {
          note: '{count} partida s’ha amidat després de preparar l’esborrany i no té línia. | {count} partides s’han amidat després de preparar l’esborrany i no tenen línia.',
          add: 'Afegir el que s’ha amidat des de llavors',
          title: 'El que s’ha amidat des que es va preparar',
          help: 'Partides amb execució que no estaven amidades quan es va preparar l’esborrany. Entren amb l’executat a origen i després es poden baixar com qualsevol altra línia. En afegir-les es desen les línies, amb els canvis que tinguis.',
          confirm: 'Afegir {count} partida | Afegir {count} partides',
          added: '{count} partida afegida | {count} partides afegides',
        },
        pdf: {
          period: 'Període', date: 'Data', work: 'Obra', code: 'Codi',
          price: 'Preu', currentAmount: 'Import del mes', chapterTotal: 'Total capítol',
          noVat: 'Imports sense IVA.', notes: 'Observacions',
          signContractor: 'L’empresa constructora', signDirection: 'La direcció facultativa',
          signAgreed: 'Conforme', signName: 'Nom i signatura', signDate: 'Data',
        },
      },
      variations: {
        new: 'Nou modificat',
        newHelp: 'Neix proposat i no suma a la venda fins que s’aprova. L’aprovació i l’execució es porten des de la seva fitxa.',
        create: 'Proposar',
        created: 'Modificat proposat',
        chapter: 'Capítol',
        noChapter: 'Sense capítol',
        kind: 'Tipus de partida',
        customerRef: 'Referència del client',
        customerRefHint: 'L’ordre o el número amb què la propietat o la DF el coneix.',
        reason: 'Motiu',
        reasonPlaceholder: 'Per què apareix aquesta feina',
        what: 'Modificat', approval: 'Aprovació', stage: 'Feina',
        approved: 'Aprovat i en venda',
        approvedHelp: "Extres que el client ha acceptat. Ja sumen a la venda de l'obra.",
        open: 'Demanat i sense resposta',
        openHelp: 'Proposat o enviat a la direcció facultativa. Encara no suma a la venda.',
        atRisk: 'Executat sense aprovar',
        atRiskHelp: 'Feina feta que ja ha costat diners i que ningú ha aprovat.',
        atRiskNote: '{count} extra executat sense aprovar, amb {cost} de cost ja dins de la previsió. | {count} extres executats sense aprovar, amb {cost} de cost ja dins de la previsió.',
        empty: { title: 'Sense modificats', subtitle: 'Tot el que es construeix era al projecte.' },
      },
      costKind: {
        incurred: 'Gastat',
        committed: 'Compromès',
      },
      lineType: {
        measurement: 'Amidament',
        subtotal_partial: 'Subtotal',
        subtotal_running: 'Acumulat',
        expression: 'Fórmula',
      },
      historyWhat: {
        created: 'Alta',
        progress: 'Amidament',
        target: 'Objectiu',
        status: 'Estat',
        cancelled: 'Anul·lada',
        restored: 'Reoberta',
        edited: 'Editada',
        stage: 'Etapa',
        copied: 'Copiada a la revisió',
        assigned: 'A un paquet',
        unassigned: "Fora d'un paquet",
        packages: 'Repartiment en paquets',
        pending: 'Pendent fixat a mà',
      },
      historyField: {
        code: 'codi',
        description: 'concepte',
        label: 'resum',
        longText: 'text llarg',
        unit: 'unitat',
        kind: 'tipus',
        saleQuantity: 'amidament de venda',
        salePrice: 'preu de venda',
        targetQuantity: 'amidament objectiu',
        targetPrice: 'preu objectiu',
        pendingOverride: 'pendent',
      },
      historyExtra: {
        automatic: 'automàtic',
        packagesCount: '{count} paquet | {count} paquets',
        copiedDetail: 'Nova revisió del pressupost',
      },

      title: 'Obres',

      related: { forCustomer: 'Obres' },

      help: {
        title: 'Obres',
        subtitle: 'Del pressupost a la previsió de tancament: objectiu, paquets de contractació, subcontractes i certificacions',
      },

      scope: { only: 'Només les obres de {name}', showAll: 'Veure-les totes', oneCustomer: 'aquest client' },
      subtitle: 'El pressupost de cada obra per capítols i partides, i el que porta gastat',
      detailTitle: 'Obra',
      search: { subtitle: 'Obra' },
      dashboard: {
        works: 'Obres en marxa',
        worksHelp: "Quantes obres hi ha obertes avui, sense comptar les tancades ni les anul·lades.",
        production: 'Producció a origen',
        productionHelp: "El que s'ha construït fins avui a les obres en marxa, valorat a preu de venda. Estigui certificat o no.",
        backlog: 'Cartera per executar',
        backlogHelp: "El que s'ha venut i encara està per construir a les obres en marxa.",
        toCertify: 'Pendent de certificar',
        toCertifyHelp: "El que s'ha produït i encara no ha entrat en cap certificació al client.",
        receivable: 'Pendent de cobrament',
        receivableHelp: 'Certificacions emeses o facturades i encara sense cobrar, amb la retenció ja descomptada.',
        margin: 'Marge previst',
        marginHelp: "El marge que s'espera al tancament de les obres en marxa, sobre la venda i ponderat per import.",
        deviation: 'Desviació sobre objectiu',
        deviationHelp: "Quant s'ha gastat per sobre de l'objectiu repartit, sumant totes les obres en marxa.",
        unassigned: 'Cost sense assignar',
        unassignedHelp: "Diners imputats a una obra i encara sense partida. Mentre no sigui zero, el marge està incomplet.",
        supplierInvoices: 'Factures de proveïdor per pagar',
        supplierInvoicesHelp: "El que s'ha rebut i no s'ha pagat als proveïdors d'obra, amb IVA.",
        worksList: 'Obres, una a una',
        worksListHelp: 'Les obres en marxa ordenades per import, amb el seu avanç i el seu marge.',
        deviated: 'Obres desviades',
        deviatedHelp: "Les obres que van per sobre de l'objectiu, de major a menor desviació.",
      },
      actions: { new: 'Nova obra', open: "Obrir l'obra", import: 'Importar BC3', more: 'Accions' },
      status: { draft: 'Esborrany', live: 'En execució', closed: 'Tancada', cancelled: 'Anul·lada' },
      fields: {
        code: "Codi d'obra",
        manager: "Cap d'obra",
        address: "Adreça", city: "Municipi", postalCode: "Codi postal",
        labourRate: "Preu de l'hora",
        labourRateHelp: "El que costa una hora de colla en aquesta obra. Amb això, les hores dels partes arriben valorades; sense això arriben comptades i sense import.",
        name: "Nom de l'obra", customerName: 'Client',
        fileNumber: 'Expedient', authority: 'Organisme',
        tenderAmount: 'Import de licitació', awardAmount: "Import d'adjudicació",
        lowCoefficient: 'Baixa', lastData: 'Última dada',
      },
      header: { collapse: 'Contraure', expand: 'Veure les xifres' },
      figures: {
        sale: 'Venda', saleHelp: "El que se li factura al client per aquesta obra, al preu del contracte.",
        target: 'Objectiu', targetHelp: "El que l'empresa es va proposar gastar a executar-la.",
        spent: 'Gastat',
        real: 'Real', realHelp: 'Cost que JA s\'ha produït: albarans rebuts, hores posades, maquinària feta servir.',
        committed: 'Compromès', committedHelp: 'Comandes i contractes ja aprovats que encara no són cost, però que ja no es poden evitar.',
        pending: 'Pendent', pendingHelp: "El que s'estima que falta per gastar, descomptat el que ja està compromès.",
        forecast: 'Previsió', forecastHelp: 'En quant acaba: real + compromès + pendent. Cap euro es compta dues vegades.',
        forecastMargin: 'Marge previst', forecastMarginHelp: 'El que quedarà al final: venda menys previsió.',
        margin: 'Marge',
        notInvoiced: 'Sense factura', notInvoicedHelp: 'Part del real ja executat i encara sense factura. Ja compta dins de Real.',
        unassigned: 'Sense assignar', unassignedHelp: "Diners gastats en aquesta obra que encara no pengen de cap partida.",
        progress: 'Avanç físic', consumed: 'Cost consumit',
        costAhead: "El cost va per davant de l'execució: s'està gastant més ràpid del que es construeix.",
      },

      alerts: {
        title: 'Què mirar avui', more: 'Veure\'n {count} més',
        work_over_target: "l'obra acabarà per sobre de l'objectiu",
        work_negative_margin: "l'obra costarà més del que es va vendre",
        work_cost_ahead_of_progress: "el cost corre per davant de l'execució",
        unassigned_significant: 'hi ha molt cost sense penjar de cap partida',
        unassigned_stale: 'hi ha costos esperant des de fa setmanes sense assignar',
        item_over_target: "acabarà per sobre del seu objectiu",
        item_negative_margin: 'es vendrà per sota del que costarà',
        cost_ahead_of_progress: 'gasta més ràpid del que construeix',
        item_near_target: "gairebé ha consumit el seu objectiu i queda feina",
        committed_without_progress: 'tot demanat i sense començar',
        item_born_losing: 'el seu descompost costa més que el seu preu objectiu',
        variation_executed_unapproved: 'executat i sense aprovar: s\'està regalant',
        variation_awaiting_approval: 'esperant resposta del client',
      },

      closing: {
        against: "Contra un objectiu de {target}:",
        marginInitial: 'Marge del contracte', marginInitialHelp: 'El que es va signar: venda menys objectiu.',
        marginCurrent: 'Marge previst',
        marginChange: "El que s'ha perdut", marginChangeHelp: 'La diferència entre el marge signat i el previst.',
        manual: '{count} de {items} partides porten el pendent posat a mà.',
        ranking: 'On estem perdent diners',
        unexplained: '{amount} sense explicar', noCauses: 'ningú ha dit per què',
      },

      causes: {
        title: 'Per què es desvia',
        edit: 'Classificar',
        help: "Reparteix la desviació per causes. El que no encaixi es queda sense explicar, i es mostra com a tal.",
        what: 'Causa', amount: 'Import', note: 'Detall',
        notePlaceholder: 'Obligatori a «Altres»',
        add: 'Afegir causa',
        sum: 'Explicat {explained} de {variance}. Sense explicar: {left}.',
        saved: 'Causes desades',
        unexplained: 'Sense explicar',
        none: 'Encara ningú ha dit per què es desvia.',
        chargeable: "{amount} es pot passar al client.",
        supplier_price: 'Preu de proveïdor', more_quantity: 'Major amidament',
        extra_hours: 'Hores addicionals', budget_error: 'Error de pressupost',
        scope_change: "Canvi d'abast", customer_variation: 'Modificat del client',
        delay: 'Retard', rework: 'Refer feina', material_waste: 'Malbaratament de material',
        subcontract_price: 'Subcontracta més cara', extra_plant: 'Maquinària addicional',
        other: 'Altres',
      },
      sort: { label: 'Ordenar per', variance: 'Major desviació', margin: 'Menor marge', unassigned: 'Més sense assignar', sale: 'Major import', name: 'Nom' },
      stats: { works: 'Obres' },
      list: { work: 'Obra' },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      filters: { searchPlaceholder: 'Nom, expedient, client…', status: 'Estat', allStatuses: 'Totes' },
      empty: {
        title: 'Encara no hi ha obres',
        subtitle: 'Una obra és un projecte amb pressupost per partides. Obre la primera i posa-hi el seu BC3.',
      },
      new: {
        codePlaceholder: "OBR-2026-014",
        customerPlaceholder: "Busca el client",
        managerPlaceholder: "Busca la persona",
        title: 'Obrir una obra',
        help: "L'obra és la fitxa principal: el seu codi, el seu client i el seu cap d'obra. El pressupost per partides se li posa després, amb el BC3.",
      },
      nav: {
        works: 'Obres',
        contracting: 'Contractació',
        subcontracts: 'Subcontractes',
        certifications: 'Certificacions',
        prices: "Preus d'obra",
      },
      sections: {
        label: "Apartats de l'obra",
        budget: "Pressupost d'obra",
        contracting: 'Contractació',
        production: 'Producció i cobrament',
        control: 'Control',
        daily: 'Dia a dia',
      },
      revisions: {
        name: 'Versió',
        created: 'Creada',
        current: 'Vigent',
        historical: 'Històrica',
        viewing: "És la que estàs veient a l'arbre",
        view: "Veure a l'arbre",
        compare: 'Comparar amb la vigent',
        noReport: "Aquesta versió no va venir d'un BC3: es va escriure a mà o és còpia d'una altra.",
      },
      across: {
        work: 'Obra',
        allWorks: 'Totes les obres',
        allBuyers: 'Tothom',
        attention: 'El que cal atendre',
        warnings: 'Avisos',
        contracting: {
          title: 'Contractació',
          subtitle: 'Els paquets i comparatius de totes les obres, contra el seu objectiu',
          count: 'Paquets',
          emptyTitle: 'Cap paquet amb aquests filtres',
          emptyHelp: 'Els paquets es creen a cada obra, a «Contractació». Aquí surten els de totes.',
          alerts: {
            offers_expired: '{name} ({work}): {count} oferta caducada; la més barata, {supplier} | {name} ({work}): {count} ofertes caducades; la més barata, {supplier}',
            offers_expiring: '{name} ({work}): {count} oferta caduca aquesta setmana; la més barata, {supplier} | {name} ({work}): {count} ofertes caduquen aquesta setmana; la més barata, {supplier}',
            awarded_over_target: "{name} ({work}) es va adjudicar a {supplier} un {pct} % per sobre de l'objectiu",
            tendering_no_offers: '{name} ({work}) és en contractació i no té cap oferta',
          },
        },
        subcontracts: {
          title: 'Subcontractes',
          subtitle: "Tots els contractes de totes les obres: el contractat, el certificat i el que se'ls deu",
          count: 'Contractes',
          search: 'Empresa, codi o abast…',
          pending: 'Certificacions per aprovar',
          pendingHelp: 'Certificacions de subcontracta en esborrany o enviades que encara ningú no ha aprovat.',
          pendingShort: 'Per aprovar',
          pendingCount: '{count} per aprovar | {count} per aprovar',
          warnings: {
            late: 'Fora de termini',
            over_scope: 'Certificat de més',
          },
        },
        certifications: {
          title: 'Certificacions',
          subtitle: 'Les certificacions al client de totes les obres: què revisar, què facturar i què cobrar',
          count: 'Certificacions',
          missingTitle: 'Obres sense la certificació d’aquest mes',
          missingRow: '{work} · {name}: producció sense certificar',
          bucket: 'Què toca',
          allBuckets: 'Totes',
          bucketCount: '{count} certificació | {count} certificacions',
          nextLabel: 'Pas següent',
          buckets: {
            review: 'Esborranys per revisar',
            to_invoice: 'Emeses per facturar',
            to_collect: 'Facturades per cobrar',
            done: 'Cobrades',
          },
          next: {
            review: 'Revisar i emetre',
            to_invoice: 'Facturar',
            to_collect: 'Cobrar',
            done: 'Cobrada',
          },
        },
        prices: {
          title: "Preus d'obra",
          subtitle: "El que les empreses han ofert i el que s'ha contractat, a totes les obres",
          count: 'Preus',
          search: 'Concepte o codi…',
          unitPlaceholder: 'm3, m2, kg…',
          supplier: 'Empresa',
          which: 'Quins preus',
          allPrices: 'Oferts i contractats',
          onlyAwarded: 'Només contractats',
          mixedUnits: "Hi ha preus de diverses unitats. Escriu una unitat per veure el preu mitjà, el mínim i l'últim.",
          mean: 'Preu mitjà',
          min: 'Mínim',
          max: 'Màxim',
          last: 'Últim',
          lastAwarded: 'Últim contractat',
          fromWhere: '{supplier} · {work} · {date}',
          awarded: 'Contractat',
          origin: 'Obra i comparatiu',
          date: 'Data',
          emptyTitle: 'Cap preu amb aquests filtres',
          emptyHelp: "Cada oferta que s'introdueix en un comparatiu deixa aquí els seus preus, línia a línia.",
          onlySupplier: 'Només els preus de {name}',
          oneSupplier: 'aquest proveïdor',
        },
      },
      supplierPanel: {
        title: 'A les obres',
        awards: 'Adjudicacions',
        offers: 'Ofertes en comparatius',
        subcontracts: 'Subcontractes',
        prices: 'Històric de preus',
        pricesRow: "Els seus preus als comparatius d'obra",
      },
      voidCost: {
        action: 'Anul·lar',
        title: 'Anul·lar aquest cost',
        help: "«{description}», {amount}. Desapareix de l'obra i de la seva partida; el motiu queda al registre d'activitat.",
        reason: 'Motiu',
        reasonPlaceholder: 'Ex.: teclejat dues vegades',
        confirm: 'Anul·lar el cost',
        done: 'Cost anul·lat.',
      },
      tabs: {
        tree: 'Partides',
        packages: 'Paquets',
        variations: 'Modificats i contradictoris',
        versions: 'Revisions',
        subcontracts: 'Subcontractes',
        certifications: 'Certificacions',
        invoices: 'Factures rebudes',
        closing: 'Previsió de tancament',
        unassigned: 'Costos sense imputar',
        indirect: 'Costos indirectes',
        report: 'La importació',
      },
      packages: {
        title: 'Paquets de contractació',
        newPackage: 'Nou paquet',
        newHelp: "Un paquet és la unitat contra la qual es COMPRA: agrupa partides per demanar preu a un proveïdor. Es crea buit i les partides se li assignen després.",
        createPackage: 'Crear paquet',
        newName: 'Paquet nou',
        buyer: 'Responsable de compra',
        buyerPlaceholder: 'Cerca algú de la casa',
        empty: {
          title: 'Sense paquets de contractació',
          subtitle: "Un paquet és el que es contracta de cop. Sense paquets no hi ha disponible, i sense disponible ningú sap si una comanda cap en el pressupostat.",
        },
        type: {
          supply: 'Subministrament',
          subcontract: 'Subcontracta',
          hire: 'Lloguer',
          service: 'Servei',
          own_means: 'Mitjans propis',
        },
        columns: {
          package: 'Paquet',
          type: 'Tipus',
          target: 'Objectiu',
          available: 'Disponible',
        },
        availableHelp: "Objectiu menys el compromès i el gastat. És el que queda per comprar.",
        itemCount: '{count} partida | {count} partides',
        itemsTitle: 'Què agrupa',
        noItems: "Encara no agrupa cap partida, així que no té objectiu contra el qual comprar.",
        addItems: 'Afegir partides',
        ctc: 'El que falta per gastar',
        ctcValue: 'Fixat a mà',
        ctcHint: 'Si el deixes buit, el motor proposa {amount}.',
        ctcReason: 'Per què',
        ctcReasonPlaceholder: 'Ex.: la partida està acabada, no gastarà més',
        ctcManual: 'La previsió d’aquest paquet fa servir la teva xifra, no la calculada.',
        calculatedHelp: 'Suma de les {count} partides que agrupa: {amount}.',
        manualAdjust: 'Ajust manual',
        manualAdjustHelp: "El que el cap d'obra sap i l'arbre no diu. Amb motiu, sempre.",
        reason: 'Motiu de l’ajust',
        removeTitle: 'Esborrar aquest paquet?',
        removeHelp: "Només s'esborra el que no té cap compra. Quan té comandes o despesa es TANCA.",
        alerts: {
          over_committed: "A {name} s'ha compromès {amount} més del que hi havia.",
          forecast_over: '{name} preveu tancar {amount} per sobre de l’objectiu.',
          suspicious_ctc: '{name} no té res compromès ni res per gastar, i l’obra no està feta.',
          no_target: "S'està gastant a {name} sense objectiu al darrere.",
          own_means_committed: '{name} és de mitjans propis i té {amount} compromesos: o la comanda va al paquet equivocat, o el paquet no era de mitjans propis.',
          sub_over_scope: '{supplier} porta certificats {certified} {unit} de {item} i el seu paquet, {name}, li dona {scope}. Es paga el que no és seu.',
          quantity_over_measurement: 'A {item} els paquets reclamen {claimed} {unit} i l’amidament és {measurement}. Cal decidir qui es queda sense els que falten.',
          offers_expired: '{name} està licitant amb {count} oferta caducada: la més barata, {supplier} | {name} està licitant amb {count} ofertes caducades: la més barata, {supplier}',
          awarded_over_target: '{name} es va adjudicar a {supplier} un {pct} % per sobre de l’objectiu.',
          awarded_incomplete: '{name} es va adjudicar a {supplier} deixant {count} línia sense contractar. | {name} es va adjudicar a {supplier} deixant {count} línies sense contractar.',
        },
        status: 'Estat',
        statusOf: {
          open: 'Obert',
          tendering: 'En contractació',
          awarded: 'Contractat',
          closed: 'Tancat',
        },
        typeHelp: {
          supply: 'Es comprarà amb comanda. Genera compromès en aprovar-la.',
          hire: 'Es comprarà amb comanda. Genera compromès en aprovar-la.',
          service: 'Es comprarà amb comanda. Genera compromès en aprovar-la.',
          subcontract: 'Es contractarà amb sostre, retenció i certificacions.',
          own_means: 'No es compra. Es controla per rendiment des dels partes.',
        },
        history: {
          title: 'El que li ha passat',
          created: "Es va donar d'alta",
          edited: 'Es va editar',
          committed: 'Va passar sol a «{to}» en arribar {reference} per {amount}',
          status: 'Va passar de «{from}» a «{to}»',
          other: 'Canvi al paquet',
          assigned: 'Va canviar el que agrupa',
          tender: 'Moviment al seu comparatiu d’ofertes',
        },
      },
      coverage: {
        target: 'Objectiu de cost de l’obra',
        covered: 'Cobert per paquets',
        pct: '{pct} % de l’objectiu',
        unpackaged: 'Sense empaquetar',
        orphans: 'Veure la partida solta · {amount} | Veure les {count} partides soltes · {amount}',
        approve: 'Aprovar l’objectiu',
        approveTitle: 'Vols aprovar l’objectiu d’aquesta obra?',
        approveHelp: 'A partir d’aquí les desviacions es mesuren contra aquesta ratlla, i el repartiment entre paquets queda quiet. Es pot aixecar després, amb motiu.',
        approved: 'Objectiu aprovat.',
        approvedBy: 'Aprovat per {who} el {when}',
        cannotApprove: 'No es pot aprovar mentre quedi objectiu fora de tot paquet: aquests diners no tenen disponible, així que cap comanda contra ells es pot comparar amb res.',
        blocked: 'L’objectiu està aprovat: el repartiment no es toca. Aixeca l’aprovació per canviar-lo.',
        revoke: 'Aixecar l’aprovació',
        revokeTitle: 'Aixecar l’aprovació de l’objectiu',
        revokeHelp: 'El repartiment torna a poder tocar-se. Queda escrit qui ho ha aixecat i per què.',
        revokeReason: 'Per què s’aixeca',
      },
      picker: {
        title: 'Què agrupa {name}',
        search: 'Cercar per codi o concepte',
        onlyFree: 'Només amb disponible',
        natureHint: 'S’afegeixen les línies d’aquella naturalesa, mai la partida sencera.',
        claimed: 'Assignat',
        free: 'Disponible',
        assigned: 'Assignada',
        noBreakdown: 'sense descompost',
        takenBy: 'Se l’enduen: {packages}',
        allChapters: 'Tots els capítols',
        allNatures: 'Totes les naturaleses',
        amountLabel: 'Import (màxim {max})',
        amountReason: 'Per què aquest import',
        amountReasonPlaceholder: 'Ex.: només el material; la bomba la posa la subcontracta',
        amountConfirm: 'Afegir amb aquest import',
        freeQuantity: 'Lliure: {free} {unit} de {total}',
        byQuantity: 'Per quantitat',
        byAmount: 'Per import',
        quantityLabel: 'Quantitat en {unit} (màxim {max})',
        quantityHint: '{pct} % de l’amidament · {amount}',
        quantityReason: 'Per què aquesta quantitat',
        quantityReasonPlaceholder: 'Ex.: tram nord, del P.K. 0+000 al 0+400',
        quantityConfirm: 'Afegir amb aquesta quantitat',
        quantityCol: 'Quantitat',
        tally: '{lines} de {items} · {amount}',
        tallyLines: '0 línies | {count} línia | {count} línies',
        tallyItems: '0 partides | {count} partida | {count} partides',
        pickAll: 'Seleccionar tot el filtrat',
        clear: 'Treure la selecció',
        add: 'Afegir {count}',
        added: 'Afegides {count} assignacions.',
        stale: 'revisar',
        origin: 'D\'on surt',
        mode: {
          whole: 'Partida sencera',
          lines: 'Línia',
          pct: 'Percentatge',
          amount: 'Import',
          quantity: 'Quantitat',
        },
        dropTitle: 'Treure {code} del paquet?',
        dropHelp: 'La partida conserva el seu objectiu íntegre; el que es treu és el que aquest paquet en reclamava.',
        dropReason: 'Per què es treu',
        dropReasonHelp: 'Aquest paquet ja ha comprat: treure-li una partida li baixa l’objectiu sense baixar-li la despesa, així que el disponible s’estreny de cop.',
        none: {
          title: 'No hi ha partides que encaixin',
          subtitle: 'Prova de treure el filtre de naturalesa o de capítol. Si l’obra encara no té pressupost, no hi ha res per empaquetar.',
        },
      },
      indirect: {
        templates: {
          'IND-01': "Cap d'obra",
          'IND-02': "Encarregat",
          'IND-03': "Administratiu d'obra",
          'IND-10': "Casetes d'obra",
          'IND-11': "Tanca provisional",
          'IND-12': "Escomeses provisionals",
          'IND-13': "Mitjans auxiliars comuns",
          'IND-20': "Electricitat",
          'IND-21': "Aigua",
          'IND-22': "Internet i comunicacions",
          'IND-30': "Neteja",
          'IND-31': "Vigilància",
          'IND-32': "Seguretat i salut general",
          'IND-33': "Gestió de residus general",
          'IND-34': "Assegurances específiques d'obra",
          'IND-40': "Vehicles d'obra",
          'IND-41': "Combustible",
          'IND-42': "Desplaçaments i dietes",
          'IND-90': "Lloguers generals",
          'IND-91': "Petits consumibles",
          'IND-92': "Altres costos generals d'obra",
        },
        title: "Costos indirectes d'obra",
        subtitle: "El que costa tenir l'obra oberta i no pertany a cap partida.",
        empty: {
          title: "Sense pressupost d'indirectes",
          subtitle: "El cap d'obra, la caseta, la vigilància i la llum costen diners cada mes que l'obra segueix oberta.",
        },
        fromTemplate: 'Començar des d’una plantilla',
        templateKind: "Tipus d'obra",
        clear: 'Buidar el bloc',
        restart: 'Començar de nou',
        restartTitle: 'Començar de nou?',
        restartConfirm: 'Es treuen els {count} conceptes i s’obre el gestor per triar una altra plantilla. El que ja tingui cost imputat es queda.',
        clearTitle: 'Buidar els costos indirectes?',
        clearConfirm: 'Es treuen els {count} conceptes d’aquesta obra. Els que ja tinguin cost imputat es queden: això no s’esborra, es tanca.',
        cleared: 'Buidat: {count} conceptes fora',
        clearedSome: 'Fora {count}. Es queden, perquè ja tenen cost: {kept}',
        removeTitle: 'Treure el concepte?',
        removeConfirm: '«{name}» desapareix del pressupost d’indirectes. Si ja té cost imputat, no es podrà: tanca’l en comptes de treure’l.',
        fullscreenHint: 'Costos indirectes a pantalla completa · Escape per sortir',
        newConcept: 'Afegir concepte',
        created: 'Pressupost creat amb {count} conceptes. Posa’ls import.',
        kind: {
          building: 'Edificació',
          industrial: 'Nau industrial',
          refurbishment: 'Rehabilitació',
          civil: 'Obra civil',
          fit_out: 'Reforma',
        },
        category: {
          personnel: "Personal d'obra",
          facilities: 'Instal·lacions provisionals',
          utilities: 'Subministraments',
          services: 'Serveis generals',
          vehicles: 'Vehicles i desplaçaments',
          other: 'Altres',
        },
        behaviour: {
          label: 'Comportament',
          help: 'Decideix com s’estima el que falta per gastar.',
          fixed: 'Pagament únic',
          monthly: 'Mensual',
          weekly: 'Setmanal',
          daily: 'Diari',
          variable: 'Variable',
          manual: 'Manual',
        },
        rate: 'Cost per període',
        rateHelp: 'El que costa cada mes, setmana o dia.',
        durationSensitive: 'Depèn del termini',
        durationSensitiveHelp: "Si un mes més d'obra el fa costar més.",
        closed: 'Tancat',
        closedHelp: 'Ja no costarà més.',
        columns: {
          concept: 'Concepte',
          type: 'Tipus',
          target: 'Objectiu',
          real: 'Real',
          committed: 'Compromès',
          pending: 'Pendent previst',
          forecast: 'Previsió tancament',
          deviation: 'Desviació',
          consumed: '% consumit',
        },
        totals: {
          target: 'Objectiu',
          real: 'Real',
          committed: 'Compromès',
          pending: 'Pendent previst',
          forecast: 'Previsió tancament',
          deviation: 'Desviació',
        },
        duration: {
          title: 'El termini',
          planned: 'Fi planificat',
          forecast: 'Fi previst',
          extra: '{days} dies de més',
          perMonth: '{amount} al mes depenen del termini',
          impact: 'Sobrecost per ampliació del termini',
          shift: 'Moure el fi previst',
          shiftTitle: "Vols moure el fi previst de tota l'obra?",
          shiftHelp: 'Es mou en els {count} conceptes que depenen del termini.',
          shiftReason: 'Per què es retarda',
          moved: 'Moguts {count} conceptes.',
          onTime: "L'obra va en termini.",
        },
        deviations: { title: 'Principals desviacions', empty: 'Cap concepte es passa de l’objectiu.' },
        evolution: { title: 'Evolució mensual', real: 'Real', target: 'Objectiu repartit', linear: "L'objectiu es reparteix lineal al llarg del termini." },
        movements: { title: 'D’on surt', empty: 'Encara no se li ha imputat res.', origin: 'Origen', date: 'Data', amount: 'Import' },
        identity: 'Identificació',
        family: 'Família',
        budget: 'Pressupost',
        period: 'Període',
        startDate: 'Inici previst',
        endDate: 'Fi previst',
        actualStart: 'Inici real',
        forecastEnd: 'Fi previst actualitzat',
        initialTarget: 'Pressupost inicial',
        pendingOverride: 'Pendent a mà',
        pendingOverrideHelp: 'Quan el que falta ho diu una persona i no el càlcul.',
        removeTitle: 'Esborrar aquest concepte?',
        removeHelp: 'Només s’esborra el que no té cap moviment. Quan té cost imputat es TANCA.',
        alerts: {
          duration_overrun: 'El retard de {days} dies suposa {amount} més en costos indirectes.',
          over_budget: '{name} supera l’objectiu en un {pct} %.',
          forecast_over: '{name} preveu tancar {amount} per sobre de l’objectiu.',
          past_due: '{name} continua generant cost {days} dies després de la seva data prevista.',
          no_budget: 'S’ha registrat cost a {name} sense pressupost associat.',
          unbooked_periods: 'Fa {periods} períodes que corre {name} i falten {amount} per imputar.',
        },
        unassignedHint: 'Hi ha {count} moviment sense imputar en aquesta obra. | Hi ha {count} moviments sense imputar en aquesta obra.',
      },
      versions: {
        remove: 'Treure aquesta versió',
        removeTitle: 'Treure la versió?',
        removeConfirm: '«{name}» desapareix amb els seus capítols i les seves partides, i això no té volta. Només es pot si no té cap amidament, cap cost ni cap certificació al darrere.',
        removed: 'Versió treta ({chapters} capítols, {items} partides)',
        label: 'Versió del pressupost', historical: 'històrica',
        newRevision: 'Nova revisió',
        newRevisionTitle: 'Vols crear una revisió del pressupost?',
        newRevisionHelp: "Es copia el pressupost sencer i l'obra passa a la còpia: amidaments, costos i certificacions es mouen amb ella, així que la certificació següent continua sent la resta amb l'anterior. La versió d'ara es queda per consultar.",
        created: 'Revisió creada amb {chapters} capítols i {items} partides.',
        compare: 'Comparar',
        compareTitle: 'Què ha canviat',
        added: 'Entren', removed: 'Surten', changed: 'Canvien',
        delta: 'Mou la venda',
        deltaHelp: "El que entra menys el que surt, més el que canvia de preu o de quantitat.",
        historicalNote: 'Estàs veient una versió antiga: només lectura.',
        same: { title: 'Sense diferències', subtitle: 'Les dues versions diuen el mateix.' },
        what: { added: 'Entra', removed: 'Surt', changed: 'Canvia' },
        field: { description: 'Concepte', unit: 'Unitat', saleQuantity: 'Quantitat', salePrice: 'Preu' },
      },
      editor: {
        edit: 'Editar capítols i partides',
        done: "Deixar d'editar",
        hint: 'Es desa sol en sortir de cada casella.',
        addChapter: 'Afegir capítol',
        addSubchapter: 'Afegir un capítol a dins',
        addItem: 'Afegir una partida a aquest capítol',
        chapter: 'capítol',
        item: 'partida',
        unit: 'Ut',
        quantity: 'Quantitat',
        price: 'Preu',
        amount: 'Import',
        newChapter: 'Capítol nou',
        newItem: 'Partida nova',
        openBudget: 'Escriure el pressupost a mà',
        budgetOpened: 'Pressupost obert. Comença per un capítol.',
        removeTitle: 'Esborrar del pressupost?',
        removeChapterHelp: "Només es pot esborrar un capítol buit. Si té partides a dins, treu-les o anul·la-les primer.",
        removeItemHelp: "Només s'esborra una partida que no tingui res apuntat. Quan té amidament, cost o certificació, s'anul·la amb un motiu i es queda a l'històric.",
      },
      tree: {
        fullscreen: 'Ampliar',
        exitFullscreen: 'Sortir',
        fullscreenHint: 'Control de partides a pantalla completa · Escape per sortir',
        concept: 'Concepte', quantity: 'Amidament', target: 'Objectiu',
        spent: 'Gastat', progress: 'Executat', consumed: 'Consumit',
        deviation: 'Desviació',
        empty: {
          title: "L'obra encara no té pressupost",
          subtitle: "Importa el BC3 que t'ha passat l'oficina tècnica i l'arbre apareix aquí.",
        },
      },
      itemKind: { normal: 'Normal', lump_sum: 'Alçada', lump_sum_justified: 'A justificar' },
      origin: { bc3: 'Del BC3', variation: 'Contradictori', manual: 'A mà' },
      nature: { labour: "Mà d'obra", plant: 'Maquinària', material: 'Material', subcontract: 'Subcontracta', other: 'Altres' },
      quiet: { days: 'Fa {days} dies', today: 'Avui', never: 'Cap encara' },
      unassigned: {
        assignTo: 'Penjar de la partida',
        pickItem: 'Busca la partida',
        assign: 'Assignar {count}',
        assigned: '{count} assentaments penjats de la seva partida',
        help: "Tot el cost que arriba del camp entra aquí: un comunicat no sap de codis de partida i no ha de saber-ne. El que fa que això funcioni és que algú buidi aquesta safata.",
        date: 'Data', description: 'Concepte', nature: 'Naturalesa', origin: "D'on ve", amount: 'Import',
        empty: { title: 'No hi ha res sense assignar', subtitle: "Tot el cost d'aquesta obra penja de la seva partida." },
      },
      import: {
        title: 'Importar un pressupost',
        help: "Un fitxer BC3 (FIEBDC-3), el que exporta Presto, Arquímedes o Menfis. Substitueix l'arbre de la versió, no el barreja.",
        file: 'El fitxer', versionName: 'Nom de la versió',
        versionHelp: 'Si el deixes buit s’usa el títol que porta el fitxer.',
        kind: 'Què és', original: 'Projecte original', amendment: 'Modificat',
        charset: 'Joc de caràcters', charsetHelp: 'Deixa-ho automàtic. Només es toca si els accents surten malament.',
        charsetAuto: 'El que digui el fitxer',
        done: 'Han entrat {items} partides per {total}',
      },
      report: {
        chapters: 'Capítols', items: 'Partides', charset: 'Caràcters', program: 'Programa',
        anomaly: 'Què ha passat', count: 'Quantes vegades',
        clean: 'El fitxer ha entrat net: res a explicar.',
        empty: { title: 'Encara no s’ha importat res', subtitle: 'Quan entri un BC3, aquí queda escrit què portava i què li faltava.' },
      },
      anomaly: {
        CHARSET_NOT_DECLARED: 'El fitxer no diu en quins caràcters està; s’ha suposat CP850',
        CHARSET_UNKNOWN: 'El fitxer anomena un joc de caràcters desconegut',
        CHARSET_IS_UTF8: 'El fitxer és UTF-8 encara que digui una altra cosa',
        PREAMBLE_IGNORED: 'Hi havia text abans del primer registre',
        RECORD_HEADER_INVALID: 'Un registre sense capçalera vàlida',
        RECORD_TYPE_UNSUPPORTED: 'Registres que aquest lector encara no interpreta',
        FORMAT_VERSION_UNKNOWN: 'Versió del format desconeguda',
        DECIMALS_NOT_DECLARED: 'Sense registre de decimals; s’usen els del format',
        ROOT_NOT_FOUND: 'El fitxer no té arrel d’obra',
        MULTIPLE_ROOTS: 'El fitxer té més d’una arrel',
        CHAPTER_EMPTY: 'Capítols sense res a dins',
        DECOMPOSITION_CYCLE: 'Un descompost que es crida a si mateix',
        DECOMPOSITION_TOO_DEEP: 'Un descompost massa imbricat',
        TREE_TOO_LARGE: 'Un arbre massa gran per ser un pressupost',
        FILE_TOO_LARGE: 'Un fitxer massa gran per llegir-lo',
        CONCEPT_NOT_DECLARED: 'Conceptes usats i mai definits',
        CONCEPT_REDECLARED: 'Conceptes definits dues vegades',
        CONCEPT_WITHOUT_PRICE: 'Conceptes sense preu',
        CONCEPT_WITHOUT_MEASUREMENT: 'Partides sense amidament',
        CONCEPT_WITHOUT_DECOMPOSITION: 'Partides sense descompost',
        PRICE_DISAGREES_WITH_DECOMPOSITION: 'El preu no quadra amb el seu descompost',
        MEASUREMENT_TOTAL_MISMATCH: 'L’amidament no quadra amb les seves línies',
        MEASUREMENT_EXPRESSION_INVALID: 'Una fórmula d’amidament que no ho és',
        MEASUREMENT_EXPRESSION_FAILED: 'Una fórmula d’amidament que no s’ha pogut calcular',
        MEASUREMENT_WITHOUT_TARGET: 'Amidaments que no diuen de què són',
      },
    },
  },

  en: {
    // El nombre de cada permiso en la pantalla de roles. Sin esto salía el
    // código crudo («construction:margins») a quien decide quién ve el dinero.
    permissions: {
      construction: {
        read: 'See jobs, their budget and their progress',
        write: 'Create jobs, record measurements and assign costs to items',
        import: 'Load and edit the budget (BC3 or by hand)',
        margins: 'See and manage the money: target, cost, margin, packages and subcontracts',
        award: 'Award packages from their bid comparison: signs the subcontract or the commitment',
      },
    },
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { construction: 'Works' },
    // Las aperturas del asistente: la clave es el id estable de
    // backend/assistant.js; el texto inglés de allí es solo el fallback.
    assistant: {
      openers: {
        construction: {
          'how-are-the-works': 'How are the works going?',
          'what-is-deviating': 'What is going over budget in the works?',
          'certifications-pending': 'Which certifications are still pending?',
        },
      },
    },
    audit: { actions: { construction: { cost_voided: 'Cost entry voided' } } },
    construction: {
      // El comparativo de ofertas de un paquete (0.25.0).
      tenders: {
        title: 'Bid comparison',
        back: 'To the packages',
        pageTitle: 'Bid comparison · {code} {name}',
        pageSubtitle: 'Comparison no. {number} · {count} line | Comparison no. {number} · {count} lines',
        print: 'Print PDF',
        template: 'Template for bidders',
        addOffer: 'Add offer',
        editOffer: 'Edit offer',
        status: {
          open: 'Out to tender',
          awarded: 'Awarded',
          cancelled: 'Closed without award',
        },
        dueOn: 'Offers by {date}',
        frozen: 'Frozen on {date}: as it stood at the award',
        target: 'Target',
        offersCount: 'Offers',
        bestComplete: 'Best complete offer',
        vsTarget: 'Against target',
        linesChanged: 'The package has changed since this comparison was opened: its lines are no longer the ones that were asked for.',
        refreshLines: 'Refresh lines',
        linesRefreshed: 'Lines refreshed. Each bidder keeps its prices per item.',
        ruleLabel: 'Lines not offered are filled with',
        rule: {
          highest: 'The highest price offered',
          target: 'The target',
        },
        ruleHelp: {
          highest: 'Homogenized: every line a bidder does not price is filled with the highest price the others offered (or the target if nobody offered it). It is the prudent rule: you will end up contracting that line, and it will not come cheap.',
          target: 'Homogenized: every line a bidder does not price is filled at the line’s target price.',
        },
        awardedTo: 'Awarded to {supplier}',
        awardedBy: 'By {who} on {date}',
        wasRecommended: 'it was the recommended offer',
        wasNotRecommended: 'the recommended one was {supplier}',
        reason: 'Reason',
        seeSubcontract: 'Open the subcontract that was created',
        orderNote: 'Commitment recorded on the package. If purchasing is installed, the order is left as a draft with these lines and prices: the buyer sends it.',
        cancelAward: 'Undo the award',
        cancelAwardHelp: 'For when the bidder backs out before starting. The comparison reopens with its offers, the subcontract is cancelled (if nothing has been certified to it) and the commitment is released. The undone award stays in the history.',
        cancelAwardWithdraw: 'The bidder withdraws: its offer stops competing',
        awardCancelled: 'Award undone. The comparison is open again.',
        recommended: 'Recommended: {supplier}',
        recommendedShort: 'Recommended',
        basis: {
          cheapest_complete: 'It is the cheapest complete and valid offer, counting homogenized lines.',
          cheapest_complete_expired: 'It is the cheapest complete offer, but every complete offer has expired: ask the bidder to hold its price before awarding.',
          no_complete: 'No offer is complete: this is the cheapest homogenized one. Ask for prices on what is missing before awarding.',
        },
        belowTarget: 'It is {amount} ({pct}) below target.',
        aboveTarget: 'It is {amount} ({pct}) above target.',
        runnerUp: 'Next comes {supplier}, {amount} dearer.',
        cheaperIncomplete: '{supplier} offers {offered} but leaves {count} line unpriced: homogenized it comes to {homogenized}. | {supplier} offers {offered} but leaves {count} lines unpriced: homogenized it comes to {homogenized}.',
        cheaperExpired: '{supplier} would be cheaper ({amount}) but its offer has expired: ask it to renew.',
        awardRecommended: 'Award to the recommended one',
        emptyTitle: 'No offers yet',
        emptyHelp: 'Download the template, send it to three or four bidders and enter their prices here: typed in or read from their spreadsheet.',
        offerStatus: {
          received: 'Received',
          awarded: 'Awarded',
          not_awarded: 'Not awarded',
          withdrawn: 'Withdrawn',
        },
        expired: 'Expired',
        missingCount: '{count} line unpriced | {count} lines unpriced',
        offered: 'Offered',
        homogenized: 'Homogenized',
        bestLines: 'Lines at best price',
        reference: 'Offer no.',
        validUntil: 'Valid until',
        executionDays: 'Duration (days)',
        days: '{count} day | {count} days',
        paymentTerms: 'Payment terms',
        paymentTermsPlaceholder: 'E.g.: 60 days from invoice',
        retentionPct: 'Retention (%)',
        discountPct: 'Overall discount (%)',
        discountHelp: 'On what it offers, not on the lines that get filled.',
        discountShort: '{pct} % off',
        totalDeclared: 'Total written by the bidder',
        totalDeclaredHelp: 'The one on its offer. If it does not match its prices, you are told.',
        declaredMismatch: 'Its offer says {declared} and its prices add up to {computed}: check what was mistyped.',
        strongCount: '{count} price is far from the average: check unit and quantity. | {count} prices are far from the average: check units and quantities.',
        exclusions: 'Not included',
        exclusionsHelp: 'What the offer leaves out. One per line, with Enter.',
        exclusionsPlaceholder: 'E.g.: paving reinstatement',
        notes: 'Notes',
        documents: 'No papers | 1 paper | {count} papers',
        documentsTitle: 'Papers of the offer from {supplier}',
        withdrawOffer: 'Withdraw',
        restoreOffer: 'Count it again',
        removeOffer: 'Remove',
        withdrawHelp: 'The bidder backs out. The offer stays in the comparison but stops competing and marking best prices.',
        restoreHelp: 'The offer competes with the others again.',
        removeOfferTitle: 'Remove the offer?',
        removeOfferHelp: 'The offer from {supplier} is deleted with its prices and papers. If the bidder backed out, «Withdraw» is better: that way it is on record.',
        award: 'Award',
        awardTitle: 'Award the package',
        awardLead: 'Award to {supplier} for {amount}.',
        awardSubcontractHelp: 'A subcontract linked to this package is created, with the offer amount, {retention} % retention and {days} days duration. Its scope is read from the package.',
        awardOrderHelp: 'A commitment of {amount} is recorded on the package, and purchasing leaves the order as a draft with these lines. Every order confirmed to this bidder on this package is deducted from the commitment.',
        awardIncomplete: 'This offer leaves {count} line unpriced, worth {amount} at target: it will have to be contracted separately. | This offer leaves {count} lines unpriced, worth {amount} at target: they will have to be contracted separately.',
        signedLabel: 'Signature',
        signedHelp: 'Leave it signed (commits its amount from today)',
        reasonRequired: 'Reason (required: the recommended one is {supplier})',
        reasonOptional: 'Reason (optional)',
        awarded: 'Package awarded. The comparison is now frozen.',
        tableTitle: 'Comparison table',
        viewLabel: 'View',
        view: {
          table: 'Table',
          lines: 'By line',
          offers: 'By offer',
        },
        line: 'Line',
        unitPrice: 'Price',
        amount: 'Amount',
        pastPrices: 'Prices from other works',
        notOffered: 'Not offered',
        filledWith: 'filled with {amount}',
        lastShort: 'last {price}',
        lastLong: 'Last time it offered {price} ({work}, {date})',
        notOfferedLines: 'Lines not offered',
        targetShort: 'target {price} · {amount}',
        best: 'Best',
        pickOffer: 'Offer',
        historyTitle: 'Comparison history',
        cancelTender: 'Close without award',
        cancelTenderHelp: 'The package goes back to «Open». The prices stay in the history: they were real offers.',
        offerDate: 'Offer date',
        pricesTitle: 'Prices per line',
        pricesHelp: 'Blank means «not offered», and it shows that way in the table. A 0 is a price: the line is included in another.',
        importSheet: 'Read their spreadsheet',
        reading: 'Reading…',
        sheetRead: '{file} · sheet «{sheet}»: {priced} of {lines} lines priced. Check and save.',
        sheetMissing: '{count} line is not on their sheet: it stays unpriced. | {count} lines are not on their sheet: they stay unpriced.',
        sheetQuantity: '{code}: offered {offered} {unit} where {asked} were asked for. Its total is not comparable until that is cleared up.',
        sheetUnmatched: 'Row {row} matches no line: {text}',
        draftTotal: 'Offered: {amount} · no unpriced lines | Offered: {amount} · {count} unpriced line | Offered: {amount} · {count} unpriced lines',
        offerSaved: 'Offer saved',
        history: {
          opened: 'Comparison opened with {count} lines',
          edited: 'Rule or date changed',
          lines_refreshed: 'Lines refreshed from the package ({count})',
          cancelled: 'Closed without award: {comment}',
          offer_added: 'Offer from {supplier}',
          offer_revised: 'Offer from {supplier} revised',
          offer_withdrawn: '{supplier} withdraws its offer {comment}',
          offer_restored: 'The offer from {supplier} competes again',
          offer_removed: 'Offer from {supplier} removed',
          awarded: 'Awarded to {supplier} for {amount} {comment}',
          award_cancelled: 'Award to {supplier} undone: {comment}',
        },
        templateLabels: {
          code: 'Code',
          description: 'Description',
          unit: 'Unit',
          quantity: 'Quantity',
          price: 'Unit price',
          amount: 'Amount',
          total: 'TOTAL',
        },
        pdf: {
          title: 'Bid comparison',
          work: 'Work',
          package: 'Package',
          date: 'Date',
          code: 'Code',
          description: 'Description',
          unit: 'Unit',
          quantity: 'Qty',
          target: 'Target',
          unitPrice: 'Price',
          amount: 'Amount',
          notOffered: 'not offered',
          offered: 'Offered',
          homogenized: 'Homogenized',
          vsTarget: 'Against target',
          missing: 'Lines not offered',
          ruleHighest: 'Lines not offered are filled with the highest price offered by the other bidders (or the target if nobody offered them).',
          ruleTarget: 'Lines not offered are filled at the target price.',
          recommended: 'Recommended',
          awardedTo: 'Awarded to',
          reason: 'Reason',
          best: 'Best price',
          frozen: 'Frozen record of the award',
          validUntil: 'valid until',
          expired: 'expired',
          withdrawn: 'withdrawn',
          exclusions: 'Not included',
          discount: 'Discount',
        },
        rowOpen: 'out to tender, no offers | out to tender, 1 offer | out to tender, {count} offers',
        rowAwarded: 'awarded to {supplier}',
        panelAwarded: 'Awarded to {supplier} for {amount}. The comparison stays as a record.',
        panelOpen: 'Out to tender, no offers yet. | Out to tender, 1 offer. | Out to tender, {count} offers.',
        panelNone: 'Ask several bidders to price what the package claims, and compare them against its target.',
        openComparison: 'Open the comparison',
        start: 'Put out to tender',
        fromTender: 'Awarded from a bid comparison: see the other offers and the reason',
      },
      // La base de precios de la casa, en la ficha de la partida (0.25.0).
      priceHistory: {
        title: 'Prices offered before for this unit',
        empty: 'No offers stored for this unit of work yet. Every price entered in a comparison will show up here.',
        min: 'Lowest',
        median: 'Median',
        max: 'Highest',
        lastAwarded: 'Last awarded',
        againstTarget: 'Target price of this item: {target}. Median of what was offered: {median}.',
        awarded: 'Awarded',
        howMatched: 'Matched by price-database code or by a similar description, with the same unit and the same numbers (diameter, thickness, strength).',
      },
      item: {
        historicalNote: 'This item belongs to a version that is no longer the current one: you can look, not change.',
        cancelAction: 'Cancel item',
        packageCommitted: '{amount} comes from the contract of its package {packages}. Already included. | {amount} comes from the contracts of its packages {packages}. Already included.',
        restoreAction: 'Reopen item',
        cancelTitle: 'Cancel this item?',
        cancelHelp: 'Its quantity goes to zero and it stops counting on the job. It is not deleted: it keeps its history and any cost already on it, and it can be reopened.',
        cancelledToast: 'Item cancelled',
        restoreTitle: 'Reopen the item?',
        restoreHelp: 'It comes back with the quantities it had before it was cancelled.',
        restoredToast: 'Item reopened',
        reasonRequired: 'Reason (required)',
        reasonOptional: 'Reason (optional)',
        reasonPlaceholder: 'Why, in one sentence',
        lumpSum: 'Lump-sum item',
        lumpSumHint: 'Measured all or nothing: not done, or done in full.',
        lumpNone: 'Not done',
        lumpWhole: 'Done in full',
        derivedHelp: 'Item justified by cost: it is not measured. Its progress comes from the cost charged to it against its target.',
        pendingAuto: 'Remaining cost worked out by the system.',
        pendingManual: 'Remaining cost set by hand: {reason}',
        pendingAdjust: 'Adjust remaining',
        targetAdjust: 'Adjust target',
        targetTitle: 'Item target',
        targetHelp: 'What the item is expected to cost. Every control figure is measured against it, so the change is kept in the history with its reason.',
        targetPriceLabel: 'Target price per {unit}',
        targetQuantityLabel: 'Target quantity ({unit})',
        targetPreview: 'Resulting target: {amount}',
        targetReason: 'Reason for the change',
        targetReasonPlaceholder: 'E.g. price agreed with the supplier, estimate revised',
        targetSaved: 'Target updated.',
        pendingTitle: 'What is left to spend',
        pendingHelp: 'If you know that what is left is not what the calculation says, write it here. The forecast at completion is redone with this figure.',
        pendingEmpty: 'Empty goes back to the automatic calculation. Zero means nothing is left.',
        pendingSaved: 'Remaining cost saved',
        cycle: {
          statusTitle: 'Change the approval',
          stageTitle: 'Change the work stage',
          moveTo: 'Move to “{what}”',
          approveHelp: 'Once approved it joins the job’s sale and can be certified. There is no going back.',
          saved: 'Variation updated',
          status: {
            proposed: 'Propose again',
            pricing: 'Move to pricing',
            sent: 'Send to the architect',
            approved: 'Approve',
            rejected: 'Reject',
          },
        },
        title: 'Budget item',
        back: 'To the outline',
        cancelled: 'Cancelled',
        bornLosing: 'Born losing',
        sale: 'Sale',
        target: 'Target',
        real: 'Real',
        deviation: 'Deviation',
        forecast: 'Forecast at completion',
        committed: 'Committed',
        margin: 'Margin',
        excess: '{quantity} {unit} more than sold has been built ({pct}). Flagged, not blocked: who pays for it is a decision for a person.',
        cancelledFor: 'Cancelled: {reason}',
        newMeasure: 'Record measurement',
        newMeasureHint: 'To date, not this month: everything built so far.',
        quantity: 'Quantity to date',
        comment: 'Comment',
        record: 'Record',
        recorded: 'Measurement recorded',
        date: 'Date',
        toDate: 'To date',
        moved: 'Moved',
        who: 'Who',
        length: 'Length',
        width: 'Width',
        height: 'Height',
        subtotal: 'Subtotal',
        code: 'Code',
        concept: 'Concept',
        performance: 'Yield',
        price: 'Price',
        breakdownCost: 'What the breakdown costs:',
        splitHelp: 'The item keeps its whole target: what the packages claim adds up to that target at most, and never subtracts from it. The split is decided from the package, in its picker.',
        splitQuantity: 'In quantity: {claimed} {unit} in packages and {free} free, out of {total}.',
        noPackages: {
          title: 'It is in no package',
          subtitle: 'Its target has not been taken to market yet, so there is no available balance to compare an order against. It goes in from a package picker.',
        },
        costKind: 'Kind',
        whatHappened: 'What happened',
        detail: 'Detail',
        bc3Lines: 'The project lines',
        tabs: {
          measurements: 'Measurements',
          breakdown: 'Breakdown',
          costs: 'Cost',
          history: 'History',
          packages: 'Split across',
          prices: 'Past prices',
        },
        noMeasures: {
          title: 'Not measured yet',
          subtitle: 'Record above what has been built, to date.',
        },
        noBreakdown: {
          title: 'No breakdown',
          subtitle: 'The file carried no resources for this item. Its target comes from the discount.',
        },
        noCosts: {
          title: 'No cost yet',
          subtitle: 'Nothing on this item. Look in the unassigned tray.',
        },
        noHistory: {
          title: 'No changes',
          subtitle: 'Nobody has touched this item since it came in.',
        },
      },
      status2: {
        approved: 'Approved',
        proposed: 'Proposed', pricing: 'Being priced',
        sent: 'Sent to the engineer',
        rejected: 'Rejected',
      },
      stage: {
        not_started: 'Not started', in_progress: 'In progress',
        done: 'Done', invoiced: 'Invoiced', paid: 'Paid',
      },
      subcerts: {
        title: 'Subcontractor certificate', back: 'To the contract',
        openDraft: 'Open the one under way',
        listTitle: 'Certificates', number: 'Certificate {number}',
        prepare: 'Prepare the next one',
        periodEnd: 'Up to',
        period: 'This period',
        periodHelp: 'Certified this month: the difference against what was already certified.',
        accumulated: 'To date', ceiling: 'Contract ceiling',
        ceilingHelp: 'Contract plus approved extras. Going past it needs an addendum first, not an explanation later.',
        charges: 'Charges', penalties: 'Penalties',
        kindLabel: 'Kind', kind: { charge: 'Charge', penalty: 'Penalty' },
        retention: 'Retention',
        retentionHelp: 'On what is payable, not on what is certified — otherwise more is held back than the contract says.',
        payable: 'Payable', payableHelp: 'The base of their invoice for this month.',
        consumed: '{pct} % of the contract',
        linesTitle: 'Certified items', addLine: 'Add item',
        itemPlaceholder: 'Find the item',
        before: 'Previous', toDate: 'To date', price: 'Price', thisPeriod: 'This month',
        noLines: { title: 'No items', subtitle: 'Add what is certified this month. Quantities are always to date.' },
        none: { title: 'No certificates', subtitle: 'Nothing has been certified to this subcontractor yet.' },
        deductionsTitle: 'Charges and penalties',
        noDeductions: { title: 'No deductions', subtitle: 'Nothing is being deducted.' },
        addCharge: 'Add charge', addPenalty: 'Add penalty',
        chargeHelp: 'Work the main contractor did that was theirs to do: the crane the site manager provided, the clearing they skipped. Backed by a delivery note.',
        penaltyHelp: 'The contract applying itself: delay, breach. Backed by a clause.',
        chargePlaceholder: 'E.g. Crane on the 12th',
        penaltyPlaceholder: 'E.g. 10 days late, clause 8.2',
        evidence: 'Evidence', evidencePlaceholder: 'E.g. DN-2026-77',
        appliedLabel: 'Status', charged: 'Deducted', pending: 'Pending',
        nothingHere: 'Nothing to pick.',
        refusalsTitle: 'This is what stops it',
        overContract: 'Past the contract: {accumulated} certified against a {ceiling} ceiling. An addendum is needed.',
        overClient: 'On {item} they are certified {toSub} {unit} and the client only {toClient}. That is work paid for before it is billed.',
        scopeMax: 'max. {max} {unit}',
        scopeNote: 'The maximum of each item comes from its package, {name}: what it claims, minus what other subcontractors of the same package already have certified.',
        overScope: 'On {item} they are certified {asked} {unit} to date and their package gives them {scope}. What is not theirs would be paid.',
        warningsTitle: 'Look at this before approving',
        warn: {
          negative_period: 'The month is negative ({amount}): this is a correction downwards.',
          negative_payable: 'Nothing to pay: the deductions eat the whole certificate ({amount}).',
          line_went_back: 'On {item} less is certified than last month ({amount}).',
        },
        approve: 'Approve',
        approveConfirm: 'The cost goes into this month’s ledger for the work and the contract commitment goes down. Payable: {amount}.',
        approved: 'Certificate approved',
        blocked: 'Cannot be approved',
        certifiedLocked: 'This is the sum of its certificates.',
        status: {
          draft: 'Draft', sent: 'Sent', approved: 'Approved',
          rejected: 'Rejected', cancelled: 'Cancelled',
        },
        what: { created: 'Created', status: 'Status', lines: 'Items' },
      },
      templates: {
        title: 'Site overhead templates', manage: 'Templates',
        yours: 'Yours ({count})', new: 'New', newName: 'New template',
        builtIn: 'Catalogue',
        builtInHelp: 'This one is from the catalogue and stays as it is. Copy it and edit the copy, so "building" is still the catalogue one and you can tell what yours changed.',
        name: 'Name', forWhat: 'What it is for',
        forWhatPlaceholder: 'E.g. industrial units under 12 months',
        concept: 'Concept', amount: 'Amount', category: 'Category',
        addLine: 'Add concept', noLines: 'No concepts yet.',
        lineCount: '{count} concept | {count} concepts',
        zeroHelp: 'An amount can be zero, and that is not a blank: it means the concept exists on this kind of work and here it costs nothing.',
        copy: 'Copy', copySuffix: 'copy', copied: 'Template copied',
        apply: 'Use on this work', saved: 'Template saved',
        saveFromWork: 'Save this work as a template',
        fromWorkSuffix: 'template',
        savedFromWork: 'Saved as "{name}"',
        dropTitle: 'Delete the template',
        dropConfirm: '"{name}" goes. Works opened with it do not change: their concepts were copied already.',
        none: { title: 'No templates', subtitle: 'Start from a catalogue one, or make your own.' },
        pick: { title: 'Pick one', subtitle: 'Yours and the catalogue ones are on the left.' },
      },
      invoices: {
        title: 'Registered invoices',
        register: 'Register an invoice', registerFrom: 'Invoice this',
        edit: 'The invoice', saved: 'Saved',
        uninvoiced: 'Not invoiced',
        uninvoicedHelp: 'Cost already incurred whose paperwork has not arrived.',
        uninvoicedTitle: 'What is waiting for an invoice',
        received: 'Received', paid: 'Paid',
        owed: 'Owed', owedHelp: 'Registered and unpaid: what has to be paid.',
        supplier: 'Supplier', number: 'Invoice no.',
        numberPlaceholder: 'E.g. F-2026-0451',
        issueDate: 'Date', dueDate: 'Due', state: 'Status',
        dueHelp: 'With no date nothing is flagged: it is neither on time nor overdue.',
        amount: 'Net', amountHelp: 'What lands on the work. Tax is not a cost.',
        tax: 'Tax', notes: 'Notes',
        against: 'Against which cost',
        nothingToPick: 'This supplier has nothing waiting to be invoiced.',
        noSupplier: 'No supplier',
        overdue: 'Overdue',
        markPaid: 'Mark paid', markUnpaid: 'Mark unpaid',
        dropTitle: 'Remove the invoice?',
        dropConfirm: '{number} goes, and its cost is waiting for an invoice again.',
        status: { received: 'Received', paid: 'Paid' },
        alerts: {
          overdue: '{count} overdue invoice unpaid | {count} overdue invoices unpaid',
          due_soon: '{count} invoice due this week | {count} invoices due this week',
          uninvoiced: 'Cost received whose invoice has not arrived yet',
        },
        none: { title: 'No invoices', subtitle: 'Material and subcontractor invoices go here.' },
        allInvoiced: { title: 'All invoiced', subtitle: 'No received cost is waiting for paperwork.' },
      },
      subcontracts: {
        title: 'Subcontract', back: 'To the work',
        new: 'New subcontract', create: 'Create',
        newHelp: 'Just the contract. The items it covers are linked later, on its own file, where you can see what target it is measured against.',
        companyPlaceholder: 'Find the supplier',
        code: 'Contract', codePlaceholder: 'e.g. SUB-2026-03',
        scope: 'Scope', scopePlaceholder: 'What work it covers',
        start: 'Start', end: 'Due',
        retentionPct: 'Retention',
        paid: 'Paid', saved: 'Saved',
        targetHelp: 'The internal target of the items it covers. With no items linked there is nothing to compare against.',
        extrasTitle: 'Contract extras',
        late: 'Past its date',
        noPackage: 'No package: it discounts no available balance',
        sources: {
          executedScope: 'what the work has measured in its share of each item, at its contract price.',
          executedCertified: 'without a package there is no telling which part of the measurement is theirs: the certified amount is shown.',
          certified: 'the sum of its approved certificates: each month plus its extras.',
          invoiced: 'the invoices received against those certificates, at their net (charges and penalties are not invoiced).',
          paid: 'what those invoices marked as paid come to, less the retention each certificate applied, which is kept apart.',
        },
        withExtras: 'includes {amount} of extras',
        tabs: { summary: 'Summary', money: 'Extras and deductions', contract: 'The contract' },
        documents: 'Contract paperwork',
        addDocument: 'Upload a document',
        uploading: 'Uploading…', uploaded: 'Document uploaded',
        noDocuments: 'No paperwork yet. Upload the signed contract, addenda and certificates here.',
        dropDocument: 'Remove the document',
        dropDocumentConfirm: '"{name}" comes off this contract.',
        addExtra: 'Add extra',
        approveExtra: 'Approve',
        extraHelp: 'Description required: a price rise with no reason is not remembered at the end of the job. Starts unapproved — proposing is not agreeing.',
        extraWhat: 'What', extraPlaceholder: 'e.g. Unforeseen reinforcement on frame 4',
        approved: 'Approved', notApproved: 'Unapproved', approvedAlready: 'Already agreed?',
        noExtras: { title: 'No extras', subtitle: 'The contract runs at what was signed.' },
        covers: 'Covers {count} item | Covers {count} items',
        scopeAlertsTitle: 'Certified beyond its scope',
        scopeOver: '{item}: {certified} {unit} certified to date and its package gives it {scope}.',
        scopeFromPackage: 'Covers what its package, {name}, claims: the items, their quantities and the cap on what can be certified. It is read from the package, so if the split changes, this does too.',
        scopePackageEmpty: 'Its package, {name}, does not group any item yet: it covers its own items and has no scope cap.',
        scopeQuantity: 'Its share',
        certifiedToDate: 'Certified / max.',
        certifiedOf: '{certified} of {max}',
        packageHelp: 'Gives it its scope: the items and quantities the package claims, and the cap on what can be certified.',
        noPackageOption: 'No package',
        packageLabel: 'Package it awards',
        noPackageOption: 'No package (link it later)',
        packagePickHelp: 'Its scope will be read from the package: the items and quantities it claims, and the cap on what can be certified to it.',
        packagePicked: 'It will inherit {count} item with a target of {amount}. | It will inherit {count} items with a target of {amount}.',
        noItems: 'No items linked: nothing to compare the contract against.',
        openItem: 'Open the item',
        to_signed: 'Sign', to_in_progress: 'Started', to_finished: 'Finished', to_closed: 'Settle',
        what: {
          created: 'Created', status: 'Status', extra: 'Extra', package: 'Package',
          extra_approved: 'Extra approved', extra_unapproved: 'Extra unapproved',
          charge: 'Charge', penalty: 'Penalty',
          charge_removed: 'Charge removed', penalty_removed: 'Penalty removed',
        },
        company: 'Company', contracted: 'Contracted', extras: 'Extras',
        variance: 'Against target',
        varianceHelp: 'The contract forecast against the internal target of the items it covers. Negative is winning.',
        outstanding: 'Owed',
        outstandingHelp: 'What its certificates make payable (after charges, penalties and retention) less what has been paid: what we owe them today.',
        retained: 'Retention held',
        retainedHelp: 'Guarantee held back and returned when the work is finished.',
        pendingExtras: '{amount} unapproved',
        status: {
          draft: 'Draft', signed: 'Signed', in_progress: 'Working',
          finished: 'Finished', closed: 'Settled', cancelled: 'Cancelled',
        },
        empty: { title: 'No subcontracts', subtitle: 'Everything on this work is done in house.' },
      },
      certifications: {
        number: 'No.', period: 'Period',
        produced: 'Executed', certified: 'Certified', invoiced: 'Invoiced', paid: 'Collected',
        toCertify: 'To certify',
        toCertifyHelp: 'Work built that has not been passed to the customer yet. Money done and unclaimed.',
        toInvoice: 'To invoice', toCollect: 'To collect',
        retained: 'Held as retention',
        retainedHelp: 'Company money the customer holds and returns at the end of the work. Forgotten on every job.',
        current: 'This month', accumulated: 'To date', payable: 'Payable',
        prepare: 'Prepare this month',
        previewTitle: "This month's certification",
        previewHelp: 'It does not exist yet: this is what would come out of what the work has built to date. Creating it files a draft, for somebody to check before it is issued.',
        prepareHelp: 'Filled with what the work has built, to date. Starts as a draft so somebody reviews it before issuing.',
        prepared: 'Certification prepared',
        to_issued: 'Issue', to_invoiced: 'Invoice', to_paid: 'Collected',
        status: { draft: 'Draft', issued: 'Issued', invoiced: 'Invoiced', paid: 'Collected' },
        empty: { title: 'No certifications', subtitle: 'Prepare the first with what the work has built.' },
      },
      clientCert: {
        title: 'Certification', number: 'Certification no. {number}', back: 'Work',
        until: 'up to {date}', unsaved: 'Unsaved changes',
        accumulated: 'To date', previous: 'Previously certified', current: 'This month',
        retention: 'Retention ({pct} %)', payable: 'Net payable',
        negativeHelp: 'This month is negative: an earlier certification claimed too much and it is being given back. It is the to-date correction, not an error.',
        previousOf: 'Subtracted from no. {number}, the last one issued before this.',
        first: 'This is the first one: there is nothing earlier to subtract.',
        reviewNote: 'It has been filled with what is executed to date. Lower the lines the site supervision will not accept and save; you cannot go above what is executed. Once issued it cannot be changed: anything to correct goes in the next one.',
        readonly: {
          issued: 'Issued: it can no longer be changed. Corrections go in the next one, to date.',
          historical: 'Made with a budget version that is no longer the live one: it can be read, not changed.',
          permission: 'Read only: you are not allowed to change certifications.',
        },
        linesTitle: 'Lines', linesCount: '{count} item | {count} items',
        reset: 'Back to executed',
        resetHelp: 'Sets every line to what the work has executed to date. Nothing is saved until you press «Save lines».',
        overMax: '{count} line is above what is executed: lower it to save | {count} lines are above what is executed: lower them to save',
        lowered: '{count} line lowered from executed | {count} lines lowered from executed',
        empty: { title: 'No lines', subtitle: 'Nothing executed to certify in this one.' },
        col: {
          item: 'Item', unit: 'Unit', contracted: 'Project', executed: 'Executed to date',
          previous: 'Prev. certified', toDate: 'This one to date', current: 'This month', toDateAmount: 'Amount to date',
        },
        blocked: {
          cancelled: 'Cancelled: it keeps what it had already certified.',
          unapproved_variation: 'Unapproved variation: it keeps what it had already certified.',
        },
        max: 'max. {max} {unit}',
        save: 'Save lines', saved: 'Lines saved',
        to_issued: 'Issue', to_invoiced: 'Mark invoiced', to_paid: 'Mark paid',
        invoiceTitle: 'Mark as invoiced',
        invoiceMessage: 'No. {number} becomes invoiced for {amount}, what was certified this month.',
        invoiceRef: 'Invoice number',
        invoiceRefHelp: 'The number of the invoice that went out. It ties the certification to the paper.',
        confirm: {
          issued: {
            title: 'Issue certification no. {number}',
            message: 'It is issued for {amount} this month. From here on it cannot be changed: corrections go in the next one, to date.',
          },
          paid: {
            title: 'Mark no. {number} as paid',
            message: 'Confirm that {amount} has reached the bank for this certification.',
          },
        },
        done: { issued: 'Certification issued', invoiced: 'Marked as invoiced', paid: 'Marked as paid' },
        issuedAt: 'Issued on {date}', invoicedAt: 'Invoiced on {date} · {ref}', paidAt: 'Paid on {date}',
        download: 'Download PDF',
        downloadHelp: 'The printed certificate, for the site management and the owner. A draft prints with a DRAFT mark across it.',
        measured: {
          note: '{count} item was measured after the draft was prepared and has no line. | {count} items were measured after the draft was prepared and have no line.',
          add: 'Add what has been measured since',
          title: 'Measured since it was prepared',
          help: 'Items with progress that had not been measured when the draft was prepared. They come in at the progress to date and can then be lowered like any other line. Adding them saves the lines, with any changes you have made.',
          confirm: 'Add {count} item | Add {count} items',
          added: '{count} item added | {count} items added',
        },
        pdf: {
          period: 'Period', date: 'Date', work: 'Work', code: 'Code',
          price: 'Price', currentAmount: 'Amount this month', chapterTotal: 'Chapter total',
          noVat: 'Amounts exclude VAT.', notes: 'Notes',
          signContractor: 'The contractor', signDirection: 'Site management',
          signAgreed: 'Agreed', signName: 'Name and signature', signDate: 'Date',
        },
      },
      variations: {
        new: 'New variation',
        newHelp: 'It starts as proposed and does not add to the sale until it is approved. Approval and work stage are handled from its page.',
        create: 'Propose',
        created: 'Variation proposed',
        chapter: 'Chapter',
        noChapter: 'No chapter',
        kind: 'Item type',
        customerRef: 'Customer reference',
        customerRefHint: 'The order or number the owner or the architect knows it by.',
        reason: 'Reason',
        reasonPlaceholder: 'Why this work comes up',
        what: 'Variation', approval: 'Approval', stage: 'Work',
        approved: 'Approved and sold',
        approvedHelp: 'Extras the customer has accepted. Already counted in the sale of the work.',
        open: 'Asked and unanswered',
        openHelp: 'Proposed or sent to the site engineer. Not counted in the sale yet.',
        atRisk: 'Executed unapproved',
        atRiskHelp: 'Work already done, already costing money, that nobody has approved.',
        atRiskNote: '{count} variation executed unapproved, with {cost} of cost already inside the forecast. | {count} variations executed unapproved, with {cost} of cost already inside the forecast.',
        empty: { title: 'No variations', subtitle: 'Everything being built was in the project.' },
      },
      costKind: {
        incurred: 'Spent',
        committed: 'Committed',
      },
      lineType: {
        measurement: 'Measurement',
        subtotal_partial: 'Subtotal',
        subtotal_running: 'Running',
        expression: 'Formula',
      },
      historyWhat: {
        created: 'Created',
        progress: 'Measurement',
        target: 'Target',
        status: 'Status',
        cancelled: 'Cancelled',
        restored: 'Restored',
        edited: 'Edited',
        stage: 'Stage',
        copied: 'Copied to the revision',
        assigned: 'Into a package',
        unassigned: 'Out of a package',
        packages: 'Package split',
        pending: 'Pending set by hand',
      },
      historyField: {
        code: 'code',
        description: 'description',
        label: 'summary',
        longText: 'long text',
        unit: 'unit',
        kind: 'kind',
        saleQuantity: 'sale quantity',
        salePrice: 'sale price',
        targetQuantity: 'target quantity',
        targetPrice: 'target price',
        pendingOverride: 'pending',
      },
      historyExtra: {
        automatic: 'automatic',
        packagesCount: '{count} package | {count} packages',
        copiedDetail: 'New budget revision',
      },

      title: 'Works',

      related: { forCustomer: 'Works' },

      help: {
        title: 'Works',
        subtitle: 'From the bill of quantities to the forecast at completion: target cost, procurement packages, subcontracts and valuations',
      },

      scope: { only: 'Only jobs for {name}', showAll: 'Show all', oneCustomer: 'this customer' },
      subtitle: 'The budget of each work, chapter by chapter, against what it is costing',
      detailTitle: 'Work',
      search: { subtitle: 'Work' },
      dashboard: {
        works: 'Works under way',
        worksHelp: 'How many works are open today, not counting closed or cancelled ones.',
        production: 'Production to date',
        productionHelp: 'What has been built so far on the works under way, valued at sale price. Certified or not.',
        backlog: 'Backlog to build',
        backlogHelp: 'What has been sold and is still to be built on the works under way.',
        toCertify: 'Awaiting certification',
        toCertifyHelp: 'Production that has not yet gone into any certification to the customer.',
        receivable: 'Awaiting payment',
        receivableHelp: 'Certifications issued or invoiced and still unpaid, with the retention already deducted.',
        margin: 'Forecast margin',
        marginHelp: 'The margin expected at closing on the works under way, over sales and weighted by amount.',
        deviation: 'Deviation from target',
        deviationHelp: 'How much has been spent above the target allocated, across every work under way.',
        unassigned: 'Unassigned cost',
        unassignedHelp: 'Money charged to a work and still without a budget item. Until it is zero, the margin is incomplete.',
        supplierInvoices: 'Supplier invoices to pay',
        supplierInvoicesHelp: 'Received and unpaid from the site suppliers, tax included.',
        worksList: 'Works, one by one',
        worksListHelp: 'The works under way ordered by amount, with their progress and their margin.',
        deviated: 'Works over target',
        deviatedHelp: 'The works running above target, from the largest deviation down.',
      },
      actions: { new: 'New work', open: 'Open the work', import: 'Import BC3', more: 'Actions' },
      status: { draft: 'Draft', live: 'On site', closed: 'Closed', cancelled: 'Cancelled' },
      fields: {
        code: "Work code",
        manager: "Site manager",
        address: "Address", city: 'Town', postalCode: 'Postcode',
        labourRate: 'Hourly rate',
        labourRateHelp: 'What an hour of crew costs on this work. With it, the hours on a parte arrive priced; without it they arrive counted and with no amount.',
        name: 'Name of the work', customerName: 'Customer',
        fileNumber: 'Tender file', authority: 'Authority',
        tenderAmount: 'Tender amount', awardAmount: 'Award amount',
        lowCoefficient: 'Discount', lastData: 'Last data',
      },
      header: { collapse: 'Collapse', expand: 'Show the figures' },
      figures: {
        sale: 'Sale', saleHelp: 'What the customer is charged for this work, at contract price.',
        target: 'Target', targetHelp: 'What the company meant to spend building it. Cost is judged against this.',
        spent: 'Spent',
        real: 'Real', realHelp: 'Cost that has ALREADY happened: deliveries received, hours worked, plant used.',
        committed: 'Committed', committedHelp: 'Orders and contracts already approved that are not cost yet, but can no longer be avoided.',
        pending: 'Pending', pendingHelp: 'What is still expected to be spent to finish, with what is committed taken off.',
        forecast: 'Forecast', forecastHelp: 'Where the work ends up: real + committed + pending. No euro counted twice.',
        forecastMargin: 'Forecast margin', forecastMarginHelp: 'What will be left: sale minus forecast.',
        margin: 'Margin',
        notInvoiced: 'Not invoiced', notInvoicedHelp: 'Part of Real already executed whose supplier invoice has not arrived. Already inside Real.',
        unassigned: 'Unassigned', unassignedHelp: 'Money spent on this work that does not hang off any item yet.',
        progress: 'Built', consumed: 'Cost consumed',
        costAhead: 'Cost is running ahead of what has been built.',
      },

      alerts: {
        title: 'What to look at today', more: 'Show {count} more',
        work_over_target: 'the work will finish over target',
        work_negative_margin: 'the work will cost more than it was sold for',
        work_cost_ahead_of_progress: 'cost is running ahead of what is built',
        unassigned_significant: 'a lot of cost hangs off no item',
        unassigned_stale: 'costs have been waiting for weeks unassigned',
        item_over_target: 'will finish over its target',
        item_negative_margin: 'will be sold for less than it will cost',
        cost_ahead_of_progress: 'spends faster than it builds',
        item_near_target: 'has almost eaten its target and work is left',
        committed_without_progress: 'all ordered and not started',
        item_born_losing: 'its breakdown costs more than its target price',
        variation_executed_unapproved: 'executed and unapproved: it is being given away',
        variation_awaiting_approval: 'waiting on the customer',
      },

      closing: {
        against: 'Against a target of {target}:',
        marginInitial: 'Contract margin', marginInitialHelp: 'The one signed: sale minus target.',
        marginCurrent: 'Forecast margin',
        marginChange: 'What has gone', marginChangeHelp: 'The difference between the margin signed and the one forecast.',
        manual: '{count} of {items} items have their pending set by hand.',
        ranking: 'Where we are losing money',
        unexplained: '{amount} unexplained', noCauses: 'nobody has said why',
      },

      causes: {
        title: 'Why it is over',
        edit: 'Classify',
        help: 'Split the variance by cause. Whatever does not fit stays unexplained and is shown as such: forcing it into «Other» would be inventing an explanation.',
        what: 'Cause', amount: 'Amount', note: 'Detail',
        notePlaceholder: 'Required for «Other»',
        add: 'Add cause',
        sum: 'Explained {explained} of {variance}. Unexplained: {left}.',
        saved: 'Causes saved',
        unexplained: 'Unexplained',
        none: 'Nobody has said why it is over yet.',
        chargeable: '{amount} can be charged to the customer.',
        supplier_price: 'Supplier price', more_quantity: 'More measured',
        extra_hours: 'Extra hours', budget_error: 'Budget error',
        scope_change: 'Scope change', customer_variation: 'Customer variation',
        delay: 'Delay', rework: 'Rework', material_waste: 'Material waste',
        subcontract_price: 'Dearer subcontract', extra_plant: 'Extra plant',
        other: 'Other',
      },
      sort: { label: 'Sort by', variance: 'Biggest overrun', margin: 'Lowest margin', unassigned: 'Most unassigned', sale: 'Biggest job', name: 'Name' },
      stats: { works: 'Works' },
      list: { work: 'Work' },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      filters: { searchPlaceholder: 'Name, file, customer…', status: 'Status', allStatuses: 'All' },
      empty: {
        title: 'No works yet',
        subtitle: 'A work is a project with a budget broken into items. Open the first one and put its BC3 in.',
      },
      new: {
        codePlaceholder: "OBR-2026-014",
        customerPlaceholder: "Find the customer",
        managerPlaceholder: "Find the person",
        title: 'Open a work',
        help: "The work is the main record: its code, its customer and its site manager. The itemised budget goes in afterwards, with the BC3.",
      },
      nav: {
        works: 'Works',
        contracting: 'Contracting',
        subcontracts: 'Subcontracts',
        certifications: 'Certifications',
        prices: 'Works prices',
      },
      sections: {
        label: 'Sections of the work',
        budget: 'Works budget',
        contracting: 'Contracting',
        production: 'Production and billing',
        control: 'Control',
        daily: 'Day to day',
      },
      revisions: {
        name: 'Version',
        created: 'Created',
        current: 'Current',
        historical: 'Historical',
        viewing: 'The one you are looking at in the tree',
        view: 'Show in the tree',
        compare: 'Compare with the current one',
        noReport: 'This version did not come from a BC3: it was typed in by hand or copied from another one.',
      },
      across: {
        work: 'Work',
        allWorks: 'All works',
        allBuyers: 'Everyone',
        attention: 'What needs attention',
        warnings: 'Warnings',
        contracting: {
          title: 'Contracting',
          subtitle: 'The packages and bid comparisons of every work, against their target',
          count: 'Packages',
          emptyTitle: 'No packages with these filters',
          emptyHelp: 'Packages are created in each work, under «Contracting». This shows the ones from all of them.',
          alerts: {
            offers_expired: '{name} ({work}): {count} expired bid; the cheapest, {supplier} | {name} ({work}): {count} expired bids; the cheapest, {supplier}',
            offers_expiring: '{name} ({work}): {count} bid expires this week; the cheapest, {supplier} | {name} ({work}): {count} bids expire this week; the cheapest, {supplier}',
            awarded_over_target: '{name} ({work}) was awarded to {supplier} {pct} % above the target',
            tendering_no_offers: '{name} ({work}) is out to tender and has no bids',
          },
        },
        subcontracts: {
          title: 'Subcontracts',
          subtitle: 'Every contract of every work: what was contracted, what was certified and what is owed',
          count: 'Contracts',
          search: 'Company, code or scope…',
          pending: 'Certifications to approve',
          pendingHelp: 'Subcontractor certifications in draft or sent that nobody has approved yet.',
          pendingShort: 'To approve',
          pendingCount: '{count} to approve | {count} to approve',
          warnings: {
            late: 'Past its end date',
            over_scope: 'Over-certified',
          },
        },
        certifications: {
          title: 'Certifications',
          subtitle: 'The client certifications of every work: what to review, what to invoice and what to collect',
          count: 'Certifications',
          missingTitle: 'Works without this month’s certification',
          missingRow: '{work} · {name}: production not yet certified',
          bucket: 'Next step',
          allBuckets: 'All',
          bucketCount: '{count} certification | {count} certifications',
          nextLabel: 'Next step',
          buckets: {
            review: 'Drafts to review',
            to_invoice: 'Issued, to invoice',
            to_collect: 'Invoiced, to collect',
            done: 'Collected',
          },
          next: {
            review: 'Review and issue',
            to_invoice: 'Invoice',
            to_collect: 'Collect',
            done: 'Collected',
          },
        },
        prices: {
          title: 'Works prices',
          subtitle: 'What companies have bid and what was contracted, across every work',
          count: 'Prices',
          search: 'Description or code…',
          unitPlaceholder: 'm3, m2, kg…',
          supplier: 'Company',
          which: 'Which prices',
          allPrices: 'Bid and contracted',
          onlyAwarded: 'Contracted only',
          mixedUnits: 'These prices are in several units. Type a unit to see the average, the lowest and the latest price.',
          mean: 'Average price',
          min: 'Lowest',
          max: 'Highest',
          last: 'Latest',
          lastAwarded: 'Latest contracted',
          fromWhere: '{supplier} · {work} · {date}',
          awarded: 'Contracted',
          origin: 'Work and comparison',
          date: 'Date',
          emptyTitle: 'No prices with these filters',
          emptyHelp: 'Every bid entered in a comparison leaves its prices here, line by line.',
          onlySupplier: 'Only the prices of {name}',
          oneSupplier: 'this supplier',
        },
      },
      supplierPanel: {
        title: 'On works',
        awards: 'Awards',
        offers: 'Bids in comparisons',
        subcontracts: 'Subcontracts',
        prices: 'Price history',
        pricesRow: 'Their prices in the works bid comparisons',
      },
      voidCost: {
        action: 'Void',
        title: 'Void this cost',
        help: '«{description}», {amount}. It disappears from the job and from its item; the reason stays in the activity log.',
        reason: 'Reason',
        reasonPlaceholder: 'E.g.: typed in twice',
        confirm: 'Void the cost',
        done: 'Cost voided.',
      },
      tabs: {
        tree: 'Items',
        packages: 'Packages',
        variations: 'Variations and extras',
        versions: 'Revisions',
        subcontracts: 'Subcontracts',
        certifications: 'Certifications',
        invoices: 'Invoices received',
        closing: 'Forecast to completion',
        unassigned: 'Unallocated costs',
        indirect: 'Site overheads',
        report: 'The import',
      },
      packages: {
        title: 'Procurement packages',
        newPackage: 'New package',
        newHelp: 'A package is the unit you BUY against: it groups items to ask a supplier for a price. It is created empty and items are assigned to it afterwards.',
        createPackage: 'Create package',
        newName: 'New package',
        buyer: 'Buyer',
        buyerPlaceholder: 'Search for someone in the company',
        empty: {
          title: 'No procurement packages',
          subtitle: 'A package is what gets contracted in one go: "structure" is the concrete, the steel and the formwork of twelve items. Without packages there is no available balance, and without it nobody knows whether an order fits the budget.',
        },
        type: {
          supply: 'Supply',
          subcontract: 'Subcontract',
          hire: 'Hire',
          service: 'Service',
          own_means: 'Own means',
        },
        columns: {
          package: 'Package',
          type: 'Type',
          target: 'Target',
          available: 'Available',
        },
        availableHelp: 'Target less what is committed and spent. What is left to buy with.',
        itemCount: '{count} item | {count} items',
        itemsTitle: 'What it groups',
        noItems: 'It groups no items yet, so it has no target to buy against.',
        addItems: 'Add items',
        ctc: 'Left to spend',
        ctcValue: 'Set by hand',
        ctcHint: 'Leave it empty and the engine proposes {amount}.',
        ctcReason: 'Why',
        ctcReasonPlaceholder: 'E.g. the item is finished, it will spend no more',
        ctcManual: 'This package forecasts with your figure, not the calculated one.',
        calculatedHelp: 'Sum of the {count} items it groups: {amount}.',
        manualAdjust: 'Manual adjustment',
        manualAdjustHelp: 'What the site manager knows and the tree does not say. Always with a reason.',
        reason: 'Reason for the adjustment',
        removeTitle: 'Delete this package?',
        removeHelp: 'Only one with no purchases is deleted. Once it has orders or spend it is CLOSED instead.',
        alerts: {
          over_committed: '{name} is committed {amount} beyond what it had.',
          forecast_over: '{name} is forecast to close {amount} over its target.',
          suspicious_ctc: '{name} has nothing committed and nothing left to spend, and the work is not done.',
          no_target: 'Money is being spent on {name} with no target behind it.',
          own_means_committed: '{name} is an own-means package and has {amount} committed: either the order is on the wrong package, or the package was not own means.',
          sub_over_scope: '{supplier} has {certified} {unit} of {item} certified and its package, {name}, gives it {scope}. What is not theirs is being paid.',
          quantity_over_measurement: 'On {item} the packages claim {claimed} {unit} and the measurement is {measurement}. Somebody has to decide who goes without the missing ones.',
          offers_expired: '{name} is out to tender with {count} expired offer: the cheapest, {supplier} | {name} is out to tender with {count} expired offers: the cheapest, {supplier}',
          awarded_over_target: '{name} was awarded to {supplier} {pct} % above target.',
          awarded_incomplete: '{name} was awarded to {supplier} leaving {count} line uncontracted. | {name} was awarded to {supplier} leaving {count} lines uncontracted.',
        },
        status: 'Status',
        statusOf: {
          open: 'Open',
          tendering: 'Out to tender',
          awarded: 'Awarded',
          closed: 'Closed',
        },
        typeHelp: {
          supply: 'It will be bought with an order. Commits when the order is approved.',
          hire: 'It will be bought with an order. Commits when the order is approved.',
          service: 'It will be bought with an order. Commits when the order is approved.',
          subcontract: 'It will be contracted with a ceiling, retention and certificates.',
          own_means: 'It is not bought. It is tracked by output from the day sheets.',
        },
        history: {
          title: 'What has happened to it',
          created: 'Created',
          edited: 'Edited',
          committed: 'Moved to «{to}» on its own when {reference} arrived for {amount}',
          status: 'Moved from «{from}» to «{to}»',
          other: 'Change to the package',
          assigned: 'What it groups changed',
          tender: 'Activity in its bid comparison',
        },
      },
      coverage: {
        target: 'Target cost of the work',
        covered: 'Covered by packages',
        pct: '{pct} % of the target',
        unpackaged: 'Unpackaged',
        orphans: 'See the loose item · {amount} | See the {count} loose items · {amount}',
        approve: 'Approve the target',
        approveTitle: 'Approve the target of this work?',
        approveHelp: 'From here on, deviations are measured against this line and the split across packages is frozen. It can be lifted later, with a reason.',
        approved: 'Target approved.',
        approvedBy: 'Approved by {who} on {when}',
        cannotApprove: 'It cannot be approved while target cost sits outside every package: that money has no available balance, so no order against it can be compared with anything.',
        blocked: 'The target is approved: the split cannot be changed. Lift the approval to change it.',
        revoke: 'Lift the approval',
        revokeTitle: 'Lift the target approval',
        revokeHelp: 'The split can be changed again. Who lifted it and why is written down.',
        revokeReason: 'Why it is being lifted',
      },
      picker: {
        title: 'What {name} groups',
        search: 'Search by code or concept',
        onlyFree: 'Only with a balance',
        natureHint: 'The lines of that nature are added, never the whole item.',
        claimed: 'Assigned',
        free: 'Available',
        assigned: 'Assigned',
        noBreakdown: 'no breakdown',
        takenBy: 'Taken by: {packages}',
        allChapters: 'All chapters',
        allNatures: 'All natures',
        amountLabel: 'Amount (at most {max})',
        amountReason: 'Why this amount',
        amountReasonPlaceholder: 'e.g. material only; the pump comes with the subcontractor',
        amountConfirm: 'Add with this amount',
        freeQuantity: 'Free: {free} {unit} of {total}',
        byQuantity: 'By quantity',
        byAmount: 'By amount',
        quantityLabel: 'Quantity in {unit} (at most {max})',
        quantityHint: '{pct} % of the measurement · {amount}',
        quantityReason: 'Why this quantity',
        quantityReasonPlaceholder: 'E.g.: north stretch, chainage 0+000 to 0+400',
        quantityConfirm: 'Add with this quantity',
        quantityCol: 'Quantity',
        tally: '{lines} from {items} · {amount}',
        tallyLines: '0 lines | {count} line | {count} lines',
        tallyItems: '0 items | {count} item | {count} items',
        pickAll: 'Select everything filtered',
        clear: 'Clear the selection',
        add: 'Add {count}',
        added: '{count} assignments added.',
        stale: 'review',
        origin: 'Where it comes from',
        mode: {
          whole: 'Whole item',
          lines: 'Line',
          pct: 'Percentage',
          amount: 'Amount',
          quantity: 'Quantity',
        },
        dropTitle: 'Take {code} out of the package?',
        dropHelp: 'The item keeps its whole target; what is removed is what this package claimed of it.',
        dropReason: 'Why it is being removed',
        dropReasonHelp: 'This package has already bought: taking an item out lowers its target without lowering its spend, so its available balance narrows at once.',
        none: {
          title: 'No items match',
          subtitle: 'Try clearing the nature or chapter filter. If the work has no budget yet, there is nothing to package.',
        },
      },
      indirect: {
        templates: {
          'IND-01': "Site manager",
          'IND-02': "Foreman",
          'IND-03': "Site administrator",
          'IND-10': "Site cabins",
          'IND-11': "Temporary fencing",
          'IND-12': "Temporary services",
          'IND-13': "Shared plant and access",
          'IND-20': "Electricity",
          'IND-21': "Water",
          'IND-22': "Internet and phones",
          'IND-30': "Cleaning",
          'IND-31': "Security",
          'IND-32': "Health and safety",
          'IND-33': "Waste management",
          'IND-34': "Site insurance",
          'IND-40': "Site vehicles",
          'IND-41': "Fuel",
          'IND-42': "Travel and subsistence",
          'IND-90': "General hire",
          'IND-91': "Small consumables",
          'IND-92': "Other site overheads",
        },
        title: 'Site overheads',
        subtitle: 'What it costs to keep the job open, and belongs to no item.',
        empty: {
          title: 'No overhead budget',
          subtitle: 'The site manager, the cabin, security and power cost money every month the job stays open. Without budgeting them, the job claims a margin it does not have.',
        },
        fromTemplate: 'Start from a template',
        templateKind: 'Type of job',
        clear: 'Empty the block',
        restart: 'Start over',
        restartTitle: 'Start over?',
        restartConfirm: 'The {count} concepts go and the template manager opens to pick another one. Anything already carrying cost stays.',
        clearTitle: 'Empty the site overheads?',
        clearConfirm: 'The {count} concepts of this work go. Any that already carry cost stay: that is not deleted, it is closed.',
        cleared: 'Emptied: {count} concepts gone',
        clearedSome: '{count} gone. These stay, because they already carry cost: {kept}',
        removeTitle: 'Remove the concept?',
        removeConfirm: '"{name}" goes from the overhead budget. If it already carries cost it will refuse: close it instead of removing it.',
        fullscreenHint: 'Site overheads full screen · Escape to leave',
        newConcept: 'Add a concept',
        created: 'Budget created with {count} concepts. Give them amounts.',
        kind: {
          building: 'Building',
          industrial: 'Industrial unit',
          refurbishment: 'Refurbishment',
          civil: 'Civil works',
          fit_out: 'Fit-out',
        },
        category: {
          personnel: 'Site staff',
          facilities: 'Temporary facilities',
          utilities: 'Utilities',
          services: 'General services',
          vehicles: 'Vehicles and travel',
          other: 'Other',
        },
        behaviour: {
          label: 'Behaviour',
          help: 'Decides how what is left to spend is estimated.',
          fixed: 'One-off',
          monthly: 'Monthly',
          weekly: 'Weekly',
          daily: 'Daily',
          variable: 'Variable',
          manual: 'Manual',
        },
        rate: 'Cost per period',
        rateHelp: 'What it costs each month, week or day.',
        durationSensitive: 'Depends on the programme',
        durationSensitiveHelp: 'Whether one more month of work makes it cost more.',
        closed: 'Closed',
        closedHelp: 'It will not cost any more.',
        columns: {
          concept: 'Concept',
          type: 'Type',
          target: 'Target',
          real: 'Actual',
          committed: 'Committed',
          pending: 'Left to spend',
          forecast: 'Forecast',
          deviation: 'Variance',
          consumed: '% used',
        },
        totals: {
          target: 'Target',
          real: 'Actual',
          committed: 'Committed',
          pending: 'Left to spend',
          forecast: 'Forecast',
          deviation: 'Variance',
        },
        duration: {
          title: 'The programme',
          planned: 'Planned finish',
          forecast: 'Forecast finish',
          extra: '{days} days over',
          perMonth: '{amount} a month depends on the programme',
          impact: 'Extra cost from the delay',
          shift: 'Move the forecast finish',
          shiftTitle: 'Move the forecast finish for the whole job?',
          shiftHelp: 'It moves on the {count} concepts that depend on the programme.',
          shiftReason: 'Why it is late',
          moved: '{count} concepts moved.',
          onTime: 'The job is on programme.',
        },
        deviations: { title: 'Biggest variances', empty: 'No concept is over its target.' },
        evolution: { title: 'Month by month', real: 'Actual', target: 'Target, spread', linear: 'The target is spread evenly across the programme.' },
        movements: { title: 'Where it comes from', empty: 'Nothing has been booked against it yet.', origin: 'Origin', date: 'Date', amount: 'Amount' },
        identity: 'Identification',
        family: 'Family',
        budget: 'Budget',
        period: 'Period',
        startDate: 'Planned start',
        endDate: 'Planned finish',
        actualStart: 'Actual start',
        forecastEnd: 'Forecast finish',
        initialTarget: 'Original budget',
        pendingOverride: 'Left to spend, by hand',
        pendingOverrideHelp: 'When a person says what is left rather than the calculation.',
        removeTitle: 'Delete this concept?',
        removeHelp: 'Only one with no movements is deleted. Once it has cost booked against it, it is CLOSED instead.',
        alerts: {
          duration_overrun: 'The {days}-day delay adds {amount} in overheads.',
          over_budget: '{name} is {pct} % over its target.',
          forecast_over: '{name} is forecast to close {amount} over its target.',
          past_due: '{name} is still costing money {days} days after its planned finish.',
          no_budget: 'Cost has been booked to {name} with no budget behind it.',
          unbooked_periods: '{name} has been running for {periods} periods and {amount} is still unbooked.',
        },
        unassignedHint: 'There is {count} unallocated movement on this job. It may be an overhead. | There are {count} unallocated movements on this job. Some may be overheads.',
      },
      versions: {
        remove: 'Remove this version',
        removeTitle: 'Remove the version?',
        removeConfirm: '"{name}" goes, with its chapters and items, and this cannot be undone. Only possible if it has no measurement, no cost and no certificate behind it.',
        removed: 'Version removed ({chapters} chapters, {items} items)',
        label: 'Budget version', historical: 'historical',
        newRevision: 'New revision',
        newRevisionTitle: 'Create a revision of the budget?',
        newRevisionHelp: 'The whole budget is copied and the work moves to the copy: measurements, costs and certificates move with it, so the next certificate is still the difference with the previous one. The current version stays for reference.',
        created: 'Revision created with {chapters} chapters and {items} items.',
        compare: 'Compare',
        compareTitle: 'What changed',
        added: 'Added', removed: 'Removed', changed: 'Changed',
        delta: 'Moves the sale by',
        deltaHelp: 'What comes in minus what goes out, plus what changed in price or quantity.',
        historicalNote: 'You are looking at an old version: read only.',
        same: { title: 'No differences', subtitle: 'Both versions say the same thing.' },
        what: { added: 'Added', removed: 'Removed', changed: 'Changed' },
        field: { description: 'Description', unit: 'Unit', saleQuantity: 'Quantity', salePrice: 'Price' },
      },
      editor: {
        edit: 'Edit chapters and items',
        done: 'Stop editing',
        hint: 'Saved on its own when you leave each box.',
        addChapter: 'Add chapter',
        addSubchapter: 'Add a chapter inside',
        addItem: 'Add an item to this chapter',
        chapter: 'chapter',
        item: 'item',
        unit: 'Unit',
        quantity: 'Quantity',
        price: 'Price',
        amount: 'Amount',
        newChapter: 'New chapter',
        newItem: 'New item',
        openBudget: 'Write the budget by hand',
        budgetOpened: 'Budget opened. Start with a chapter.',
        removeTitle: 'Delete from the budget?',
        removeChapterHelp: 'Only an empty chapter can be deleted. If it still has items inside, move them out or cancel them first.',
        removeItemHelp: 'Only an item with nothing recorded against it is deleted. Once it has measurement, cost or a certificate, it is cancelled with a reason and stays in the history.',
      },
      tree: {
        fullscreen: 'Expand',
        exitFullscreen: 'Exit',
        fullscreenHint: 'Item control full screen · Escape to leave',
        concept: 'Concept', quantity: 'Measurement', target: 'Target',
        spent: 'Spent', progress: 'Built', consumed: 'Consumed',
        deviation: 'Deviation',
        empty: {
          title: 'This work has no budget yet',
          subtitle: 'Import the BC3 the estimating office sent and the outline appears here.',
        },
      },
      itemKind: { normal: 'Normal', lump_sum: 'Lump sum', lump_sum_justified: 'To be justified' },
      origin: { bc3: 'From the BC3', variation: 'Variation', manual: 'By hand' },
      nature: { labour: 'Labour', plant: 'Plant', material: 'Material', subcontract: 'Subcontract', other: 'Other' },
      quiet: { days: '{days} days ago', today: 'Today', never: 'None yet' },
      unassigned: {
        assignTo: 'Put on the item',
        pickItem: 'Find the item',
        assign: 'Assign {count}',
        assigned: '{count} entries put on their item',
        help: "The work is the file itself: its code, its customer and its site manager. The budget goes in afterwards, with the BC3.",
        date: 'Date', description: 'Concept', nature: 'Nature', origin: 'Where from', amount: 'Amount',
        empty: { title: 'Nothing is unassigned', subtitle: 'Every cost on this work hangs off its item.' },
      },
      import: {
        title: 'Import a budget',
        help: 'A BC3 file (FIEBDC-3), the one Presto, Arquímedes or Menfis export. It replaces the outline of the version rather than merging into it.',
        file: 'The file', versionName: 'Name of the version',
        versionHelp: 'Left empty, the title inside the file is used.',
        kind: 'What it is', original: 'Original project', amendment: 'Amendment',
        charset: 'Character set', charsetHelp: 'Leave it automatic. Only touched when the accents come out wrong.',
        charsetAuto: 'Whatever the file says',
        done: '{items} items came in, worth {total}',
      },
      report: {
        chapters: 'Chapters', items: 'Items', charset: 'Characters', program: 'Program',
        anomaly: 'What happened', count: 'How many',
        clean: 'The file came in clean: nothing to report.',
        empty: { title: 'Nothing imported yet', subtitle: 'When a BC3 comes in, what it carried and what it lacked is written here.' },
      },
      anomaly: {
        CHARSET_NOT_DECLARED: 'The file does not say what character set it is in; CP850 was assumed',
        CHARSET_UNKNOWN: 'The file names a character set we do not know',
        CHARSET_IS_UTF8: 'The file is UTF-8 whatever it declares',
        PREAMBLE_IGNORED: 'There was text before the first record',
        RECORD_HEADER_INVALID: 'A record with no valid header',
        RECORD_TYPE_UNSUPPORTED: 'Records this reader does not interpret yet',
        FORMAT_VERSION_UNKNOWN: 'Unknown format version',
        DECIMALS_NOT_DECLARED: 'No decimals record; the format defaults are used',
        ROOT_NOT_FOUND: 'The file has no root',
        MULTIPLE_ROOTS: 'The file has more than one root',
        CHAPTER_EMPTY: 'Chapters with nothing in them',
        DECOMPOSITION_CYCLE: 'A breakdown that calls itself',
        DECOMPOSITION_TOO_DEEP: 'A breakdown nested too deep',
        TREE_TOO_LARGE: 'A tree too large to be a budget',
        FILE_TOO_LARGE: 'A file too large to read',
        CONCEPT_NOT_DECLARED: 'Concepts used and never declared',
        CONCEPT_REDECLARED: 'Concepts declared twice',
        CONCEPT_WITHOUT_PRICE: 'Concepts with no price',
        CONCEPT_WITHOUT_MEASUREMENT: 'Items with no measurement',
        CONCEPT_WITHOUT_DECOMPOSITION: 'Items with no breakdown',
        PRICE_DISAGREES_WITH_DECOMPOSITION: 'The price does not match its own breakdown',
        MEASUREMENT_TOTAL_MISMATCH: 'The measurement does not match its own lines',
        MEASUREMENT_EXPRESSION_INVALID: 'A measurement formula that is not one',
        MEASUREMENT_EXPRESSION_FAILED: 'A measurement formula that could not be evaluated',
        MEASUREMENT_WITHOUT_TARGET: 'Measurements that do not say what they are for',
      },
    },
  },

  fr: {
    // El nombre de cada permiso en la pantalla de roles. Sin esto salía el
    // código crudo («construction:margins») a quien decide quién ve el dinero.
    permissions: {
      construction: {
        read: 'Voir les chantiers, leur budget et leur avancement',
        write: 'Créer des chantiers, saisir les métrés et affecter les coûts aux postes',
        import: 'Charger et modifier le budget (BC3 ou à la main)',
        margins: 'Voir et gérer l’argent : objectif, coût, marge, lots et sous-traitances',
        award: 'Attribuer des lots depuis leur comparatif d’offres : signe la sous-traitance ou l’engagement',
      },
    },
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { construction: 'Chantiers' },
    // Las aperturas del asistente: la clave es el id estable de
    // backend/assistant.js; el texto inglés de allí es solo el fallback.
    assistant: {
      openers: {
        construction: {
          'how-are-the-works': 'Où en sont les chantiers ?',
          'what-is-deviating': "Qu'est-ce qui dérape sur les chantiers ?",
          'certifications-pending': 'Quelles situations de travaux restent en attente ?',
        },
      },
    },
    audit: { actions: { construction: { cost_voided: 'Écriture de coût annulée' } } },
    construction: {
      // El comparativo de ofertas de un paquete (0.25.0).
      tenders: {
        title: 'Comparatif des offres',
        back: 'Aux lots',
        pageTitle: 'Comparatif · {code} {name}',
        pageSubtitle: 'Comparatif n° {number} · {count} ligne | Comparatif n° {number} · {count} lignes',
        print: 'Imprimer le PDF',
        template: 'Modèle pour les entreprises',
        addOffer: 'Ajouter une offre',
        editOffer: 'Modifier l’offre',
        status: {
          open: 'En consultation',
          awarded: 'Attribué',
          cancelled: 'Clos sans attribution',
        },
        dueOn: 'Offres jusqu’au {date}',
        frozen: 'Figé le {date} : tel qu’on l’a vu à l’attribution',
        target: 'Objectif',
        offersCount: 'Offres',
        bestComplete: 'Meilleure offre complète',
        vsTarget: 'Par rapport à l’objectif',
        linesChanged: 'Le lot a changé depuis l’ouverture du comparatif : ses lignes ne sont plus celles qui ont été demandées.',
        refreshLines: 'Mettre à jour les lignes',
        linesRefreshed: 'Lignes mises à jour. Les prix de chaque entreprise sont conservés par article.',
        ruleLabel: 'Les lignes non chiffrées sont complétées avec',
        rule: {
          highest: 'Le prix le plus élevé proposé',
          target: 'L’objectif',
        },
        ruleHelp: {
          highest: 'Homogénéisé : chaque ligne qu’une entreprise ne chiffre pas est complétée avec le prix le plus élevé proposé par les autres (ou l’objectif si personne ne l’a chiffrée). C’est la règle prudente : cette ligne, vous finirez par la contracter, et elle ne sera pas bon marché.',
          target: 'Homogénéisé : chaque ligne qu’une entreprise ne chiffre pas est complétée au prix objectif de la ligne.',
        },
        awardedTo: 'Attribué à {supplier}',
        awardedBy: 'Par {who} le {date}',
        wasRecommended: 'c’était l’offre recommandée',
        wasNotRecommended: 'la recommandée était {supplier}',
        reason: 'Motif',
        seeSubcontract: 'Voir le contrat de sous-traitance créé',
        orderNote: 'Engagement inscrit sur le lot. Si les achats sont installés, la commande reste en brouillon avec ces lignes et ces prix : c’est l’acheteur qui l’envoie.',
        cancelAward: 'Annuler l’attribution',
        cancelAwardHelp: 'Pour quand l’entreprise se retire avant de commencer. Le comparatif est rouvert avec ses offres, la sous-traitance est annulée (si rien ne lui a été certifié) et l’engagement est levé. L’attribution annulée reste dans l’historique.',
        cancelAwardWithdraw: 'L’entreprise se retire : son offre cesse de concourir',
        awardCancelled: 'Attribution annulée. Le comparatif est de nouveau ouvert.',
        recommended: 'Recommandée : {supplier}',
        recommendedShort: 'Recommandée',
        basis: {
          cheapest_complete: 'C’est l’offre complète et valide la moins chère, lignes homogénéisées comprises.',
          cheapest_complete_expired: 'C’est la complète la moins chère, mais toutes les complètes ont expiré : demandez à l’entreprise de maintenir son prix avant d’attribuer.',
          no_complete: 'Aucune offre n’est complète : c’est la moins chère homogénéisée. Demandez un prix pour ce qui manque avant d’attribuer.',
        },
        belowTarget: 'Elle est {amount} ({pct}) sous l’objectif.',
        aboveTarget: 'Elle est {amount} ({pct}) au-dessus de l’objectif.',
        runnerUp: 'La suivante est {supplier}, {amount} plus chère.',
        cheaperIncomplete: '{supplier} propose {offered}, mais laisse {count} ligne sans prix : homogénéisée, elle revient à {homogenized}. | {supplier} propose {offered}, mais laisse {count} lignes sans prix : homogénéisée, elle revient à {homogenized}.',
        cheaperExpired: '{supplier} serait moins chère ({amount}) mais son offre a expiré : demandez-lui de la renouveler.',
        awardRecommended: 'Attribuer à la recommandée',
        emptyTitle: 'Pas encore d’offres',
        emptyHelp: 'Téléchargez le modèle, envoyez-le à trois ou quatre entreprises et saisissez ici leurs prix : à la main ou en lisant leur tableur.',
        offerStatus: {
          received: 'Reçue',
          awarded: 'Attribuée',
          not_awarded: 'Non retenue',
          withdrawn: 'Retirée',
        },
        expired: 'Expirée',
        missingCount: '{count} ligne sans prix | {count} lignes sans prix',
        offered: 'Proposé',
        homogenized: 'Homogénéisé',
        bestLines: 'Lignes au meilleur prix',
        reference: 'N° d’offre',
        validUntil: 'Valable jusqu’au',
        executionDays: 'Délai (jours)',
        days: '{count} jour | {count} jours',
        paymentTerms: 'Conditions de paiement',
        paymentTermsPlaceholder: 'Ex. : 60 jours date de facture',
        retentionPct: 'Retenue (%)',
        discountPct: 'Remise globale (%)',
        discountHelp: 'Sur ce qu’elle propose, pas sur les lignes complétées.',
        discountShort: 'remise {pct} %',
        totalDeclared: 'Total écrit par l’entreprise',
        totalDeclaredHelp: 'Celui de son offre. S’il ne correspond pas à ses prix, on vous le signale.',
        declaredMismatch: 'Son offre indique {declared} et ses prix totalisent {computed} : vérifiez ce qui a été mal saisi.',
        strongCount: '{count} prix s’écarte fortement de la moyenne : vérifiez unité et quantité. | {count} prix s’écartent fortement de la moyenne : vérifiez unités et quantités.',
        exclusions: 'Non compris',
        exclusionsHelp: 'Ce que l’offre laisse de côté. Une par ligne, avec Entrée.',
        exclusionsPlaceholder: 'Ex. : réfection de chaussée',
        notes: 'Notes',
        documents: 'Aucun document | 1 document | {count} documents',
        documentsTitle: 'Documents de l’offre de {supplier}',
        withdrawOffer: 'Retirer',
        restoreOffer: 'La reprendre en compte',
        // «Retirer» es ya retirar la oferta (sigue constando); quitarla del todo
        // necesita otra palabra, y en el mismo menú no pueden decir lo mismo.
        removeOffer: 'Supprimer',
        withdrawHelp: 'L’entreprise se retire. L’offre reste dans le comparatif mais ne concourt plus et ne marque plus de meilleurs prix.',
        restoreHelp: 'L’offre concourt de nouveau avec les autres.',
        removeOfferTitle: 'Supprimer l’offre ?',
        removeOfferHelp: 'L’offre de {supplier} est supprimée avec ses prix et ses documents. Si l’entreprise s’est retirée, mieux vaut « Retirer » : ainsi cela reste consigné.',
        award: 'Attribuer',
        awardTitle: 'Attribuer le lot',
        awardLead: 'Attribuer à {supplier} pour {amount}.',
        awardSubcontractHelp: 'Un contrat de sous-traitance lié à ce lot est créé, au montant de l’offre, avec {retention} % de retenue et {days} jours de délai. Son périmètre est lu depuis le lot.',
        awardOrderHelp: 'Un engagement de {amount} est inscrit sur le lot, et les achats laissent la commande en brouillon avec ces lignes. Chaque commande confirmée à cette entreprise sur ce lot vient en déduction de l’engagement.',
        awardIncomplete: 'Cette offre laisse {count} ligne sans prix, d’une valeur de {amount} à l’objectif : il faudra la contracter à part. | Cette offre laisse {count} lignes sans prix, d’une valeur de {amount} à l’objectif : il faudra les contracter à part.',
        signedLabel: 'Signature',
        signedHelp: 'La laisser signée (engage son montant dès aujourd’hui)',
        reasonRequired: 'Motif (obligatoire : la recommandée est {supplier})',
        reasonOptional: 'Motif (facultatif)',
        awarded: 'Lot attribué. Le comparatif est figé.',
        tableTitle: 'Tableau comparatif',
        viewLabel: 'Voir',
        view: {
          table: 'Tableau',
          lines: 'Par ligne',
          offers: 'Par offre',
        },
        line: 'Ligne',
        unitPrice: 'Prix',
        amount: 'Montant',
        pastPrices: 'Prix d’autres chantiers',
        notOffered: 'Non chiffrée',
        filledWith: 'complétée à {amount}',
        lastShort: 'dern. {price}',
        lastLong: 'La dernière fois elle a proposé {price} ({work}, {date})',
        notOfferedLines: 'Lignes non chiffrées',
        targetShort: 'objectif {price} · {amount}',
        best: 'Meilleur',
        pickOffer: 'Offre',
        historyTitle: 'Historique du comparatif',
        cancelTender: 'Clore sans attribuer',
        cancelTenderHelp: 'Le lot repasse à « Ouvert ». Les prix restent dans l’historique : c’étaient de vraies offres.',
        offerDate: 'Date de l’offre',
        pricesTitle: 'Prix par ligne',
        pricesHelp: 'Vide signifie « non chiffrée », et c’est ainsi que cela apparaît dans le tableau. Un 0 est un prix : la ligne est incluse dans une autre.',
        importSheet: 'Lire son tableur',
        reading: 'Lecture…',
        sheetRead: '{file} · feuille « {sheet} » : {priced} lignes chiffrées sur {lines}. Vérifiez et enregistrez.',
        sheetMissing: '{count} ligne n’apparaît pas dans sa feuille : elle reste non chiffrée. | {count} lignes n’apparaissent pas dans sa feuille : elles restent non chiffrées.',
        sheetQuantity: '{code} : elle a chiffré {offered} {unit} là où l’on en demandait {asked}. Son total n’est pas comparable tant que ce n’est pas clarifié.',
        sheetUnmatched: 'Ligne {row} sans correspondance : {text}',
        draftTotal: 'Proposé : {amount} · aucune ligne sans prix | Proposé : {amount} · {count} ligne sans prix | Proposé : {amount} · {count} lignes sans prix',
        offerSaved: 'Offre enregistrée',
        history: {
          opened: 'Comparatif ouvert avec {count} lignes',
          edited: 'Règle ou date modifiée',
          lines_refreshed: 'Lignes mises à jour depuis le lot ({count})',
          cancelled: 'Clos sans attribution : {comment}',
          offer_added: 'Offre de {supplier}',
          offer_revised: 'Offre de {supplier} révisée',
          offer_withdrawn: '{supplier} retire son offre {comment}',
          offer_restored: 'L’offre de {supplier} concourt de nouveau',
          offer_removed: 'Offre de {supplier} supprimée',
          awarded: 'Attribué à {supplier} pour {amount} {comment}',
          award_cancelled: 'Attribution à {supplier} annulée : {comment}',
        },
        templateLabels: {
          code: 'Code',
          description: 'Désignation',
          unit: 'U',
          quantity: 'Quantité',
          price: 'Prix unitaire',
          amount: 'Montant',
          total: 'TOTAL',
        },
        pdf: {
          title: 'Comparatif des offres',
          work: 'Chantier',
          package: 'Lot',
          date: 'Date',
          code: 'Code',
          description: 'Désignation',
          unit: 'U',
          quantity: 'Qté',
          target: 'Objectif',
          unitPrice: 'Prix',
          amount: 'Montant',
          notOffered: 'non chiffrée',
          offered: 'Proposé',
          homogenized: 'Homogénéisé',
          vsTarget: 'Par rapport à l’objectif',
          missing: 'Lignes non chiffrées',
          ruleHighest: 'Les lignes non chiffrées sont complétées avec le prix le plus élevé proposé par les autres entreprises (ou l’objectif si personne ne les a chiffrées).',
          ruleTarget: 'Les lignes non chiffrées sont complétées au prix objectif.',
          recommended: 'Recommandée',
          awardedTo: 'Attribué à',
          reason: 'Motif',
          best: 'Meilleur prix',
          frozen: 'Archive figée de l’attribution',
          validUntil: 'valable jusqu’au',
          expired: 'expirée',
          withdrawn: 'retirée',
          exclusions: 'Non compris',
          discount: 'Remise',
        },
        rowOpen: 'en consultation, sans offre | en consultation, 1 offre | en consultation, {count} offres',
        rowAwarded: 'attribué à {supplier}',
        panelAwarded: 'Attribué à {supplier} pour {amount}. Le comparatif reste comme archive.',
        panelOpen: 'En consultation, encore sans offre. | En consultation, 1 offre. | En consultation, {count} offres.',
        panelNone: 'Demandez un prix à plusieurs entreprises pour ce que réclame le lot, et comparez-les à son objectif.',
        openComparison: 'Ouvrir le comparatif',
        start: 'Mettre en consultation',
        fromTender: 'Attribuée depuis un comparatif d’offres : voir les autres offres et le motif',
      },
      // La base de precios de la casa, en la ficha de la partida (0.25.0).
      priceHistory: {
        title: 'Prix proposés auparavant pour cette unité',
        empty: 'Aucune offre enregistrée pour cette unité d’ouvrage pour l’instant. Chaque prix saisi dans un comparatif apparaîtra ici.',
        min: 'Minimum',
        median: 'Médiane',
        max: 'Maximum',
        lastAwarded: 'Dernier attribué',
        againstTarget: 'Prix objectif de cet article : {target}. Médiane des prix proposés : {median}.',
        awarded: 'Attribué',
        howMatched: 'Recherche par code de base de prix ou par désignation proche, avec la même unité et les mêmes chiffres (diamètre, épaisseur, résistance).',
      },
      item: {
        historicalNote: 'Ce poste appartient à une version qui n’est plus la version en vigueur : on le consulte, on ne le modifie pas.',
        cancelAction: 'Annuler le poste',
        packageCommitted: '{amount} viennent du contrat de son lot {packages}. Déjà inclus. | {amount} viennent des contrats de ses lots {packages}. Déjà inclus.',
        restoreAction: 'Rouvrir le poste',
        cancelTitle: 'Annuler ce poste ?',
        cancelHelp: 'Sa quantité passe à zéro et il ne compte plus sur le chantier. Il n’est pas supprimé : il garde son historique et le coût déjà imputé, et il peut être rouvert.',
        cancelledToast: 'Poste annulé',
        restoreTitle: 'Rouvrir le poste ?',
        restoreHelp: 'Il revient avec les quantités qu’il avait avant l’annulation.',
        restoredToast: 'Poste rouvert',
        reasonRequired: 'Motif (obligatoire)',
        reasonOptional: 'Motif (facultatif)',
        reasonPlaceholder: 'Pourquoi, en une phrase',
        lumpSum: 'Poste forfaitaire',
        lumpSumHint: 'Il se mesure en entier ou pas du tout : non réalisé, ou réalisé en entier.',
        lumpNone: 'Non réalisé',
        lumpWhole: 'Réalisé en entier',
        derivedHelp: 'Poste à justifier : il ne se mesure pas. Son avancement vient du coût imputé par rapport à son objectif.',
        pendingAuto: 'Reste à dépenser calculé par le système.',
        pendingManual: 'Reste à dépenser fixé à la main : {reason}',
        pendingAdjust: 'Ajuster le reste',
        targetAdjust: "Ajuster l'objectif",
        targetTitle: 'Objectif du poste',
        targetHelp: "Ce que le poste doit coûter. Tous les chiffres de contrôle sont jugés par rapport à ce montant : la modification reste dans l'historique avec son motif.",
        targetPriceLabel: 'Prix objectif par {unit}',
        targetQuantityLabel: 'Quantité objectif ({unit})',
        targetPreview: 'Objectif résultant : {amount}',
        targetReason: 'Motif de la modification',
        targetReasonPlaceholder: "Ex. : prix arrêté avec le fournisseur, révision de l'étude",
        targetSaved: 'Objectif mis à jour.',
        pendingTitle: 'Ce qui reste à dépenser',
        pendingHelp: 'Si vous savez que le reste n’est pas celui du calcul, écrivez-le ici. La prévision à terminaison est refaite avec ce chiffre.',
        pendingEmpty: 'Vide revient au calcul automatique. Zéro veut dire qu’il ne reste rien.',
        pendingSaved: 'Reste à dépenser enregistré',
        cycle: {
          statusTitle: 'Changer l’approbation',
          stageTitle: 'Changer l’avancement',
          moveTo: 'Passer à « {what} »',
          approveHelp: 'Une fois approuvé, il entre dans la vente du chantier et peut être certifié. Il n’y a pas de retour en arrière.',
          saved: 'Avenant mis à jour',
          status: {
            proposed: 'Proposer à nouveau',
            pricing: 'Passer en chiffrage',
            sent: 'Envoyer au maître d’œuvre',
            approved: 'Approuver',
            rejected: 'Refuser',
          },
        },
        title: 'Poste',
        back: 'À l’arborescence',
        cancelled: 'Annulé',
        bornLosing: 'Perdant dès le départ',
        sale: 'Vente',
        target: 'Objectif',
        real: 'Réel',
        deviation: 'Écart',
        forecast: 'Prévision à l’achèvement',
        committed: 'Engagé',
        margin: 'Marge',
        excess: '{quantity} {unit} de plus que vendu ont été réalisés ({pct}). Signalé, non bloqué.',
        cancelledFor: 'Annulé : {reason}',
        newMeasure: 'Saisir un métré',
        newMeasureHint: 'À l’origine, pas du mois : tout ce qui est réalisé à ce jour.',
        quantity: 'Quantité à l’origine',
        comment: 'Commentaire',
        record: 'Saisir',
        recorded: 'Métré saisi',
        date: 'Date',
        toDate: 'À l’origine',
        moved: 'Variation',
        who: 'Qui',
        length: 'Longueur',
        width: 'Largeur',
        height: 'Hauteur',
        subtotal: 'Partiel',
        code: 'Code',
        concept: 'Concept',
        performance: 'Rendement',
        price: 'Prix',
        breakdownCost: 'Ce que coûte le sous-détail :',
        splitHelp: 'Le poste conserve son objectif entier : ce que les lots réclament atteint au plus cet objectif et ne le retranche jamais. La répartition se décide depuis le lot, dans son sélecteur.',
        splitQuantity: 'En quantité : {claimed} {unit} dans des lots et {free} libres, sur {total}.',
        noPackages: {
          title: 'Il n’est dans aucun lot',
          subtitle: 'Son objectif n’a pas encore été mis sur le marché, il n’y a donc pas de disponible auquel comparer une commande. Il s’ajoute depuis le sélecteur d’un lot.',
        },
        costKind: 'Type',
        whatHappened: 'Ce qui s’est passé',
        detail: 'Détail',
        bc3Lines: 'Les lignes du projet',
        tabs: {
          measurements: 'Métrés',
          breakdown: 'Sous-détail',
          costs: 'Coût',
          history: 'Historique',
          packages: 'Réparti sur',
          prices: 'Prix antérieurs',
        },
        noMeasures: {
          title: 'Pas encore métré',
          subtitle: 'Saisissez ci-dessus ce qui est réalisé, à l’origine.',
        },
        noBreakdown: {
          title: 'Pas de sous-détail',
          subtitle: 'Le fichier ne portait pas les ressources de ce poste.',
        },
        noCosts: {
          title: 'Pas encore de coût',
          subtitle: 'Rien sur ce poste. Regardez le bac des non affectés.',
        },
        noHistory: {
          title: 'Aucun changement',
          subtitle: 'Personne n’a touché à ce poste depuis son import.',
        },
      },
      status2: {
        approved: 'Approuvé',
        proposed: 'Proposé',
        pricing: 'En cours de chiffrage',
        sent: 'Envoyé au maître d’œuvre',
        rejected: 'Refusé',
      },
      stage: {
        not_started: 'Pas commencé', in_progress: 'En cours',
        done: 'Exécuté', invoiced: 'Facturé', paid: 'Encaissé',
      },
      subcerts: {
        title: 'Situation de sous-traitant', back: 'Au contrat',
        openDraft: 'Ouvrir celle en cours',
        listTitle: 'Situations', number: 'Situation {number}',
        prepare: 'Préparer la suivante',
        periodEnd: 'Jusqu’au',
        period: 'De la période',
        periodHelp: 'Ce qui est certifié ce mois-ci : la différence avec ce qui l’était déjà.',
        accumulated: 'Cumulé', ceiling: 'Plafond du contrat',
        ceilingHelp: 'Le contrat plus les avenants approuvés. Le dépasser exige un avenant avant, pas une explication après.',
        charges: 'Refacturations', penalties: 'Pénalités',
        kindLabel: 'Type', kind: { charge: 'Refacturation', penalty: 'Pénalité' },
        retention: 'Retenue',
        retentionHelp: 'Sur ce qui est payable, pas sur ce qui est certifié — sinon on retient plus que ce que dit le contrat.',
        payable: 'À payer', payableHelp: 'La base de sa facture de ce mois.',
        consumed: '{pct} % du contrat',
        linesTitle: 'Postes certifiés', addLine: 'Ajouter un poste',
        itemPlaceholder: 'Chercher le poste',
        before: 'Précédent', toDate: 'Cumulé', price: 'Prix', thisPeriod: 'Ce mois',
        noLines: { title: 'Aucun poste', subtitle: 'Ajoutez ce qui lui est certifié ce mois. Les quantités sont cumulées.' },
        none: { title: 'Aucune situation', subtitle: 'Rien n’a encore été certifié à ce sous-traitant.' },
        deductionsTitle: 'Refacturations et pénalités',
        noDeductions: { title: 'Aucune retenue', subtitle: 'Rien ne lui est retenu.' },
        addCharge: 'Ajouter une refacturation', addPenalty: 'Ajouter une pénalité',
        chargeHelp: 'Un travail fait par l’entreprise principale et qui lui revenait : la grue fournie par le chef de chantier, le nettoyage non fait. Justifié par un bon de livraison.',
        penaltyHelp: 'Le contrat qui s’applique : retard, manquement. Justifié par une clause.',
        chargePlaceholder: 'Ex. : Grue du 12',
        penaltyPlaceholder: 'Ex. : 10 jours de retard, clause 8.2',
        evidence: 'Justificatif', evidencePlaceholder: 'Ex. : BL-2026-77',
        appliedLabel: 'État', charged: 'Retenu', pending: 'En attente',
        nothingHere: 'Rien à choisir.',
        refusalsTitle: 'Ce qui empêche de l’approuver',
        overContract: 'Dépasse le contrat : {accumulated} certifiés contre un plafond de {ceiling}. Un avenant est nécessaire.',
        overClient: 'Sur {item}, {toSub} {unit} lui sont certifiés et seulement {toClient} au client. C’est du travail payé avant d’être facturé.',
        scopeMax: 'max. {max} {unit}',
        scopeNote: 'Le maximum de chaque poste vient de son lot, {name} : ce qu’il réclame, moins ce que d’autres sous-traitants du même lot ont déjà de certifié.',
        overScope: 'Sur {item}, {asked} {unit} lui sont certifiés en cumulé et son lot lui en donne {scope}. On paierait ce qui n’est pas à lui.',
        warningsTitle: 'À regarder avant d’approuver',
        warn: {
          negative_period: 'Le mois est négatif ({amount}) : c’est une correction à la baisse.',
          negative_payable: 'Rien à payer : les retenues absorbent toute la situation ({amount}).',
          line_went_back: 'Sur {item}, moins est certifié que le mois dernier ({amount}).',
        },
        approve: 'Approuver',
        approveConfirm: 'Le coût entre dans le journal du chantier de ce mois et l’engagement du contrat baisse. À payer : {amount}.',
        approved: 'Situation approuvée',
        blocked: 'Impossible à approuver',
        certifiedLocked: 'C’est la somme de ses situations.',
        status: {
          draft: 'Brouillon', sent: 'Envoyée', approved: 'Approuvée',
          rejected: 'Refusée', cancelled: 'Annulée',
        },
        what: { created: 'Création', status: 'État', lines: 'Postes' },
      },
      templates: {
        title: 'Modèles de frais de chantier', manage: 'Modèles',
        yours: 'Les vôtres ({count})', new: 'Nouveau', newName: 'Nouveau modèle',
        builtIn: 'Catalogue',
        builtInHelp: 'Celui-ci vient du catalogue et ne se modifie pas. Copiez-le et modifiez la copie : « bâtiment » reste ainsi celui du catalogue et l’on voit ce que le vôtre change.',
        name: 'Nom', forWhat: 'À quoi il sert',
        forWhatPlaceholder: 'Ex. : bâtiments industriels de moins de 12 mois',
        concept: 'Poste', amount: 'Montant', category: 'Catégorie',
        addLine: 'Ajouter un poste', noLines: 'Aucun poste pour l’instant.',
        lineCount: '{count} poste | {count} postes',
        zeroHelp: 'Un montant peut être à zéro, et ce n’est pas un vide : cela veut dire que ce poste existe sur ce type de chantier et qu’ici il ne coûte rien.',
        copy: 'Copier', copySuffix: 'copie', copied: 'Modèle copié',
        apply: 'Utiliser sur ce chantier', saved: 'Modèle enregistré',
        saveFromWork: 'Enregistrer ce chantier comme modèle',
        fromWorkSuffix: 'modèle',
        savedFromWork: 'Enregistré sous « {name} »',
        dropTitle: 'Supprimer le modèle',
        dropConfirm: '« {name} » disparaît. Les chantiers ouverts avec lui ne changent pas : leurs postes sont déjà copiés.',
        none: { title: 'Aucun modèle', subtitle: 'Partez d’un modèle du catalogue, ou faites le vôtre.' },
        pick: { title: 'Choisissez-en un', subtitle: 'Les vôtres et ceux du catalogue sont à gauche.' },
      },
      invoices: {
        title: 'Factures enregistrées',
        register: 'Enregistrer une facture', registerFrom: 'Facturer ceci',
        edit: 'La facture', saved: 'Enregistré',
        uninvoiced: 'Sans facture',
        uninvoicedHelp: 'Coût déjà engagé dont le papier n’est pas arrivé.',
        uninvoicedTitle: 'Ce qui attend une facture',
        received: 'Reçues', paid: 'Payées',
        owed: 'Dû', owedHelp: 'Enregistré et impayé : ce qu’il faut payer.',
        supplier: 'Fournisseur', number: 'N° de facture',
        numberPlaceholder: 'Ex. : F-2026-0451',
        issueDate: 'Date', dueDate: 'Échéance', state: 'État',
        dueHelp: 'Sans date, rien n’est signalé : ni à jour ni en retard.',
        amount: 'Base', amountHelp: 'Ce qui est imputé au chantier. La TVA n’est pas un coût.',
        tax: 'TVA', notes: 'Notes',
        against: 'Contre quel coût',
        nothingToPick: 'Ce fournisseur n’a rien en attente de facturation.',
        noSupplier: 'Sans fournisseur',
        overdue: 'En retard',
        markPaid: 'Marquer payée', markUnpaid: 'Marquer impayée',
        dropTitle: 'Retirer la facture ?',
        dropConfirm: '{number} disparaît et son coût attend de nouveau une facture.',
        status: { received: 'Reçue', paid: 'Payée' },
        alerts: {
          overdue: '{count} facture en retard impayée | {count} factures en retard impayées',
          due_soon: '{count} facture échoit cette semaine | {count} factures échoient cette semaine',
          uninvoiced: 'Coût reçu dont la facture n’est pas arrivée',
        },
        none: { title: 'Aucune facture', subtitle: 'Les factures de matériaux et de sous-traitance vont ici.' },
        allInvoiced: { title: 'Tout facturé', subtitle: 'Aucun coût reçu n’attend son papier.' },
      },
      subcontracts: {
        title: 'Sous-traitance', back: 'Au chantier',
        new: 'Nouveau contrat', create: 'Créer',
        newHelp: "Seulement le contrat. Les postes couverts se lient après, sur sa fiche.",
        companyPlaceholder: 'Chercher le fournisseur',
        code: 'Contrat', codePlaceholder: 'Ex. : SUB-2026-03',
        scope: 'Périmètre', scopePlaceholder: 'Quels travaux il couvre',
        start: 'Début', end: 'Fin prévue',
        retentionPct: 'Retenue',
        paid: 'Payé', saved: 'Enregistré',
        targetHelp: "L'objectif interne des postes couverts.",
        extrasTitle: 'Avenants du contrat',
        late: 'Hors délai',
        noPackage: 'Sans lot : ne décompte aucun disponible',
        sources: {
          executedScope: 'ce que le chantier a métré dans sa part de chaque poste, au prix de son contrat.',
          executedCertified: 'sans lot, on ne sait pas quelle part du métré est la sienne : le certifié est affiché.',
          certified: 'la somme de ses situations approuvées : celle de chaque mois plus ses suppléments.',
          invoiced: 'les factures reçues contre ces situations, pour leur net (les frais et pénalités ne se facturent pas).',
          paid: 'ce que font ces factures marquées payées, hors la retenue appliquée par chaque situation, qui reste à part.',
        },
        withExtras: 'inclut {amount} d’avenants',
        tabs: { summary: 'Résumé', money: 'Avenants et retenues', contract: 'Le contrat' },
        documents: 'Pièces du contrat',
        addDocument: 'Téléverser un document',
        uploading: 'Envoi…', uploaded: 'Document envoyé',
        noDocuments: 'Aucune pièce. Déposez ici le contrat signé, les avenants et les attestations.',
        dropDocument: 'Retirer le document',
        dropDocumentConfirm: '« {name} » quitte ce contrat.',
        addExtra: 'Ajouter un avenant',
        approveExtra: 'Approuver',
        extraHelp: "Description obligatoire. Commence non approuvé : proposer n'est pas convenir.",
        extraWhat: 'Objet', extraPlaceholder: 'Ex. : Renforts imprévus',
        approved: 'Approuvé', notApproved: 'Non approuvé', approvedAlready: 'Déjà convenu ?',
        noExtras: { title: 'Aucun avenant', subtitle: 'Le contrat suit ce qui a été signé.' },
        covers: 'Couvre {count} poste | Couvre {count} postes',
        scopeAlertsTitle: 'Certifié au-delà de son périmètre',
        scopeOver: '{item} : {certified} {unit} certifiés en cumulé et son lot lui en donne {scope}.',
        scopeFromPackage: 'Couvre ce que réclame son lot, {name} : les postes, leurs quantités et le plafond de ce qui peut lui être certifié. C’est lu dans le lot : si la répartition change, ceci aussi.',
        scopePackageEmpty: 'Son lot, {name}, ne regroupe encore aucun poste : il couvre les siens et n’a pas de plafond de périmètre.',
        scopeQuantity: 'Sa part',
        certifiedToDate: 'Certifié / max.',
        certifiedOf: '{certified} sur {max}',
        packageHelp: 'Lui donne son périmètre : les postes et quantités que réclame le lot, et le plafond de ce qui peut lui être certifié.',
        noPackageOption: 'Sans lot',
        packageLabel: 'Lot attribué',
        noPackageOption: 'Sans lot (à lier plus tard)',
        packagePickHelp: 'Son périmètre sera lu depuis le lot : les articles et quantités qu’il réclame, et le plafond de ce qui peut lui être certifié.',
        packagePicked: 'Elle héritera de {count} article avec un objectif de {amount}. | Elle héritera de {count} articles avec un objectif de {amount}.',
        noItems: 'Aucun poste lié : rien à quoi comparer le contrat.',
        openItem: 'Ouvrir le poste',
        to_signed: 'Signer', to_in_progress: 'Démarré', to_finished: 'Terminé', to_closed: 'Solder',
        what: {
          created: 'Création', status: 'Statut', extra: 'Avenant', package: 'Lot',
          extra_approved: 'Avenant approuvé', extra_unapproved: 'Avenant non approuvé',
          charge: 'Imputation', penalty: 'Pénalité',
          charge_removed: 'Imputation retirée', penalty_removed: 'Pénalité retirée',
        },
        company: 'Entreprise', contracted: 'Contracté', extras: 'Avenants',
        variance: 'Face à l’objectif',
        varianceHelp: "La prévision du contrat face à l'objectif interne des postes qu'il couvre. Négatif, c'est gagner.",
        outstanding: 'Dû',
        outstandingHelp: 'Le payable de ses situations (hors frais, pénalités et retenue) moins le payé.',
        retained: 'Retenue',
        retainedHelp: 'Garantie retenue et rendue à la fin des travaux.',
        pendingExtras: '{amount} non approuvés',
        status: {
          draft: 'Brouillon', signed: 'Signé', in_progress: 'En cours',
          finished: 'Terminé', closed: 'Soldé', cancelled: 'Annulé',
        },
        empty: { title: 'Aucune sous-traitance', subtitle: 'Tout est fait en interne sur ce chantier.' },
      },
      certifications: {
        number: 'N°', period: 'Période',
        produced: 'Exécuté', certified: 'Certifié', invoiced: 'Facturé', paid: 'Encaissé',
        toCertify: 'À certifier',
        toCertifyHelp: "Travaux réalisés pas encore transmis au client. De l'argent fait et non réclamé.",
        toInvoice: 'À facturer', toCollect: 'À encaisser',
        retained: 'Retenue de garantie',
        retainedHelp: "Argent de l'entreprise que le client garde et rend à la fin du chantier.",
        current: 'Ce mois', accumulated: 'Cumulé', payable: 'À encaisser',
        prepare: 'Préparer celle du mois',
        previewTitle: 'La situation de ce mois',
        previewHelp: "Elle n'existe pas encore : voici ce qui sortirait de ce que le chantier a exécuté à ce jour. En la créant, elle naît en brouillon, pour que quelqu'un la vérifie avant de l'émettre.",
        prepareHelp: "Remplie avec ce que le chantier a réalisé, en cumulé. Commence en brouillon.",
        prepared: 'Situation préparée',
        to_issued: 'Émettre', to_invoiced: 'Facturer', to_paid: 'Encaissée',
        status: { draft: 'Brouillon', issued: 'Émise', invoiced: 'Facturée', paid: 'Encaissée' },
        empty: { title: 'Aucune situation', subtitle: 'Prépare la première avec ce qui est réalisé.' },
      },
      clientCert: {
        title: 'Situation de travaux', number: 'Situation n° {number}', back: 'Chantier',
        until: 'au {date}', unsaved: 'Modifications non enregistrées',
        accumulated: 'Cumulé', previous: 'Situation précédente', current: 'Ce mois-ci',
        retention: 'Retenue ({pct} %)', payable: 'Net à encaisser',
        negativeHelp: "Ce mois-ci est négatif : une situation précédente a trop réclamé et on le rend. C'est la correction en cumulé, pas une erreur.",
        previousOf: 'Déduite de la n° {number}, la dernière émise avant celle-ci.',
        first: "C'est la première : il n'y a rien d'antérieur à déduire.",
        reviewNote: "Elle a été remplie avec le réalisé cumulé. Baisse les lignes que la maîtrise d'œuvre n'acceptera pas et enregistre ; on ne peut pas dépasser le réalisé. Une fois émise, elle ne se modifie plus : ce qu'il faut corriger ira dans la suivante.",
        readonly: {
          issued: 'Émise : elle ne se modifie plus. Les corrections vont dans la suivante, en cumulé.',
          historical: "Faite avec une version du budget qui n'est plus la version en vigueur : elle se lit, elle ne se modifie pas.",
          permission: "Lecture seule : tu n'as pas le droit de modifier les situations.",
        },
        linesTitle: 'Lignes', linesCount: '{count} poste | {count} postes',
        reset: 'Revenir au réalisé',
        resetHelp: "Met chaque ligne à ce que le chantier a réalisé en cumulé. Rien n'est enregistré tant que tu n'appuies pas sur « Enregistrer les lignes ».",
        overMax: '{count} ligne dépasse le réalisé : baisse-la pour enregistrer | {count} lignes dépassent le réalisé : baisse-les pour enregistrer',
        lowered: '{count} ligne baissée par rapport au réalisé | {count} lignes baissées par rapport au réalisé',
        empty: { title: 'Aucune ligne', subtitle: "Rien de réalisé à facturer dans celle-ci." },
        col: {
          item: 'Poste', unit: 'U', contracted: 'Projet', executed: 'Réalisé cumulé',
          previous: 'Sit. précédente', toDate: 'Cumulé de celle-ci', current: 'Ce mois-ci', toDateAmount: 'Montant cumulé',
        },
        blocked: {
          cancelled: 'Annulé : il garde ce qui était déjà facturé en situation.',
          unapproved_variation: 'Avenant non approuvé : il garde ce qui était déjà facturé en situation.',
        },
        max: 'max. {max} {unit}',
        save: 'Enregistrer les lignes', saved: 'Lignes enregistrées',
        to_issued: 'Émettre', to_invoiced: 'Marquer facturée', to_paid: 'Marquer encaissée',
        invoiceTitle: 'Marquer comme facturée',
        invoiceMessage: 'La n° {number} passe à facturée pour {amount}, le montant de ce mois-ci.',
        invoiceRef: 'Numéro de facture',
        invoiceRefHelp: "Celui de la facture émise. C'est ce qui relie la situation au document.",
        confirm: {
          issued: {
            title: 'Émettre la situation n° {number}',
            message: "Elle est émise pour {amount} ce mois-ci. Ensuite elle ne se modifie plus : les corrections iront dans la suivante, en cumulé.",
          },
          paid: {
            title: 'Marquer la n° {number} comme encaissée',
            message: 'Confirme que {amount} sont arrivés en banque pour cette situation.',
          },
        },
        done: { issued: 'Situation émise', invoiced: 'Marquée comme facturée', paid: 'Marquée comme encaissée' },
        issuedAt: 'Émise le {date}', invoicedAt: 'Facturée le {date} · {ref}', paidAt: 'Encaissée le {date}',
        download: 'Télécharger le PDF',
        downloadHelp: 'La situation imprimée, pour la maîtrise d’œuvre et le maître d’ouvrage. Un brouillon sort avec la mention « BROUILLON ».',
        measured: {
          note: '{count} poste a été métré après la préparation du brouillon et n’a pas de ligne. | {count} postes ont été métrés après la préparation du brouillon et n’ont pas de ligne.',
          add: 'Ajouter ce qui a été métré depuis',
          title: 'Métré depuis la préparation',
          help: 'Postes avec avancement qui n’étaient pas métrés quand le brouillon a été préparé. Ils entrent avec l’exécuté à l’origine et peuvent ensuite être baissés comme n’importe quelle ligne. Les ajouter enregistre les lignes, avec vos modifications.',
          confirm: 'Ajouter {count} poste | Ajouter {count} postes',
          added: '{count} poste ajouté | {count} postes ajoutés',
        },
        pdf: {
          period: 'Période', date: 'Date', work: 'Chantier', code: 'Code',
          price: 'Prix', currentAmount: 'Montant du mois', chapterTotal: 'Total chapitre',
          noVat: 'Montants hors TVA.', notes: 'Observations',
          signContractor: 'L’entreprise', signDirection: 'La maîtrise d’œuvre',
          signAgreed: 'Bon pour accord', signName: 'Nom et signature', signDate: 'Date',
        },
      },
      variations: {
        new: 'Nouvel avenant',
        newHelp: 'Il naît proposé et ne s’ajoute à la vente qu’une fois approuvé. L’approbation et l’avancement se gèrent depuis sa fiche.',
        create: 'Proposer',
        created: 'Avenant proposé',
        chapter: 'Chapitre',
        noChapter: 'Sans chapitre',
        kind: 'Type de poste',
        customerRef: 'Référence client',
        customerRefHint: 'L’ordre ou le numéro sous lequel le maître d’ouvrage ou le maître d’œuvre le connaît.',
        reason: 'Motif',
        reasonPlaceholder: 'Pourquoi ce travail apparaît',
        what: 'Avenant', approval: 'Approbation', stage: 'Travaux',
        approved: 'Approuvé et vendu',
        approvedHelp: 'Avenants acceptés par le client. Déjà comptés dans la vente du chantier.',
        open: 'Demandé sans réponse',
        openHelp: 'Proposé ou envoyé à la maîtrise d’œuvre. Pas encore compté dans la vente.',
        atRisk: 'Exécuté sans approbation',
        atRiskHelp: 'Travaux déjà faits, coûtant déjà de l’argent, que personne n’a approuvés.',
        atRiskNote: '{count} avenant exécuté sans approbation, avec {cost} de coût déjà dans la prévision. | {count} avenants exécutés sans approbation, avec {cost} de coût déjà dans la prévision.',
        empty: { title: 'Aucun avenant', subtitle: 'Tout ce qui se construit était au projet.' },
      },
      costKind: {
        incurred: 'Dépensé',
        committed: 'Engagé',
      },
      lineType: {
        measurement: 'Métré',
        subtotal_partial: 'Sous-total',
        subtotal_running: 'Cumulé',
        expression: 'Formule',
      },
      historyWhat: {
        created: 'Création',
        progress: 'Métré',
        target: 'Objectif',
        status: 'Statut',
        cancelled: 'Annulé',
        restored: 'Rouvert',
        edited: 'Modifié',
        stage: 'Étape',
        copied: 'Copié dans la révision',
        assigned: 'Dans un lot',
        unassigned: "Hors d'un lot",
        packages: 'Répartition en lots',
        pending: 'Reste fixé à la main',
      },
      historyField: {
        code: 'code',
        description: 'libellé',
        label: 'résumé',
        longText: 'texte long',
        unit: 'unité',
        kind: 'type',
        saleQuantity: 'quantité de vente',
        salePrice: 'prix de vente',
        targetQuantity: 'quantité objectif',
        targetPrice: 'prix objectif',
        pendingOverride: 'reste',
      },
      historyExtra: {
        automatic: 'automatique',
        packagesCount: '{count} lot | {count} lots',
        copiedDetail: 'Nouvelle révision du budget',
      },

      title: 'Chantiers',

      related: { forCustomer: 'Chantiers' },

      help: {
        title: 'Chantiers',
        subtitle: 'Du devis quantitatif au coût final prévu : objectif, lots d’achat, sous-traitants et situations de travaux',
      },

      scope: { only: 'Uniquement les chantiers de {name}', showAll: 'Tout afficher', oneCustomer: 'ce client' },
      subtitle: 'Le budget de chaque chantier par chapitres et postes, et ce qu’il a coûté',
      detailTitle: 'Chantier',
      search: { subtitle: 'Chantier' },
      dashboard: {
        works: 'Chantiers en cours',
        worksHelp: "Combien de chantiers sont ouverts aujourd'hui, hors clôturés et annulés.",
        production: 'Production à ce jour',
        productionHelp: 'Ce qui a été construit à ce jour sur les chantiers en cours, valorisé au prix de vente. Certifié ou non.',
        backlog: 'Carnet à exécuter',
        backlogHelp: 'Ce qui a été vendu et reste à construire sur les chantiers en cours.',
        toCertify: 'En attente de certification',
        toCertifyHelp: "La production qui n'est encore entrée dans aucune situation adressée au client.",
        receivable: 'En attente de règlement',
        receivableHelp: 'Situations émises ou facturées et toujours impayées, retenue déjà déduite.',
        margin: 'Marge prévue',
        marginHelp: 'La marge attendue à la clôture des chantiers en cours, sur la vente et pondérée par montant.',
        deviation: 'Écart sur objectif',
        deviationHelp: "Ce qui a été dépensé au-delà de l'objectif réparti, sur tous les chantiers en cours.",
        unassigned: 'Coût non affecté',
        unassignedHelp: "Argent imputé à un chantier et encore sans poste. Tant que ce n'est pas zéro, la marge est incomplète.",
        supplierInvoices: 'Factures fournisseurs à payer',
        supplierInvoicesHelp: 'Reçu et non payé aux fournisseurs de chantier, TVA comprise.',
        worksList: 'Les chantiers, un par un',
        worksListHelp: 'Les chantiers en cours classés par montant, avec leur avancement et leur marge.',
        deviated: 'Chantiers en dépassement',
        deviatedHelp: "Les chantiers au-dessus de l'objectif, du plus grand écart au plus petit.",
      },
      actions: { new: 'Nouveau chantier', open: 'Ouvrir le chantier', import: 'Importer BC3', more: 'Actions' },
      status: { draft: 'Brouillon', live: 'En cours', closed: 'Clôturé', cancelled: 'Annulé' },
      fields: {
        code: "Code du chantier",
        manager: "Conducteur de travaux",
        address: "Adresse", city: 'Commune', postalCode: 'Code postal',
        labourRate: 'Prix de l’heure',
        labourRateHelp: 'Ce que coûte une heure d’équipe sur ce chantier. Avec ça, les heures des rapports arrivent valorisées ; sans ça elles arrivent comptées et sans montant.',
        name: 'Nom du chantier', customerName: 'Client',
        fileNumber: 'Dossier', authority: 'Organisme',
        tenderAmount: 'Montant de l’appel d’offres', awardAmount: 'Montant attribué',
        lowCoefficient: 'Rabais', lastData: 'Dernière donnée',
      },
      header: { collapse: 'Replier', expand: 'Voir les chiffres' },
      figures: {
        sale: 'Vente', saleHelp: 'Ce qui est facturé au client pour ce chantier, au prix du contrat.',
        target: 'Objectif', targetHelp: "Ce que l'entreprise comptait dépenser pour l'exécuter.",
        spent: 'Dépensé',
        real: 'Réel', realHelp: 'Coût DÉJÀ engagé : bons reçus, heures faites, matériel utilisé.',
        committed: 'Engagé', committedHelp: "Commandes et contrats déjà approuvés qui ne sont pas encore un coût, mais qu'on ne peut plus éviter.",
        pending: 'Reste à dépenser', pendingHelp: "Ce qu'il reste à dépenser pour finir, déduction faite de l'engagé.",
        forecast: 'Prévision', forecastHelp: 'Où finit le chantier : réel + engagé + reste. Aucun euro compté deux fois.',
        forecastMargin: 'Marge prévue', forecastMarginHelp: 'Ce qui restera à la fin : vente moins prévision.',
        margin: 'Marge',
        notInvoiced: 'Non facturé', notInvoicedHelp: 'Part du réel déjà exécutée dont la facture fournisseur manque. Déjà comptée dans le réel.',
        unassigned: 'Non affecté', unassignedHelp: "Argent dépensé sur ce chantier qui n'est rattaché à aucun poste.",
        progress: 'Avancement', consumed: 'Coût consommé',
        costAhead: "Le coût va plus vite que l'exécution.",
      },

      alerts: {
        title: 'À regarder aujourd’hui', more: 'Voir {count} de plus',
        work_over_target: "le chantier finira au-dessus de l'objectif",
        work_negative_margin: "le chantier coûtera plus qu'il n'a été vendu",
        work_cost_ahead_of_progress: "le coût va plus vite que l'exécution",
        unassigned_significant: "beaucoup de coût n'est rattaché à aucun poste",
        unassigned_stale: 'des coûts attendent depuis des semaines sans affectation',
        item_over_target: "finira au-dessus de son objectif",
        item_negative_margin: 'sera vendu moins cher que son coût',
        cost_ahead_of_progress: "dépense plus vite qu'il ne construit",
        item_near_target: "a presque consommé son objectif et il reste du travail",
        committed_without_progress: 'tout commandé et pas commencé',
        item_born_losing: 'son sous-détail coûte plus que son prix objectif',
        variation_executed_unapproved: 'exécuté et non approuvé : on le donne',
        variation_awaiting_approval: 'en attente du client',
      },

      closing: {
        against: "Face à un objectif de {target} :",
        marginInitial: 'Marge du contrat', marginInitialHelp: 'Celle signée : vente moins objectif.',
        marginCurrent: 'Marge prévue',
        marginChange: 'Ce qui est parti', marginChangeHelp: 'La différence entre la marge signée et celle prévue.',
        manual: '{count} postes sur {items} ont leur reste saisi à la main.',
        ranking: 'Où on perd de l’argent',
        unexplained: '{amount} sans explication', noCauses: 'personne n’a dit pourquoi',
      },

      causes: {
        title: 'Pourquoi ça dérape',
        edit: 'Classer',
        help: "Répartis l'écart par cause. Ce qui ne rentre nulle part reste sans explication, et s'affiche comme tel.",
        what: 'Cause', amount: 'Montant', note: 'Détail',
        notePlaceholder: 'Obligatoire pour « Autres »',
        add: 'Ajouter une cause',
        sum: 'Expliqué {explained} sur {variance}. Sans explication : {left}.',
        saved: 'Causes enregistrées',
        unexplained: 'Sans explication',
        none: "Personne n'a encore dit pourquoi.",
        chargeable: '{amount} peuvent être facturés au client.',
        supplier_price: 'Prix fournisseur', more_quantity: 'Quantité supérieure',
        extra_hours: 'Heures supplémentaires', budget_error: 'Erreur de devis',
        scope_change: 'Changement de périmètre', customer_variation: 'Avenant client',
        delay: 'Retard', rework: 'Reprise', material_waste: 'Gaspillage de matériel',
        subcontract_price: 'Sous-traitance plus chère', extra_plant: 'Matériel supplémentaire',
        other: 'Autres',
      },
      sort: { label: 'Trier par', variance: 'Plus gros dépassement', margin: 'Marge la plus faible', unassigned: 'Plus de non affecté', sale: 'Montant le plus élevé', name: 'Nom' },
      stats: { works: 'Chantiers' },
      list: { work: 'Chantier' },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      filters: { searchPlaceholder: 'Nom, dossier, client…', status: 'Statut', allStatuses: 'Tous' },
      empty: {
        title: 'Aucun chantier pour l’instant',
        subtitle: 'Un chantier est un projet avec un budget par postes. Ouvrez le premier et importez son BC3.',
      },
      new: {
        codePlaceholder: "OBR-2026-014",
        customerPlaceholder: "Cherchez le client",
        managerPlaceholder: "Cherchez la personne",
        title: 'Ouvrir un chantier',
        help: "Le chantier est la fiche elle-même : son code, son client et son conducteur de travaux. Le budget arrive ensuite, avec le BC3.",
      },
      nav: {
        works: 'Chantiers',
        contracting: 'Contractualisation',
        subcontracts: 'Sous-traitance',
        certifications: 'Situations',
        prices: 'Prix de chantier',
      },
      sections: {
        label: 'Rubriques du chantier',
        budget: 'Budget du chantier',
        contracting: 'Contractualisation',
        production: 'Production et encaissement',
        control: 'Contrôle',
        daily: 'Au quotidien',
      },
      revisions: {
        name: 'Version',
        created: 'Créée',
        current: 'En vigueur',
        historical: 'Historique',
        viewing: 'Celle que vous voyez dans l’arbre',
        view: 'Voir dans l’arbre',
        compare: 'Comparer avec celle en vigueur',
        noReport: 'Cette version ne vient pas d’un BC3 : elle a été saisie à la main ou copiée d’une autre.',
      },
      across: {
        work: 'Chantier',
        allWorks: 'Tous les chantiers',
        allBuyers: 'Tout le monde',
        attention: 'Ce qu’il faut traiter',
        warnings: 'Alertes',
        contracting: {
          title: 'Contractualisation',
          subtitle: 'Les lots et comparatifs de tous les chantiers, face à leur objectif',
          count: 'Lots',
          emptyTitle: 'Aucun lot avec ces filtres',
          emptyHelp: 'Les lots se créent dans chaque chantier, sous « Contractualisation ». Ici apparaissent ceux de tous les chantiers.',
          alerts: {
            offers_expired: '{name} ({work}) : {count} offre expirée ; la moins chère, {supplier} | {name} ({work}) : {count} offres expirées ; la moins chère, {supplier}',
            offers_expiring: '{name} ({work}) : {count} offre expire cette semaine ; la moins chère, {supplier} | {name} ({work}) : {count} offres expirent cette semaine ; la moins chère, {supplier}',
            awarded_over_target: '{name} ({work}) a été attribué à {supplier} {pct} % au-dessus de l’objectif',
            tendering_no_offers: '{name} ({work}) est en consultation et n’a aucune offre',
          },
        },
        subcontracts: {
          title: 'Sous-traitance',
          subtitle: 'Tous les contrats de tous les chantiers : le contracté, le situé et ce qui leur est dû',
          count: 'Contrats',
          search: 'Entreprise, code ou périmètre…',
          pending: 'Situations à approuver',
          pendingHelp: 'Situations de sous-traitants en brouillon ou envoyées que personne n’a encore approuvées.',
          pendingShort: 'À approuver',
          pendingCount: '{count} à approuver | {count} à approuver',
          warnings: {
            late: 'Hors délai',
            over_scope: 'Situé en trop',
          },
        },
        certifications: {
          title: 'Situations',
          subtitle: 'Les situations au client de tous les chantiers : quoi revoir, quoi facturer et quoi encaisser',
          count: 'Situations',
          missingTitle: 'Chantiers sans la situation de ce mois',
          missingRow: '{work} · {name} : production non située',
          bucket: 'Étape',
          allBuckets: 'Toutes',
          bucketCount: '{count} situation | {count} situations',
          nextLabel: 'Étape suivante',
          buckets: {
            review: 'Brouillons à revoir',
            to_invoice: 'Émises à facturer',
            to_collect: 'Facturées à encaisser',
            done: 'Encaissées',
          },
          next: {
            review: 'Revoir et émettre',
            to_invoice: 'Facturer',
            to_collect: 'Encaisser',
            done: 'Encaissée',
          },
        },
        prices: {
          title: 'Prix de chantier',
          subtitle: 'Ce que les entreprises ont offert et ce qui a été contracté, sur tous les chantiers',
          count: 'Prix',
          search: 'Désignation ou code…',
          unitPlaceholder: 'm3, m2, kg…',
          supplier: 'Entreprise',
          which: 'Quels prix',
          allPrices: 'Offerts et contractés',
          onlyAwarded: 'Contractés seulement',
          mixedUnits: 'Ces prix sont dans plusieurs unités. Saisissez une unité pour voir le prix moyen, le plus bas et le dernier.',
          mean: 'Prix moyen',
          min: 'Plus bas',
          max: 'Plus haut',
          last: 'Dernier',
          lastAwarded: 'Dernier contracté',
          fromWhere: '{supplier} · {work} · {date}',
          awarded: 'Contracté',
          origin: 'Chantier et comparatif',
          date: 'Date',
          emptyTitle: 'Aucun prix avec ces filtres',
          emptyHelp: 'Chaque offre saisie dans un comparatif laisse ici ses prix, ligne par ligne.',
          onlySupplier: 'Seulement les prix de {name}',
          oneSupplier: 'ce fournisseur',
        },
      },
      supplierPanel: {
        title: 'Sur les chantiers',
        awards: 'Attributions',
        offers: 'Offres dans les comparatifs',
        subcontracts: 'Sous-traitance',
        prices: 'Historique des prix',
        pricesRow: 'Ses prix dans les comparatifs de chantier',
      },
      voidCost: {
        action: 'Annuler',
        title: 'Annuler ce coût',
        help: '« {description} », {amount}. Il disparaît du chantier et de son poste ; le motif reste dans le journal d’activité.',
        reason: 'Motif',
        reasonPlaceholder: 'Ex. : saisi deux fois',
        confirm: 'Annuler le coût',
        done: 'Coût annulé.',
      },
      tabs: {
        tree: 'Postes',
        packages: 'Lots',
        variations: 'Modificatifs et avenants',
        versions: 'Révisions',
        subcontracts: 'Sous-traitance',
        certifications: 'Situations',
        invoices: 'Factures reçues',
        closing: 'Prévision de fin',
        unassigned: 'Coûts non affectés',
        indirect: 'Coûts indirects',
        report: 'L’import',
      },
      packages: {
        title: 'Lots de passation',
        newPackage: 'Nouveau lot',
        newHelp: "Un lot est l'unité contre laquelle on ACHÈTE : il regroupe des postes pour demander un prix à un fournisseur. Il est créé vide et les postes lui sont affectés ensuite.",
        createPackage: 'Créer le lot',
        newName: 'Nouveau lot',
        buyer: 'Acheteur',
        buyerPlaceholder: "Cherchez quelqu'un de l'entreprise",
        empty: {
          title: 'Aucun lot de passation',
          subtitle: "Un lot est ce qui se contracte d'un coup. Sans lots il n'y a pas de disponible, et sans disponible personne ne sait si une commande tient dans le budget.",
        },
        type: {
          supply: 'Fourniture',
          subcontract: 'Sous-traitance',
          hire: 'Location',
          service: 'Service',
          own_means: 'Moyens propres',
        },
        columns: {
          package: 'Lot',
          type: 'Type',
          target: 'Objectif',
          available: 'Disponible',
        },
        availableHelp: "Objectif moins l'engagé et le dépensé. Ce qu'il reste pour acheter.",
        itemCount: '{count} poste | {count} postes',
        itemsTitle: 'Ce qu’il regroupe',
        noItems: "Il ne regroupe encore aucun poste, donc il n'a pas d'objectif.",
        addItems: 'Ajouter des postes',
        ctc: 'Reste à dépenser',
        ctcValue: 'Fixé à la main',
        ctcHint: 'Laissez vide et le moteur propose {amount}.',
        ctcReason: 'Pourquoi',
        ctcReasonPlaceholder: 'Ex. : le poste est terminé, il ne dépensera plus',
        ctcManual: 'Ce lot prévoit avec votre chiffre, pas avec celui calculé.',
        calculatedHelp: 'Somme des {count} postes regroupés : {amount}.',
        manualAdjust: 'Ajustement manuel',
        manualAdjustHelp: "Ce que le conducteur sait et que l'arborescence ne dit pas. Toujours motivé.",
        reason: 'Motif de l’ajustement',
        removeTitle: 'Supprimer ce lot ?',
        removeHelp: "Seul un lot sans achat est supprimé. Dès qu'il a des commandes ou des dépenses, il est CLÔTURÉ.",
        alerts: {
          over_committed: '{name} est engagé {amount} au-delà de ce qu’il avait.',
          forecast_over: '{name} devrait clôturer {amount} au-dessus de son objectif.',
          suspicious_ctc: '{name} n’a rien d’engagé ni rien à dépenser, et le chantier n’est pas fait.',
          no_target: 'On dépense sur {name} sans objectif derrière.',
          own_means_committed: '{name} est en moyens propres et a {amount} engagés : soit la commande est sur le mauvais lot, soit le lot n’était pas en moyens propres.',
          sub_over_scope: '{supplier} a {certified} {unit} de {item} certifiés et son lot, {name}, lui en donne {scope}. On paie ce qui n’est pas à lui.',
          quantity_over_measurement: 'Sur {item}, les lots réclament {claimed} {unit} et le métré est de {measurement}. Il faut décider qui se passe de ce qui manque.',
          offers_expired: '{name} est en consultation avec {count} offre expirée : la moins chère, {supplier} | {name} est en consultation avec {count} offres expirées : la moins chère, {supplier}',
          awarded_over_target: '{name} a été attribué à {supplier} {pct} % au-dessus de l’objectif.',
          awarded_incomplete: '{name} a été attribué à {supplier} en laissant {count} ligne non contractée. | {name} a été attribué à {supplier} en laissant {count} lignes non contractées.',
        },
        status: 'Statut',
        statusOf: {
          open: 'Ouvert',
          tendering: 'En consultation',
          awarded: 'Attribué',
          closed: 'Clôturé',
        },
        typeHelp: {
          supply: 'Sera acheté par commande. Engage à l’approbation de la commande.',
          hire: 'Sera acheté par commande. Engage à l’approbation de la commande.',
          service: 'Sera acheté par commande. Engage à l’approbation de la commande.',
          subcontract: 'Sera contracté avec plafond, retenue et situations.',
          own_means: 'Ne s’achète pas. Se suit au rendement depuis les rapports.',
        },
        history: {
          title: 'Ce qui lui est arrivé',
          created: 'Créé',
          edited: 'Modifié',
          committed: 'Passé seul à « {to} » à l’arrivée de {reference} pour {amount}',
          status: 'Passé de « {from} » à « {to} »',
          other: 'Modification du lot',
          assigned: 'Ce qu’il regroupe a changé',
          tender: 'Mouvement dans son comparatif d’offres',
        },
      },
      coverage: {
        target: 'Objectif de coût du chantier',
        covered: 'Couvert par des lots',
        pct: '{pct} % de l’objectif',
        unpackaged: 'Hors lot',
        orphans: 'Voir le poste isolé · {amount} | Voir les {count} postes isolés · {amount}',
        approve: 'Approuver l’objectif',
        approveTitle: 'Approuver l’objectif de ce chantier ?',
        approveHelp: 'À partir d’ici les écarts se mesurent contre cette ligne, et la répartition entre lots est figée. Elle peut être levée ensuite, avec un motif.',
        approved: 'Objectif approuvé.',
        approvedBy: 'Approuvé par {who} le {when}',
        cannotApprove: 'Impossible d’approuver tant qu’il reste de l’objectif hors de tout lot : cet argent n’a pas de disponible, donc aucune commande ne peut être comparée à quoi que ce soit.',
        blocked: 'L’objectif est approuvé : la répartition ne se touche pas. Levez l’approbation pour la modifier.',
        revoke: 'Lever l’approbation',
        revokeTitle: 'Lever l’approbation de l’objectif',
        revokeHelp: 'La répartition redevient modifiable. Qui l’a levée et pourquoi reste écrit.',
        revokeReason: 'Pourquoi elle est levée',
      },
      picker: {
        title: 'Ce que regroupe {name}',
        search: 'Chercher par code ou concept',
        onlyFree: 'Seulement avec disponible',
        natureHint: 'Ce sont les lignes de cette nature qui sont ajoutées, jamais le poste entier.',
        claimed: 'Affecté',
        free: 'Disponible',
        assigned: 'Affecté',
        noBreakdown: 'sans sous-détail',
        takenBy: 'Pris par : {packages}',
        allChapters: 'Tous les chapitres',
        allNatures: 'Toutes les natures',
        amountLabel: 'Montant (au plus {max})',
        amountReason: 'Pourquoi ce montant',
        amountReasonPlaceholder: 'Ex. : seulement la fourniture ; la pompe vient avec le sous-traitant',
        amountConfirm: 'Ajouter avec ce montant',
        freeQuantity: 'Libre : {free} {unit} sur {total}',
        byQuantity: 'Par quantité',
        byAmount: 'Par montant',
        quantityLabel: 'Quantité en {unit} (maximum {max})',
        quantityHint: '{pct} % du métré · {amount}',
        quantityReason: 'Pourquoi cette quantité',
        quantityReasonPlaceholder: 'Ex. : tronçon nord, du PK 0+000 au 0+400',
        quantityConfirm: 'Ajouter avec cette quantité',
        quantityCol: 'Quantité',
        tally: '{lines} sur {items} · {amount}',
        tallyLines: '0 ligne | {count} ligne | {count} lignes',
        tallyItems: '0 poste | {count} poste | {count} postes',
        pickAll: 'Sélectionner tout le filtré',
        clear: 'Effacer la sélection',
        add: 'Ajouter {count}',
        added: '{count} affectations ajoutées.',
        stale: 'à revoir',
        origin: 'D’où ça vient',
        mode: {
          whole: 'Poste entier',
          lines: 'Ligne',
          pct: 'Pourcentage',
          amount: 'Montant',
          quantity: 'Quantité',
        },
        dropTitle: 'Retirer {code} du lot ?',
        dropHelp: 'Le poste conserve son objectif entier ; ce qui est retiré est ce que ce lot en réclamait.',
        dropReason: 'Pourquoi on le retire',
        dropReasonHelp: 'Ce lot a déjà acheté : lui retirer un poste baisse son objectif sans baisser sa dépense, donc son disponible se resserre d’un coup.',
        none: {
          title: 'Aucun poste ne correspond',
          subtitle: 'Essayez d’enlever le filtre de nature ou de chapitre. Si le chantier n’a pas encore de budget, il n’y a rien à mettre en lot.',
        },
      },
      indirect: {
        templates: {
          'IND-01': "Conducteur de travaux",
          'IND-02': "Chef de chantier",
          'IND-03': "Assistant de chantier",
          'IND-10': "Base vie",
          'IND-11': "Clôture provisoire",
          'IND-12': "Branchements provisoires",
          'IND-13': "Moyens communs",
          'IND-20': "Électricité",
          'IND-21': "Eau",
          'IND-22': "Internet et téléphonie",
          'IND-30': "Nettoyage",
          'IND-31': "Gardiennage",
          'IND-32': "Hygiène et sécurité",
          'IND-33': "Gestion des déchets",
          'IND-34': "Assurances de chantier",
          'IND-40': "Véhicules de chantier",
          'IND-41': "Carburant",
          'IND-42': "Déplacements et indemnités",
          'IND-90': "Locations générales",
          'IND-91': "Petites fournitures",
          'IND-92': "Autres frais de chantier",
        },
        title: 'Coûts indirects de chantier',
        subtitle: "Ce que coûte le maintien du chantier, sans appartenir à un poste.",
        empty: {
          title: 'Aucun budget de coûts indirects',
          subtitle: "Le conducteur de travaux, la base vie, le gardiennage et l'électricité coûtent chaque mois où le chantier reste ouvert.",
        },
        fromTemplate: 'Partir d’un modèle',
        templateKind: 'Type de chantier',
        clear: 'Vider le bloc',
        restart: 'Recommencer',
        restartTitle: 'Recommencer ?',
        restartConfirm: 'Les {count} postes partent et le gestionnaire s’ouvre pour choisir un autre modèle. Ce qui porte déjà un coût reste.',
        clearTitle: 'Vider les frais de chantier ?',
        clearConfirm: 'Les {count} postes de ce chantier partent. Ceux qui portent déjà un coût restent : cela ne se supprime pas, cela se clôture.',
        cleared: 'Vidé : {count} postes retirés',
        clearedSome: '{count} retirés. Ceux-ci restent, car ils portent déjà un coût : {kept}',
        removeTitle: 'Retirer le poste ?',
        removeConfirm: '« {name} » quitte le budget de frais de chantier. S’il porte déjà un coût, ce sera refusé : clôturez-le au lieu de le retirer.',
        fullscreenHint: 'Frais de chantier en plein écran · Échap pour sortir',
        newConcept: 'Ajouter un poste',
        created: 'Budget créé avec {count} postes. Renseignez les montants.',
        kind: {
          building: 'Bâtiment',
          industrial: 'Bâtiment industriel',
          refurbishment: 'Réhabilitation',
          civil: 'Travaux publics',
          fit_out: 'Aménagement',
        },
        category: {
          personnel: 'Personnel de chantier',
          facilities: 'Installations provisoires',
          utilities: 'Fluides',
          services: 'Services généraux',
          vehicles: 'Véhicules et déplacements',
          other: 'Autres',
        },
        behaviour: {
          label: 'Comportement',
          help: 'Détermine comment est estimé le reste à dépenser.',
          fixed: 'Paiement unique',
          monthly: 'Mensuel',
          weekly: 'Hebdomadaire',
          daily: 'Journalier',
          variable: 'Variable',
          manual: 'Manuel',
        },
        rate: 'Coût par période',
        rateHelp: 'Ce que cela coûte chaque mois, semaine ou jour.',
        durationSensitive: 'Dépend du délai',
        durationSensitiveHelp: "Si un mois de plus de chantier le rend plus cher.",
        closed: 'Clôturé',
        closedHelp: 'Il ne coûtera plus rien.',
        columns: {
          concept: 'Poste',
          type: 'Type',
          target: 'Objectif',
          real: 'Réel',
          committed: 'Engagé',
          pending: 'Reste à dépenser',
          forecast: 'Prévision',
          deviation: 'Écart',
          consumed: '% consommé',
        },
        totals: {
          target: 'Objectif',
          real: 'Réel',
          committed: 'Engagé',
          pending: 'Reste à dépenser',
          forecast: 'Prévision',
          deviation: 'Écart',
        },
        duration: {
          title: 'Le délai',
          planned: 'Fin planifiée',
          forecast: 'Fin prévue',
          extra: '{days} jours de plus',
          perMonth: '{amount} par mois dépendent du délai',
          impact: 'Surcoût dû à l’allongement du délai',
          shift: 'Déplacer la fin prévue',
          shiftTitle: 'Déplacer la fin prévue de tout le chantier ?',
          shiftHelp: 'Elle se déplace sur les {count} postes qui dépendent du délai.',
          shiftReason: 'Pourquoi le retard',
          moved: '{count} postes déplacés.',
          onTime: 'Le chantier est dans les délais.',
        },
        deviations: { title: 'Principaux écarts', empty: 'Aucun poste ne dépasse son objectif.' },
        evolution: { title: 'Évolution mensuelle', real: 'Réel', target: 'Objectif réparti', linear: "L'objectif est réparti linéairement sur le délai." },
        movements: { title: 'D’où cela vient', empty: 'Rien ne lui a encore été imputé.', origin: 'Origine', date: 'Date', amount: 'Montant' },
        identity: 'Identification',
        family: 'Famille',
        budget: 'Budget',
        period: 'Période',
        startDate: 'Début prévu',
        endDate: 'Fin prévue',
        actualStart: 'Début réel',
        forecastEnd: 'Fin prévue actualisée',
        initialTarget: 'Budget initial',
        pendingOverride: 'Reste à dépenser, à la main',
        pendingOverrideHelp: 'Quand le reste est dit par une personne et non par le calcul.',
        removeTitle: 'Supprimer ce poste ?',
        removeHelp: 'Seul un poste sans mouvement est supprimé. Dès qu’il a du coût imputé, il est CLÔTURÉ.',
        alerts: {
          duration_overrun: 'Le retard de {days} jours ajoute {amount} de coûts indirects.',
          over_budget: '{name} dépasse son objectif de {pct} %.',
          forecast_over: '{name} devrait clôturer {amount} au-dessus de son objectif.',
          past_due: '{name} continue de coûter {days} jours après sa fin prévue.',
          no_budget: 'Un coût a été imputé à {name} sans budget associé.',
          unbooked_periods: '{name} tourne depuis {periods} périodes et {amount} restent à imputer.',
        },
        unassignedHint: 'Il y a {count} mouvement non affecté sur ce chantier. | Il y a {count} mouvements non affectés sur ce chantier.',
      },
      versions: {
        remove: 'Retirer cette version',
        removeTitle: 'Retirer la version ?',
        removeConfirm: '« {name} » disparaît avec ses chapitres et ses postes, et c’est sans retour. Possible seulement si elle n’a ni métré, ni coût, ni situation derrière elle.',
        removed: 'Version retirée ({chapters} chapitres, {items} postes)',
        label: 'Version du budget', historical: 'historique',
        newRevision: 'Nouvelle révision',
        newRevisionTitle: 'Créer une révision du budget ?',
        newRevisionHelp: "Le budget entier est copié et le chantier passe à la copie : métrés, coûts et situations se déplacent avec elle, donc la situation suivante reste la différence avec la précédente. La version actuelle reste consultable.",
        created: 'Révision créée avec {chapters} chapitres et {items} postes.',
        compare: 'Comparer',
        compareTitle: 'Ce qui a changé',
        added: 'Entrent', removed: 'Sortent', changed: 'Changent',
        delta: 'Fait bouger la vente de',
        deltaHelp: "Ce qui entre moins ce qui sort, plus ce qui change de prix ou de quantité.",
        historicalNote: 'Vous consultez une ancienne version : lecture seule.',
        same: { title: 'Aucune différence', subtitle: 'Les deux versions disent la même chose.' },
        what: { added: 'Entre', removed: 'Sort', changed: 'Change' },
        field: { description: 'Désignation', unit: 'Unité', saleQuantity: 'Quantité', salePrice: 'Prix' },
      },
      editor: {
        edit: 'Modifier chapitres et postes',
        done: 'Arrêter de modifier',
        hint: 'Enregistré tout seul en quittant chaque case.',
        addChapter: 'Ajouter un chapitre',
        addSubchapter: 'Ajouter un chapitre à l’intérieur',
        addItem: 'Ajouter un poste à ce chapitre',
        chapter: 'chapitre',
        item: 'poste',
        unit: 'U',
        quantity: 'Quantité',
        price: 'Prix',
        amount: 'Montant',
        newChapter: 'Nouveau chapitre',
        newItem: 'Nouveau poste',
        openBudget: 'Saisir le budget à la main',
        budgetOpened: 'Budget ouvert. Commencez par un chapitre.',
        removeTitle: 'Supprimer du budget ?',
        removeChapterHelp: 'Seul un chapitre vide peut être supprimé. S’il contient encore des postes, sortez-les ou annulez-les d’abord.',
        removeItemHelp: 'Seul un poste sans rien d’enregistré est supprimé. Dès qu’il a un métré, un coût ou une situation, il est annulé avec un motif et reste dans l’historique.',
      },
      tree: {
        fullscreen: 'Agrandir',
        exitFullscreen: 'Quitter',
        fullscreenHint: 'Contrôle des postes en plein écran · Échap pour sortir',
        concept: 'Concept', quantity: 'Métré', target: 'Objectif',
        spent: 'Dépensé', progress: 'Réalisé', consumed: 'Consommé',
        deviation: 'Écart',
        empty: {
          title: 'Ce chantier n’a pas encore de budget',
          subtitle: 'Importez le BC3 du bureau d’études et l’arborescence apparaît ici.',
        },
      },
      itemKind: { normal: 'Normal', lump_sum: 'Forfait', lump_sum_justified: 'À justifier' },
      origin: { bc3: 'Du BC3', variation: 'Avenant', manual: 'À la main' },
      nature: { labour: 'Main-d’œuvre', plant: 'Matériel', material: 'Matériaux', subcontract: 'Sous-traitance', other: 'Autres' },
      quiet: { days: 'Il y a {days} jours', today: "Aujourd’hui", never: 'Aucune encore' },
      unassigned: {
        assignTo: 'Rattacher au poste',
        pickItem: 'Cherchez le poste',
        assign: 'Affecter {count}',
        assigned: '{count} écritures rattachées à leur poste',
        help: 'Tout le coût qui vient du chantier arrive ici : un rapport ne connaît pas les codes de postes et n’a pas à les connaître. Ce qui fait marcher le dispositif, c’est que quelqu’un vide ce bac.',
        date: 'Date', description: 'Concept', nature: 'Nature', origin: 'Provenance', amount: 'Montant',
        empty: { title: 'Rien n’est en attente', subtitle: 'Tout le coût de ce chantier est rattaché à son poste.' },
      },
      import: {
        title: 'Importer un budget',
        help: 'Un fichier BC3 (FIEBDC-3), celui qu’exportent Presto, Arquímedes ou Menfis. Il remplace l’arborescence de la version, il ne la fusionne pas.',
        file: 'Le fichier', versionName: 'Nom de la version',
        versionHelp: 'Laissé vide, le titre contenu dans le fichier est utilisé.',
        kind: 'De quoi il s’agit', original: 'Projet initial', amendment: 'Avenant',
        charset: 'Jeu de caractères', charsetHelp: 'Laissez automatique. On n’y touche que si les accents sortent mal.',
        charsetAuto: 'Ce que dit le fichier',
        done: '{items} postes importés, pour {total}',
      },
      report: {
        chapters: 'Chapitres', items: 'Postes', charset: 'Caractères', program: 'Logiciel',
        anomaly: 'Ce qui s’est passé', count: 'Combien de fois',
        clean: 'Le fichier est entré proprement : rien à signaler.',
        empty: { title: 'Rien n’a encore été importé', subtitle: 'Quand un BC3 entrera, ce qu’il contenait et ce qui lui manquait sera écrit ici.' },
      },
      anomaly: {
        CHARSET_NOT_DECLARED: 'Le fichier ne dit pas son jeu de caractères ; CP850 supposé',
        CHARSET_UNKNOWN: 'Le fichier nomme un jeu de caractères inconnu',
        CHARSET_IS_UTF8: 'Le fichier est en UTF-8 quoi qu’il déclare',
        PREAMBLE_IGNORED: 'Du texte précédait le premier enregistrement',
        RECORD_HEADER_INVALID: 'Un enregistrement sans en-tête valide',
        RECORD_TYPE_UNSUPPORTED: 'Enregistrements que ce lecteur n’interprète pas encore',
        FORMAT_VERSION_UNKNOWN: 'Version du format inconnue',
        DECIMALS_NOT_DECLARED: 'Pas d’enregistrement de décimales ; ceux du format sont utilisés',
        ROOT_NOT_FOUND: 'Le fichier n’a pas de racine',
        MULTIPLE_ROOTS: 'Le fichier a plusieurs racines',
        CHAPTER_EMPTY: 'Chapitres vides',
        DECOMPOSITION_CYCLE: 'Un sous-détail qui s’appelle lui-même',
        DECOMPOSITION_TOO_DEEP: 'Un sous-détail trop imbriqué',
        TREE_TOO_LARGE: 'Une arborescence trop grande pour être un devis',
        FILE_TOO_LARGE: 'Un fichier trop volumineux pour être lu',
        CONCEPT_NOT_DECLARED: 'Concepts utilisés et jamais déclarés',
        CONCEPT_REDECLARED: 'Concepts déclarés deux fois',
        CONCEPT_WITHOUT_PRICE: 'Concepts sans prix',
        CONCEPT_WITHOUT_MEASUREMENT: 'Postes sans métré',
        CONCEPT_WITHOUT_DECOMPOSITION: 'Postes sans sous-détail',
        PRICE_DISAGREES_WITH_DECOMPOSITION: 'Le prix ne correspond pas à son sous-détail',
        MEASUREMENT_TOTAL_MISMATCH: 'Le métré ne correspond pas à ses lignes',
        MEASUREMENT_EXPRESSION_INVALID: 'Une formule de métré qui n’en est pas une',
        MEASUREMENT_EXPRESSION_FAILED: 'Une formule de métré incalculable',
        MEASUREMENT_WITHOUT_TARGET: 'Des métrés qui ne disent pas à quoi ils se rapportent',
      },
    },
  },
}

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

Object.assign(messages, withToolLabels(messages))
